import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactWordcloud from 'react-wordcloud';
import Lottie from 'lottie-react';
import badgeAnimationData from '../../resources/Congratulation _ Success batch.json';
import moduleOneAnimationData from '../../resources/module1.json';
import thinkAnimationData from '../../resources/think.json';
import checklistAnimationData from '../../resources/checklist.json';
import pdfAnimationData from '../../resources/contract.json';
import questionAnimationData from '../../resources/question.json';
import imageAnimationData from '../../resources/image.json';
import { MODULES_CATALOG } from '../../data/modulesCatalog';
import {
  WORKPLACE_EDI_IDENTITY_TITLE,
  WORKPLACE_EDI_IDENTITY_INTRO_BODY,
  WORKPLACE_EDI_IDENTITY_OPTIONS,
} from '../../data/workplaceSurveyIdentityOptions';
import {
  ensureModuleEnrollment,
  trackModuleStageProgress,
  markModuleProgressCompleted,
  resetModuleProgressToBeginning,
  writeReflectionSubmission,
  fetchReflectionWordCloudAggregates,
  writeMetaReflexivityResponse,
  fetchMetaReflexivityPollAggregates,
  writeIntentionalShiftCommitment,
  writeWorkplaceSurveyResponse,
  writeWorkplaceParticipantReview,
  writeEducatorBarrierRecognitionResponse,
  fetchEducatorBarrierRecognitionAggregates,
  writeParentModuleStoryResponse,
  fetchParentPauseWordCloudAggregates,
  fetchLatestParentStoryResponsesForUser,
  normalizeReflectionCloudWord,
} from '../../services/lmsData';
import {
  EDUCATOR_CONTENT,
  EDUCATOR_M1_SLIDES,
  EDUCATOR_M2_SLIDES,
  EDUCATOR_M3_REFLECTION_SLIDES,
  EDUCATOR_M3_RESEARCH_CONNECTION_SLIDE,
  EDUCATOR_PIPELINE_FIGURE_MODAL_FOOTER,
  EDUCATOR_M4_RESEARCH_INSIGHT,
  EDUCATOR_M4_GUIDED_STEPS,
  EDUCATOR_FIVE_BARRIERS,
  EDUCATOR_BARRIER_CAROUSEL_SLIDES,
  EDUCATOR_BARRIERS_INTRO_PREVIEW_IMAGE,
  EDUCATOR_BARRIER_HOTSPOTS,
  EDUCATOR_BARRIER_POLL_OPTIONS,
  EDUCATOR_M5_SLIDES,
  EDUCATOR_M5_HEADER,
  EDUCATOR_M6_SLIDES,
  EDUCATOR_M7_SLIDES,
  EDUCATOR_ACTIONS_IMAGE_SRC,
  EDUCATOR_ACTIONS_HOTSPOTS,
  EDUCATOR_ACTIONS_TABLE_CONTENT,
  SARA_CASE_STUDY_VIDEO,
  REVISITING_SARA_STORY_VIDEO,
  EDUCATOR_META_PRIVATE_NOTE_KEY,
} from '../../data/educatorModuleContent';
import {
  COMMUNITY_CONTENT,
  COMMUNITY_M1_SLIDES,
  COMMUNITY_M2_SLIDES,
  COMMUNITY_M3_REFLECTION_SLIDES,
  COMMUNITY_M3_EXTERNAL_SIGNAL_SLIDE,
  COMMUNITY_M3_RESEARCH_CONNECTION_SLIDE,
  COMMUNITY_BARRIER_ECOSYSTEM_FIGURE,
  COMMUNITY_M4_RESEARCH_INSIGHT,
  COMMUNITY_M4_GUIDED_STEPS,
  COMMUNITY_BARRIERS_IMAGE_PLACEHOLDER_TEXT,
  COMMUNITY_BARRIERS_INTRO_PREVIEW_IMAGE,
  COMMUNITY_FIVE_BARRIERS,
  COMMUNITY_BARRIER_CAROUSEL_SLIDES,
  COMMUNITY_BARRIER_HOTSPOTS,
  COMMUNITY_BARRIER_POLL_OPTIONS,
  COMMUNITY_M5_SLIDES,
  COMMUNITY_M5_HEADER,
  COMMUNITY_M6_SLIDES,
  COMMUNITY_M7_SLIDES,
  COMMUNITY_ACTIONS_IMAGE_SRC,
  COMMUNITY_ACTIONS_IMAGE_PLACEHOLDER_TEXT,
  COMMUNITY_ACTIONS_HOTSPOTS,
  COMMUNITY_ACTIONS_TABLE_CONTENT,
  AMAN_CASE_STUDY_VIDEO,
  REVISITING_AMAN_STORY_VIDEO,
  COMMUNITY_META_PRIVATE_NOTE_KEY,
} from '../../data/communityModuleContent';
import {
  PARENT_CONTENT,
  PARENT_M1_SLIDES,
  PARENT_STORY_SECTIONS,
  PARENT_STORY_SECTION_IDS,
  PARENT_M5_SLIDES,
  PARENT_SURVEY_INTRO,
  PARENT_SURVEY_SUBMITTED_TITLE,
  PARENT_PAUSE_STORAGE_PREFIX,
  PARENT_ACT_STORAGE_PREFIX,
  PARENT_SURVEY_STORAGE_KEY,
  PARENT_PARTICIPANT_REVIEW_STORAGE_KEY,
} from '../../data/parentModuleContent';
import { useAuth } from '../../contexts/AuthContext';
import { heroImageProps, sceneImageProps, videoThumbProps, contentImageProps } from '../../utils/imageDelivery';
import { paintCertificateDynamicText, CERTIFICATE_TEMPLATE_PATH } from '../../utils/certificateCanvas';
import { resolveParticipantDisplayName } from '../../utils/participantDisplayName';
import { pickFlowModule } from '../../utils/studyModuleFlow';
import { OVERVIEW_PARTS_STEPPER_EXPERIMENT } from '../../config/overviewLayoutExperiment';
import ModuleOverviewPartsStepper from '../StudyModule/ModuleOverviewPartsStepper';
import './StudyModule.css';
import './StudyModule.mobile.css';
import './StudyModule.overviewStepperExperiment.css';
import './Dashboard.css';

/** Part 6 slide 3: question Lottie; memoized so reveal re-renders don't restart it. */
const IntentionalShiftQuestionLottie = memo(function IntentionalShiftQuestionLottie() {
  return (
    <Lottie
      animationData={questionAnimationData}
      loop
      autoplay
      className="actions-slide2-lottie"
      rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
    />
  );
});

/** Pause & Reflect think Lottie - same pattern as Part 6 slide 3 intentional shift. */
const ReflectionThinkLottie = memo(function ReflectionThinkLottie() {
  return (
    <Lottie
      animationData={thinkAnimationData}
      loop
      autoplay
      className="reflection-pause-lottie"
      rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
    />
  );
});

/** Pause & Reflect lines only; sibling of Lottie inside the two-column layout. */
const ReflectionPauseReflectLines = memo(function ReflectionPauseReflectLines({ lines, revealCount }) {
  const visibleLines = lines.slice(0, revealCount);
  return (
    <div className="reflection-slide-lines reflection-slide-lines--pause">
      {visibleLines.map((line) => (
        <p key={line} className="reflection-slide-line reflection-slide-line--pause">
          {line}
        </p>
      ))}
    </div>
  );
});

function ReflectionWordInputFooter({
  hasExistingReflection,
  latestWord,
  value,
  onChange,
  onSubmit,
}) {
  return (
    <div className="reflection-footer-input">
      {hasExistingReflection ? (
        <div className="reflection-box">
          <p className="reflection-label">Your response is already submitted.</p>
          {latestWord && (
            <p className="reflection-user-word-chip" aria-live="polite">
              Your word: <strong>{latestWord}</strong>
            </p>
          )}
        </div>
      ) : (
        <div className="reflection-box">
          <label htmlFor="reflection-input" className="reflection-label">Enter one word</label>
          <input
            type="text"
            id="reflection-input"
            className="reflection-input reflection-input--one-word"
            maxLength={40}
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <button
            type="button"
            className="reflection-submit-btn"
            onClick={onSubmit}
            disabled={!String(value || '').trim()}
          >
            Submit
          </button>
        </div>
      )}
    </div>
  );
}

const STEP_COLORS = [
  { bg: '#0d9488', light: 'rgba(13, 148, 136, 0.15)' },
  { bg: '#2563eb', light: 'rgba(37, 99, 235, 0.15)' },
  { bg: '#7c3aed', light: 'rgba(124, 58, 237, 0.15)' },
  { bg: '#846595', light: 'rgba(132, 101, 149, 0.2)' },
  { bg: '#6b21a8', light: 'rgba(107, 33, 168, 0.15)' },
  { bg: '#c026d3', light: 'rgba(192, 38, 211, 0.15)' },
];

const ALL_MODULES = [
  { id: 'intro-module', title: 'Introduction Module', icon: '🌟', duration: '~15 min', color: 'blue' },
  { id: 'workplace-module', title: 'Building Workplace Bridges', icon: '💼', duration: '2 weeks', color: 'purple' },
  { id: 'educator-module', title: 'Educator Module', icon: '📚', duration: '2 weeks', color: 'teal' },
  { id: 'community-module', title: 'Community Module', icon: '🤝', duration: '2 weeks', color: 'orange' },
  { id: 'parent-module', title: 'Parent Module', icon: '👨‍👩‍👧', duration: '2 weeks', color: 'pink' },
];

const SIX_BARRIERS = [
  {
    name: 'Cultural Dissonance',
    researchThemeTitle: 'Cultural Dissonance in Professional Identity',
    participantContext:
      "This barrier arises when cultural expectations and workplace norms pull in opposite directions. People feel split between honoring their family's ideals (e.g., humility, stability, service) and the dominant norms of leadership (e.g., assertiveness, ambition, visibility).",
    participantQuotes: [
      "I think what has worked for me is, you know, learning those subtle cues you know, in workplace language, or, you know, for example, if I'm in a meeting, I wouldn't say, you know, I think that should be the case, or, you know, I would just rephrase it to based on, you know, all the evidence or, you know, based on all the facts that I've considered. So that establishes, sort of, that you have done your homework. I'm trying to incorporate, you know, these small things in, you know, our… in my communication with our stakeholders, and again.",
    ],
    howItLooksInAnika: 'Feeling like she has to "practice" a version of leadership that feels forced.',
    workplaceConditionTriggersIt: 'Leadership models that only reward Western-centric "assertiveness" over collaborative excellence.',
    leveragePoint: 'Redefine leadership criteria to value collaborative excellence, humility, and technical depth alongside assertiveness.',
  },
  {
    name: 'Self-Censorship',
    researchThemeTitle: 'Self-Censorship',
    participantContext:
      'This is the act of editing yourself - toning down your voice, softening your message, or staying silent - to avoid judgment or conflict. It often stems from past experiences',
    participantQuotes: [
      'I could say something, but I know how that usually goes.',
      "I really have to talk like a white person, you know? I have to really, like, I have to lower my voice. Like, when I'm excited, my voice gets really high, but, like, I have to lower my voice, I have to talk corporate, I have to learn… I had to learn the lingo, which was a… which was a whole shock for me when I started working in corporate for 3 years now,",
    ],
    howItLooksInAnika: 'Waiting for the "right moment" to speak or letting others take credit for her ideas.',
    workplaceConditionTriggersIt: 'Environments where ideas are only "heard" when they come from someone with a specific "presence" or seniority.',
    leveragePoint: 'Change meeting facilitation and credit attribution; explicitly interrupt when an employee is talked over.',
  },
  {
    name: 'Imposter Syndrome',
    researchThemeTitle: 'Imposter Syndrome',
    participantContext:
      "This isn't just about confidence. Imposter syndrome often reflects a lack of identity mirrors - not seeing people like you in leadership, which makes you question whether you belong there.",
    participantQuotes: [
      "I know I'm qualified, but I still feel like a fraud sometimes.",
      "So, totally 100% agree with, you know, everybody's thoughts, and you know, I've faced the same thing, Imposter Syndrome, it's really, you know, it's real, and you self-doubt yourself, you know, every… each and every day on every decision. I think what has worked for me is, you know, learning those subtle cues.",
    ],
    howItLooksInAnika: 'Doubting if she is cut out for leadership despite exceptional performance ratings.',
    workplaceConditionTriggersIt: 'Lack of identity mirrors: when an employee sees zero South Asian women in leadership, they naturally question if they belong there.',
    leveragePoint: 'Formalize sponsorship pathways; ensure leaders are trained to recognize and interrupt subtle invalidation.',
  },
  {
    name: 'Stereotype Threat',
    researchThemeTitle: 'Stereotype Threat',
    participantContext:
      'This barrier refers to the pressure of feeling like your actions represent your entire group. It can lead people to over-perform, avoid risk, or stay invisible to avoid reinforcing negative stereotypes.',
    participantQuotes: [
      "If I fail, I'm not just failing - I'm confirming what they already think.",
      "There was this gatekeeping and this thought process that whatever research South Asian people are doing, it's not up to the mark, it doesn't. It's not that good in quality.",
    ],
    howItLooksInAnika: 'Feeling tired of "chasing a version of leadership" and fearing that failure confirms group stereotypes.',
    workplaceConditionTriggersIt: 'High-stakes environments where racialized women feel they must be "twice as good" to be seen as equal.',
    leveragePoint: 'Reduce reliance on "readiness" or "fit" language; implement objective, evidence-based performance metrics.',
  },
  {
    name: 'Fear of Disappointing Elders',
    researchThemeTitle: 'Fear of Disappointing Elders',
    participantContext:
      "Many participants shared that they hesitated to make bold career decisions - switching fields, asking for more, or asserting themselves - out of fear they'd be seen as selfish or disobedient.",
    participantQuotes: [
      "Even when I do well, I still wonder if I've let someone down.",
      "Rather than just, like, taking time for myself to heal and, you know, like. focus on my mental health, and I feel like that just isn't talked about enough, even though now more than before, they do talk about mental health, but… You know, for us, they're just like, no, you should still keep going, like, don't let this stop you, don't let this tear you down, and just… Go, go, go, always, and never stop, take a break.",
    ],
    howItLooksInAnika: 'Balancing a motto of "put your head down" with a need to be the "dependable one."',
    workplaceConditionTriggersIt: 'Mentorship gaps that fail to acknowledge the double burden of cultural/familial expectations and professional drive.',
    leveragePoint: 'Offer culturally responsive mentorship and career conversations that account for intersectional identities.',
  },
  {
    name: 'Niceness & Obedience Norms',
    researchThemeTitle: 'Shame, Obedience, and Niceness Norms',
    participantContext:
      'Deeply internalized norms around obedience and being agreeable can prevent people from advocating for themselves. These values may come from upbringing, gender socialization, or both - but they shape behavior long into adulthood.',
    participantQuotes: [
      "I don't want to be seen as difficult. So I just try to be nice.",
      "If somebody's telling me calmly and politely, I'll try to improve myself. But if there is this problem in the tone where you know they they are trying to put me down. So it's it's a if I I think I never reacted on such situations, but it always stayed somewhere,",
    ],
    howItLooksInAnika: 'Being praised for being "reliable" and "collaborative" while her leadership potential is ignored.',
    workplaceConditionTriggersIt: 'Systems that reward "docility" in some employees while promoting "aggression" in others.',
    leveragePoint: 'Audit performance language and promotion criteria to ensure "agreeableness" isn\'t a barrier to leadership.',
  },
];

/** Module 4 barrier popup: research framing + participant quotes + case-study / context sections. */
function renderManagerStageBlock(stage) {
  const bodyContent = stage.paragraphs?.length
    ? stage.paragraphs.map((para, i) => (
        <p key={`${stage.label}-p-${i}`} className="meta-reflexivity-body meta-reflexivity-manager-line">
          {para}
        </p>
      ))
    : stage.text
      ? <p className="meta-reflexivity-body meta-reflexivity-manager-line">{stage.text}</p>
      : null;

  if (stage.bullets?.length) {
    return (
      <div key={stage.label} className="meta-reflexivity-manager-stage">
        <h4 className="meta-reflexivity-manager-stage-heading">{stage.label}</h4>
        {bodyContent}
        {stage.listIntro ? (
          <p className="meta-reflexivity-body meta-reflexivity-manager-line">{stage.listIntro}</p>
        ) : null}
        <ul className="meta-reflexivity-manager-stage-list">
          {stage.bullets.map((item) => (
            <li key={item} className="meta-reflexivity-body meta-reflexivity-manager-line">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div key={stage.label} className="meta-reflexivity-manager-stage">
      <h4 className="meta-reflexivity-manager-stage-heading">{stage.label}</h4>
      {bodyContent}
    </div>
  );
}

function ensureDoubleQuoted(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return '';
  const startsQuoted =
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith('\u201C') && raw.endsWith('\u201D')) ||
    (raw.startsWith('\u2018') && raw.endsWith('\u2019'));
  if (startsQuoted) return raw;
  return `\u201C${raw}\u201D`;
}

function formatBarrierStatNumber(n) {
  return Number(n).toLocaleString('en-CA');
}

function formatBarrierPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
}

function BarrierResearchDonut({ percent, valueLabel, label, meta, tone, ariaLabel }) {
  const pct = Math.min(100, Math.max(0, Number(percent) || 0));
  // Tiny shares (< ~4%) get a slight visual floor so the slice stays noticeable; true % stays in the label.
  const visualPct = pct > 0 && pct < 4 ? Math.max(pct, 3.5) : pct;
  return (
    <div className={`barrier-research-donut barrier-research-donut--${tone}`}>
      <div
        className="barrier-research-donut-ring"
        style={{ '--donut-pct': `${visualPct}%` }}
        role="img"
        aria-label={ariaLabel}
      >
        <div className="barrier-research-donut-hole">
          <strong className="barrier-research-donut-value">{valueLabel}%</strong>
        </div>
      </div>
      <div className="barrier-research-donut-copy">
        <p className="barrier-research-donut-label">{label}</p>
        <p className="barrier-research-donut-meta">{meta}</p>
      </div>
    </div>
  );
}

/** Participant donut chart for community barrier researchStats. */
function BarrierResearchStatsCharts({ chart }) {
  if (!chart) return null;
  const participantPct = Math.min(100, Math.max(0, Number(chart.participantPercent) || 0));
  const participantLabel = formatBarrierPercent(chart.participantPercent);

  return (
    <div className="barrier-research-charts barrier-research-charts--single" role="group" aria-label="Research theme statistics">
      <BarrierResearchDonut
        tone="participants"
        percent={participantPct}
        valueLabel={participantLabel}
        label="Participants who discussed this theme"
        meta={`${formatBarrierStatNumber(chart.participantCount)} of ${formatBarrierStatNumber(chart.participantTotal)} participants`}
        ariaLabel={`${participantLabel}% of participants (${chart.participantCount} of ${chart.participantTotal}) discussed this theme`}
      />
    </div>
  );
}

function BarrierDetailBody({ barrier, barrierIndex, titleId, variant = 'workplace' }) {
  if (!barrier) return null;
  const heading = barrier.researchThemeTitle || barrier.name;
  const quotes = barrier.participantQuotes && barrier.participantQuotes.length > 0 ? barrier.participantQuotes : [];
  const howLabel =
    variant === 'community'
      ? "How it looks in Aman's story"
      : variant === 'educator'
        ? "How it looks in Sara's story"
        : 'How it looks in Anika';
  const secondLabel =
    variant === 'educator' || variant === 'community' ? 'Everyday meaning' : 'What workplace condition triggers it';
  const secondBody =
    (variant === 'educator' || variant === 'community') && barrier.everydayMeaning != null && String(barrier.everydayMeaning).trim()
      ? barrier.everydayMeaning
      : barrier.workplaceConditionTriggersIt;
  const hasResearchChart = Boolean(barrier.researchChart);
  const hasResearch = Boolean(
    barrier.participantContext || barrier.researchStats || barrier.researchChart || quotes.length
  );
  const quoteList = quotes.length > 0 ? (
    <ul className="barriers-modal-quotes">
      {quotes.map((text, i) => (
        <li key={i} className="barriers-modal-quote">
          {variant === 'educator' || variant === 'community' ? ensureDoubleQuoted(text) : text}
        </li>
      ))}
    </ul>
  ) : null;
  return (
    <>
      <span className="barriers-modal-number">Barrier {barrierIndex + 1}</span>
      <h3 id={titleId || undefined} className="barriers-modal-title">
        {heading}
      </h3>

      <div className="barriers-modal-section">
        <h4 className="barriers-modal-label">{howLabel}</h4>
        <p className="barriers-modal-desc">{barrier.howItLooksInAnika}</p>
      </div>
      <div className="barriers-modal-section">
        <h4 className="barriers-modal-label">{secondLabel}</h4>
        <p className="barriers-modal-desc">{secondBody}</p>
      </div>

      {hasResearch ? (
        <div className="barriers-modal-section barriers-modal-section--research">
          <h4 className="barriers-modal-label">From the research</h4>
          {barrier.participantContext ? (
            <p className="barriers-modal-desc">{barrier.participantContext}</p>
          ) : null}
          {quoteList}
          {(variant === 'community' || variant === 'educator') && hasResearchChart ? (
            <BarrierResearchStatsCharts chart={barrier.researchChart} />
          ) : barrier.researchStats ? (
            <p className="barriers-modal-research-stats">{barrier.researchStats}</p>
          ) : null}
        </div>
      ) : null}
      {variant !== 'educator' && barrier.leveragePoint ? (
        <div className="barriers-modal-section">
          <h4 className="barriers-modal-label">Leverage point (what to examine or redesign)</h4>
          <p className="barriers-modal-desc">{barrier.leveragePoint}</p>
        </div>
      ) : null}
    </>
  );
}

/** Part 3 slide 1: preview beside explore CTA (public/images/barrierImage.jpg). */
const BARRIERS_INTRO_PREVIEW_IMAGE = 'images/barrierImage.jpg';

/** Module 4: two-scene carousel - barriers 1-3 on barrier1.jpg, 4-6 on barrier2.jpg (public/images/). */
const BARRIER_CAROUSEL_SLIDES = [
  {
    imageSrc: 'images/barrier1.jpg',
    alt: 'Illustration for barriers 1 to 3 - click numbered hotspots to read more',
    hotspots: [
      { barrierIndex: 0, left: 22, top: 34, label: '1' },
      { barrierIndex: 1, left: 52, top: 48, label: '2' },
      { barrierIndex: 2, left: 76, top: 62, label: '3' },
    ],
  },
  {
    imageSrc: 'images/barrier2.jpg',
    alt: 'Illustration for barriers 4 to 6 - click numbered hotspots to read more',
    hotspots: [
      { barrierIndex: 3, left: 28, top: 40, label: '4' },
      { barrierIndex: 4, left: 52, top: 56, label: '5' },
      { barrierIndex: 5, left: 72, top: 36, label: '6' },
    ],
  },
];

const ALL_BARRIER_HOTSPOTS = BARRIER_CAROUSEL_SLIDES.flatMap((slide) => slide.hotspots);

/** Path under `public/` or full `https://` URL (for CDN / storage - Vercel max ~100 MB per file). */
function resolvePublicOrAbsoluteUrl(base, pathOrUrl) {
  const s = (pathOrUrl || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  const path = s.replace(/^\/+/, '');
  return base ? `${base}/${path}` : `/${path}`;
}

/**
 * Anika's Case Study video (MP4 fallback only).
 * Workplace Part 2 slide 1 uses YouTube (`WORKPLACE_M2_ANIKA_STORY_YOUTUBE_VIDEO_ID`).
 * Keep this for any remaining local/remote MP4 paths or if YouTube id is unset.
 */
const ANIKA_CASE_STUDY_VIDEO = {
  localSrc: 'videos/anika-case-study.mp4',
  remoteSrc: process.env.REACT_APP_ANIKA_VIDEO_URL || '',
  poster: process.env.REACT_APP_ANIKA_VIDEO_POSTER || 'images/thumb1.jpg',
  title: 'Anika\'s Story',
};

/**
 * Revisiting Anika's Story (meta-reflexive manager).
 * Workplace Part 5 popup uses YouTube (`WORKPLACE_M6_REVISITING_ANIKA_YOUTUBE_VIDEO_ID`).
 * Keep local/remote MP4 config as fallback when YouTube id is unset.
 */
const REVISITING_ANIKA_STORY_VIDEO = {
  localSrc: 'videos/revisiting-anika-story.mp4',
  remoteSrc: process.env.REACT_APP_REVISITING_ANIKA_VIDEO_URL || '',
  poster: process.env.REACT_APP_REVISITING_ANIKA_VIDEO_POSTER || 'images/thumb2.jpg',
  title: "Revisiting Anika's Story",
};

/** Module 6 (Small Actions): six barrier points on one image; aligns with ACTIONS_TABLE_CONTENT order. */
const ACTIONS_IMAGE_SRC = 'images/module1.jpg';
const ACTIONS_HOTSPOTS = [
  { spotIndex: 0, left: 14, top: 28, label: '1' },
  { spotIndex: 1, left: 38, top: 20, label: '2' },
  { spotIndex: 2, left: 86, top: 30, label: '3' },
  { spotIndex: 3, left: 16, top: 74, label: '4' },
  { spotIndex: 4, left: 46, top: 84, label: '5' },
  { spotIndex: 5, left: 84, top: 66, label: '6' },
];
const ACTIONS_TABLE_CONTENT = [
  { name: 'Cultural Dissonance', howItLooksInAnika: 'Feeling like she has to "practice" a version of leadership that feels forced.', workplaceConditionTriggersIt: 'Leadership models that only reward Western-centric "assertiveness" over collaborative excellence.', leveragePoint: 'Redefine leadership criteria to value collaborative excellence, humility, and technical depth alongside assertiveness.' },
  { name: 'Self-Censorship', howItLooksInAnika: 'Waiting for the "right moment" to speak or letting others take credit for her ideas.', workplaceConditionTriggersIt: 'Environments where ideas are only "heard" when they come from someone with a specific "presence" or seniority.', leveragePoint: 'Change meeting facilitation and credit attribution; explicitly interrupt when an employee is talked over.' },
  { name: 'Imposter Syndrome', howItLooksInAnika: 'Doubting if she is cut out for leadership despite exceptional performance ratings.', workplaceConditionTriggersIt: 'Lack of Identity Mirrors: When an employee sees zero South Asian women in leadership, they naturally question if they belong there.', leveragePoint: 'Formalize sponsorship pathways; ensure leaders are trained to recognize and interrupt subtle invalidation.' },
  { name: 'Stereotype Threat', howItLooksInAnika: 'Feeling tired of "chasing a version of leadership" and fearing that failure confirms group stereotypes.', workplaceConditionTriggersIt: 'High-stakes environments where racialized women feel they must be "twice as good" to be seen as equal.', leveragePoint: 'Reduce reliance on "readiness" or "fit" language; implement objective, evidence-based performance metrics.' },
  { name: 'Fear of Disappointing Elders', howItLooksInAnika: 'Balancing a motto of "put your head down" with a need to be the "dependable one".', workplaceConditionTriggersIt: 'Mentorship gaps that fail to acknowledge the double-burden of cultural/familial expectations and professional drive.', leveragePoint: 'Offer culturally responsive mentorship and career conversations that account for intersectional identities.' },
  { name: 'Niceness & Obedience Norms', howItLooksInAnika: 'Being praised for being "reliable" and "collaborative" while her leadership potential is ignored.', workplaceConditionTriggersIt: 'Systems that reward "docility" in some employees while promoting "aggression" in others.', leveragePoint: 'Audit performance language and promotion criteria to ensure "agreeableness" isn\'t a barrier to leadership.' },
];

/** Module 5 (Meta-Reflexivity): PDF filename in public folder - place your PDF there. */
const META_REFLEXIVITY_PDF_FILENAME = 'Meta-Reflexivity-Framework.pdf';
const EDUCATOR_META_REFLEXIVITY_PDF_FILENAME = 'MetaReflexivityEducator.pdf';
const COMMUNITY_META_REFLEXIVITY_PDF_FILENAME = 'MetaReflexivityCommunity.pdf';
/** Module 7 slide 1 (Small Actions): system mapping worksheet in public folder. */
const SYSTEM_MAPPING_WORKSHEET_PDF = 'system_mapping_worksheet_v3.pdf';
const EDUCATOR_MAPPING_ROLE_PDF = 'MappingRoleEducator.pdf';
const COMMUNITY_MAPPING_ROLE_PDF = 'MappingRoleComunity.pdf';
/** Module 7 slide 3 (Small Actions): glossary and additional resources in public folder. */
const GLOSSARY_AND_RESOURCES_PDF = 'Glossary.pdf';
const EDUCATOR_GLOSSARY_AND_RESOURCES_PDF = 'Glossary_Educator.pdf';
const COMMUNITY_GLOSSARY_AND_RESOURCES_PDF = 'Glossary_Community.pdf';
const META_REFLEXIVITY_MOMENT_STORAGE_KEY = 'lms-meta-reflexivity-download-responses';
const META_REFLEXIVITY_MOMENT_OPTIONS = [
  { value: 'hiring', label: 'Hiring' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'mentorship', label: 'Mentorship' },
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'other', label: 'Other' },
];

const META_REFLEXIVITY_MOMENT_VALUE_SET = new Set(META_REFLEXIVITY_MOMENT_OPTIONS.map((o) => o.value));

/** Dedupe + filter to known option values for submit / restore. */
function normalizeMetaReflexivityMomentTypesForSubmit(selected) {
  const seen = new Set();
  const out = [];
  for (const v of Array.isArray(selected) ? selected : []) {
    if (META_REFLEXIVITY_MOMENT_VALUE_SET.has(v) && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out.length ? out : ['other'];
}

/** Map localStorage or API-shaped payload to `{ momentTypes, otherText }`. */
function storedPayloadToUserChoice(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const momentTypes = Array.isArray(payload.momentTypes) && payload.momentTypes.length
    ? normalizeMetaReflexivityMomentTypesForSubmit(payload.momentTypes)
    : payload.momentType
      ? normalizeMetaReflexivityMomentTypesForSubmit([payload.momentType])
      : null;
  if (!momentTypes || momentTypes.length === 0) return null;
  return {
    momentTypes,
    otherText: payload.otherText != null ? String(payload.otherText).trim() || null : null,
  };
}

const REFLECTION_STORAGE_KEY = 'lms-workplace-reflection-submissions';
const INTENTIONAL_SHIFT_STORAGE_KEY = 'lms-workplace-intentional-shift-submissions';
const WORKPLACE_SURVEY_STORAGE_KEY = 'lms-workplace-module-survey-submissions';
const WORKPLACE_PARTICIPANT_REVIEW_STORAGE_KEY = 'lms-workplace-participant-review';

function reflectionStorageKeyForModule(mid) {
  if (mid === 'community-module') return 'lms-community-reflection-submissions';
  if (mid === 'educator-module') return 'lms-educator-reflection-submissions';
  return REFLECTION_STORAGE_KEY;
}
function intentionalShiftStorageKeyForModule(mid) {
  if (mid === 'community-module') return 'lms-community-intentional-shift-submissions';
  if (mid === 'educator-module') return 'lms-educator-intentional-shift-submissions';
  return INTENTIONAL_SHIFT_STORAGE_KEY;
}
function surveyStorageKeyForModule(mid) {
  if (mid === 'community-module') return 'lms-community-module-survey-submissions';
  if (mid === 'educator-module') return 'lms-educator-module-survey-submissions';
  if (mid === 'parent-module') return PARENT_SURVEY_STORAGE_KEY;
  return WORKPLACE_SURVEY_STORAGE_KEY;
}
function participantReviewStorageKeyForModule(mid) {
  if (mid === 'community-module') return 'lms-community-participant-review';
  if (mid === 'educator-module') return 'lms-educator-participant-review';
  if (mid === 'parent-module') return PARENT_PARTICIPANT_REVIEW_STORAGE_KEY;
  return WORKPLACE_PARTICIPANT_REVIEW_STORAGE_KEY;
}
function parentPauseStorageKey(storyKey) {
  return `${PARENT_PAUSE_STORAGE_PREFIX}-${storyKey}`;
}
function parentActStorageKey(storyKey) {
  return `${PARENT_ACT_STORAGE_PREFIX}-${storyKey}`;
}
function metaPrivateNoteKeyForModule(mid) {
  if (mid === 'community-module') return COMMUNITY_META_PRIVATE_NOTE_KEY;
  if (mid === 'educator-module') return EDUCATOR_META_PRIVATE_NOTE_KEY;
  return '';
}

const SURVEY_STAGE_IDS = new Set(['workplace-survey', 'educator-survey', 'community-survey', 'parent-survey']);

/** Visiting these parts only updates position; completion is gated elsewhere (hotspots / survey submit). */
const STAGES_NOT_AUTO_COMPLETE_ON_VISIT = new Set(['barriers', 'actions', ...SURVEY_STAGE_IDS]);

function createEmptySurveyResponses() {
  return {
    awarenessBefore: {},
    awarenessAfter: {},
    experience: {},
    additionalFeedback: '',
    optional: {
      identitySelections: [],
      identityOther: '',
      followupInterview: '',
      currentRole: '',
      currentRoleOther: '',
      yearsExperience: '',
      workContext: '',
      workContextOther: '',
    },
  };
}

function isModuleProgressComplete(prog) {
  return Boolean(prog?.status === 'completed' || prog?.isCompleted);
}

/** Progressive reveal count for case-study slide 1 (lines, optional bullets, optional closing). */
function getM2SlideRevealTotal(slide) {
  if (!slide) return 0;
  const lines = slide.lines?.length || 0;
  const bullets = slide.bullets?.length || 0;
  const closing = slide.closingLine ? 1 : 0;
  return lines + bullets + closing;
}

function getM2SlideRevealParts(slide, revealCount) {
  const lines = slide?.lines || [];
  const bullets = slide?.bullets || [];
  const closingLine = slide?.closingLine;
  let remaining = Math.max(0, revealCount);
  const visibleLines = lines.slice(0, Math.min(lines.length, remaining));
  remaining -= visibleLines.length;
  const visibleBullets = bullets.slice(0, Math.min(bullets.length, remaining));
  remaining -= visibleBullets.length;
  const showClosing = Boolean(closingLine) && remaining >= 1;
  return { visibleLines, visibleBullets, showClosing, closingLine };
}

function reflectionResearchLineKey(line) {
  if (line && typeof line === 'object' && line.html) return line.html;
  if (line && typeof line === 'object' && line.variant === 'statsTable') {
    return `stats-table-${(line.headers || []).join('|')}`;
  }
  if (line && typeof line === 'object' && line.variant === 'groupedBarChart') {
    return `grouped-bar-${(line.categories || []).map((c) => c.label).join('|')}`;
  }
  if (line && typeof line === 'object' && line.variant === 'researchImage') {
    return `research-image-${line.src || ''}`;
  }
  return String(line);
}

function renderResearchStatsTable(statsTable, key) {
  if (!statsTable || !Array.isArray(statsTable.headers) || !Array.isArray(statsTable.rows)) return null;
  return (
    <figure key={key || 'research-stats-table'} className="parent-research-stats-table-wrap">
      <div className="parent-research-stats-table-scroll">
        <table className="parent-research-stats-table">
          <thead>
            <tr>
              {statsTable.headers.map((header) => (
                <th key={header} scope="col">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statsTable.rows.map((row) => (
              <tr key={Array.isArray(row) ? row.join('|') : String(row)}>
                {(Array.isArray(row) ? row : [row]).map((cell, cellIdx) => (
                  <td key={`${cellIdx}-${cell}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {statsTable.footnote ? (
        <figcaption className="parent-research-stats-table-footnote">{statsTable.footnote}</figcaption>
      ) : null}
    </figure>
  );
}

function renderResearchGroupedBarChart(chart, key) {
  if (!chart || !Array.isArray(chart.series) || !Array.isArray(chart.categories)) return null;
  const yMax = Number(chart.yMax) > 0 ? Number(chart.yMax) : 100;
  const valueSuffix = chart.valueSuffix != null ? String(chart.valueSuffix) : '%';
  const ticks = Array.isArray(chart.yTicks) && chart.yTicks.length
    ? chart.yTicks
    : [0, Math.round(yMax / 3), Math.round((yMax * 2) / 3), yMax];
  const dense = chart.series.length > 3 || chart.categories.length > 3;
  const formatValue = (value) => `${value}${valueSuffix}`;
  return (
    <figure
      key={key || 'research-grouped-bar'}
      className={`parent-research-bar-chart${dense ? ' parent-research-bar-chart--dense' : ''}`}
      aria-label={chart.title || 'Research comparison chart'}
    >
      {chart.title ? <h4 className="parent-research-bar-chart-title">{chart.title}</h4> : null}
      <div className="parent-research-bar-chart-legend">
        {chart.series.map((series) => (
          <span key={series.key} className="parent-research-bar-chart-legend-item">
            <span
              className="parent-research-bar-chart-swatch"
              style={{ background: series.color }}
              aria-hidden="true"
            />
            {series.label}
          </span>
        ))}
      </div>
      <div className="parent-research-bar-chart-plot">
        {chart.yAxisLabel ? (
          <span className="parent-research-bar-chart-y-label" aria-hidden="true">{chart.yAxisLabel}</span>
        ) : null}
        <div className="parent-research-bar-chart-y" aria-hidden="true">
          {[...ticks].reverse().map((tick) => (
            <span key={tick}>{formatValue(tick)}</span>
          ))}
        </div>
        <div className="parent-research-bar-chart-grid" aria-hidden="true">
          {ticks.map((tick) => (
            <span
              key={`grid-${tick}`}
              className="parent-research-bar-chart-gridline"
              style={{ bottom: `${(tick / yMax) * 100}%` }}
            />
          ))}
        </div>
        <div
          className="parent-research-bar-chart-categories"
          style={{ gridTemplateColumns: `repeat(${chart.categories.length}, minmax(0, 1fr))` }}
        >
          {chart.categories.map((category) => (
            <div key={category.label} className="parent-research-bar-chart-category">
              <div className="parent-research-bar-chart-bars">
                {chart.series.map((series) => {
                  const value = Number(category[series.key]) || 0;
                  return (
                    <div
                      key={`${category.label}-${series.key}`}
                      className="parent-research-bar-chart-bar"
                      style={{
                        height: `${Math.min(100, (value / yMax) * 100)}%`,
                        background: series.color,
                      }}
                      title={`${series.label}: ${formatValue(value)}`}
                      aria-label={`${category.label}, ${series.label}: ${formatValue(value)}`}
                    >
                      {!dense ? (
                        <span className="parent-research-bar-chart-value">{formatValue(value)}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <span className="parent-research-bar-chart-label">
                {category.shortLabel || category.label}
              </span>
            </div>
          ))}
        </div>
      </div>
      {chart.xAxisLabel ? (
        <p className="parent-research-bar-chart-x-label">{chart.xAxisLabel}</p>
      ) : null}
      {chart.caption ? (
        <figcaption className="parent-research-bar-chart-caption">{chart.caption}</figcaption>
      ) : null}
      {chart.footnote ? (
        <p className="parent-research-bar-chart-footnote">{chart.footnote}</p>
      ) : null}
    </figure>
  );
}

function renderReflectionResearchLine(line, options = {}) {
  if (line && typeof line === 'object' && line.variant === 'statsTable') {
    return renderResearchStatsTable(line, reflectionResearchLineKey(line));
  }
  if (line && typeof line === 'object' && line.variant === 'researchImage' && line.src) {
    const morePayload = line.moreImage || line.moreChart || null;
    return (
      <div
        key={reflectionResearchLineKey(line)}
        className="reflection-slide-line reflection-slide-line--emphasis parent-research-emphasis-with-stats"
      >
        <figure className="parent-research-stat-image-wrap">
          <img
            src={line.src}
            alt={line.alt || ''}
            className="parent-research-stat-image"
            loading="lazy"
          />
          {line.caption ? (
            <figcaption className="parent-research-bar-chart-caption">{line.caption}</figcaption>
          ) : null}
        </figure>
        {morePayload && typeof options.onOpenMoreChart === 'function' ? (
          <button
            type="button"
            className="parent-research-show-more-btn"
            onClick={() => options.onOpenMoreChart(morePayload)}
          >
            {line.moreLabel || 'Show more'}
          </button>
        ) : null}
      </div>
    );
  }
  if (line && typeof line === 'object' && line.variant === 'groupedBarChart') {
    return (
      <div
        key={reflectionResearchLineKey(line)}
        className="reflection-slide-line reflection-slide-line--emphasis parent-research-emphasis-with-stats"
      >
        {renderResearchGroupedBarChart(line)}
        {line.moreChart && typeof options.onOpenMoreChart === 'function' ? (
          <button
            type="button"
            className="parent-research-show-more-btn"
            onClick={() => options.onOpenMoreChart(line.moreChart)}
          >
            {line.moreLabel || 'Show more'}
          </button>
        ) : null}
      </div>
    );
  }
  if (line && typeof line === 'object' && line.html) {
    const isEmphasis = line.variant === 'emphasis';
    const hasExtras = Boolean(line.statsTable || line.groupedBarChart || line.afterHtml);
    if (isEmphasis && hasExtras) {
      return (
        <div key={reflectionResearchLineKey(line)} className="reflection-slide-line reflection-slide-line--emphasis parent-research-emphasis-with-stats">
          <p
            className="parent-research-emphasis-copy"
            dangerouslySetInnerHTML={{ __html: line.html }}
          />
          {line.statsTable ? renderResearchStatsTable(line.statsTable) : null}
          {line.groupedBarChart ? renderResearchGroupedBarChart(line.groupedBarChart) : null}
          {line.afterHtml ? (
            <p
              className="parent-research-emphasis-copy"
              dangerouslySetInnerHTML={{ __html: line.afterHtml }}
            />
          ) : null}
        </div>
      );
    }
    const variantClass = isEmphasis ? ' reflection-slide-line--emphasis' : '';
    return (
      <p
        key={reflectionResearchLineKey(line)}
        className={`reflection-slide-line${variantClass}`}
        dangerouslySetInnerHTML={{ __html: line.html }}
      />
    );
  }
  return (
    <p key={reflectionResearchLineKey(line)} className="reflection-slide-line">
      {line}
    </p>
  );
}

function metaReflexivityLineKey(line) {
  if (line && typeof line === 'object' && line.html) return line.html;
  return String(line);
}

function renderMetaReflexivityTextLine(line) {
  const heading =
    line === 'Why this matters' || line === 'The four parts invite you to:' ? ' meta-reflexivity-line--heading' : '';
  if (line && typeof line === 'object' && line.html) {
    return (
      <p
        key={metaReflexivityLineKey(line)}
        className={`meta-reflexivity-line${heading}`}
        dangerouslySetInnerHTML={{ __html: line.html }}
      />
    );
  }
  return (
    <p key={metaReflexivityLineKey(line)} className={`meta-reflexivity-line${heading}`}>
      {line}
    </p>
  );
}

const PARTICIPANT_REVIEW_Q1_OPTIONS = [
  { value: 'yes_completely', label: 'Yes, completely' },
  { value: 'mostly', label: 'Mostly' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'not_really', label: 'Not really' },
  { value: 'not_at_all', label: 'Not at all' },
];

const PARTICIPANT_REVIEW_SCALE_QUESTIONS = [
  {
    id: 'reflectsLivedPatterns',
    label:
      'The workplace module reflects patterns I recognize from lived experience, even if the details are not exactly my own.',
  },
  {
    id: 'conceptsPrecise',
    label: 'The concepts used in the module are precise and meaningful rather than vague or oversimplified.',
  },
  {
    id: 'decisionPointsSystems',
    label:
      'The module makes specific decision points (e.g., hiring, promotion, visibility, evaluation) easier to notice and helpful to understand that systems - not individuals - are the focus.',
  },
  {
    id: 'interactiveEngaging',
    label:
      'The module is interactive enough to keep sustained interest, with good use of videos and reflection questions.',
  },
  {
    id: 'memorableContent',
    label: 'There are images, phrases, or ideas that I would likely remember over time.',
  },
  {
    id: 'instructionsClear',
    label: 'The instructions are easy to hear/read and follow.',
  },
  {
    id: 'barriersSouthAsianStem',
    label:
      'The module would help someone in a decision-making role understand the barriers that South Asian women in STEM face differently.',
  },
];

const PARTICIPANT_REVIEW_YEARS_OPTIONS = [
  { value: 'under3', label: 'Less than 3 years' },
  { value: '4to10', label: '4-10 years' },
  { value: '11to20', label: '11-20 years' },
  { value: '20plus', label: '20+ years' },
];

const PARTICIPANT_REVIEW_STEMM_PROFESSIONS = [
  { id: 'science', label: 'Science' },
  { id: 'technology', label: 'Technology' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'mathematics', label: 'Mathematics' },
  { id: 'medicine', label: 'Medicine' },
  { id: 'finance_accounting', label: 'Finance & Accounting' },
];

/** Likert labels for participant review (aligned with copy on the form). */
const PARTICIPANT_REVIEW_LIKERT_HEADERS = [
  { value: 1, label: '1', fullLabel: 'Strongly disagree' },
  { value: 2, label: '2', fullLabel: 'Somewhat disagree' },
  { value: 3, label: '3', fullLabel: 'Neither agree nor disagree' },
  { value: 4, label: '4', fullLabel: 'Somewhat agree' },
  { value: 5, label: '5', fullLabel: 'Strongly agree' },
];

function initialParticipantReviewForm() {
  const ratings = {};
  const explains = {};
  for (const q of PARTICIPANT_REVIEW_SCALE_QUESTIONS) {
    ratings[q.id] = '';
    explains[q.id] = '';
  }
  return {
    q1IntroClarity: '',
    q1Explain: '',
    ratings,
    ratingExplains: explains,
    q9Mindful: '',
    q10YearsStemm: '',
    stemmProfessions: [],
  };
}
const SURVEY_SCALE_HEADERS = [
  { value: 1, label: 'SD', fullLabel: 'Strongly disagree' },
  { value: 2, label: 'D', fullLabel: 'Disagree' },
  { value: 3, label: 'NAD', fullLabel: 'Neither agree nor disagree' },
  { value: 4, label: 'A', fullLabel: 'Agree' },
  { value: 5, label: 'SA', fullLabel: 'Strongly agree' },
];
const SURVEY_AWARENESS_STATEMENTS = [
  { id: 'selfReflectiveEdi', label: 'I am self-reflective on the issues of equity, diversity and inclusion' },
  { id: 'recognizingBiases', label: 'I understand the importance of recognizing biases' },
  { id: 'mentalModelsAwareness', label: 'I have an awareness of the mental models I use on a daily basis (internal thinking tools that help make sense of complex information)' },
];
const SURVEY_EXPERIENCE_STATEMENTS = [
  { id: 'highQuality', label: 'The training was high quality' },
  { id: 'metExpectations', label: 'The training met my expectations' },
  { id: 'relevantToRole', label: 'The training was relevant to my job role' },
  { id: 'instructorEngaging', label: 'The instructor was knowledgeable and engaging' },
  { id: 'balancePresentationActivities', label: 'There was a good balance of presentations and interactive activities' },
  { id: 'comfortableParticipating', label: 'I felt comfortable participating in discussions and asking questions' },
  { id: 'changedHowISeeMyself', label: 'As a result of this training, I have changed the way I look at myself' },
  { id: 'challengedHeldIdeas', label: 'This training has challenged some of my firmly held ideas' },
  { id: 'discoveredInconsistencies', label: 'During this training I discovered inconsistencies in what I had previously believed to be right' },
  { id: 'awareDecisionsCreateBarriers', label: 'This training helped me become more aware of how decisions made in the workplace can unintentionally create barriers to advancement' },
  { id: 'awareSystemsCreateBarriers', label: 'This training helped me become more aware of how workplace systems can unintentionally create barriers to advancement' },
];

/** Workplace-only experience items (Q13–Q14 in full workplace survey). */
const SURVEY_WORKPLACE_ONLY_EXPERIENCE_IDS = new Set([
  'awareDecisionsCreateBarriers',
  'awareSystemsCreateBarriers',
]);

function getSurveyExperienceStatementsForModule(moduleId) {
  if (moduleId === 'workplace-module') return SURVEY_EXPERIENCE_STATEMENTS;
  return SURVEY_EXPERIENCE_STATEMENTS.filter((s) => !SURVEY_WORKPLACE_ONLY_EXPERIENCE_IDS.has(s.id));
}

const SURVEY_EXPERIENCE_NA_IDS = new Set([
  'instructorEngaging',
  'balancePresentationActivities',
  'comfortableParticipating',
]);

/** Last optional screen before submitted - Firebase save runs here. */
const SURVEY_SUBMIT_STEP_TYPE = 'optional-work-context';

const OPTIONAL_DEMOGRAPHIC_STEP_TYPES = new Set([
  'optional-role',
  'optional-years-experience',
  'optional-work-context',
]);

/** After follow-up interview "Yes", role / experience / work context are required. */
function isOptionalDemographicsRequired(optional) {
  return optional?.followupInterview === 'yes';
}

function isOptionalSurveyStepComplete(step, optional) {
  if (!step) return false;
  const opt = optional || {};
  const demographicsRequired = isOptionalDemographicsRequired(opt);

  if (step.type === 'optional-identity') return true;
  if (step.type === 'optional-followup') {
    return opt.followupInterview === 'yes' || opt.followupInterview === 'no';
  }
  if (OPTIONAL_DEMOGRAPHIC_STEP_TYPES.has(step.type)) {
    if (!demographicsRequired) return true;
    if (step.type === 'optional-role') {
      if (!opt.currentRole) return false;
      if (opt.currentRole === 'other' && !String(opt.currentRoleOther || '').trim()) return false;
      return true;
    }
    if (step.type === 'optional-years-experience') {
      return Boolean(opt.yearsExperience);
    }
    if (step.type === 'optional-work-context') {
      if (!opt.workContext) return false;
      if (opt.workContext === 'other' && !String(opt.workContextOther || '').trim()) return false;
      return true;
    }
  }
  return true;
}

/** intro + awareness + experience + feedback + optional (EDI + 4 follow-up) + submitted */
function getWorkplaceSurveyTotalSteps(moduleId = 'workplace-module') {
  return (
    1
    + SURVEY_AWARENESS_STATEMENTS.length
    + getSurveyExperienceStatementsForModule(moduleId).length
    + 1
    + 5
    + 1
  );
}

/** Counter label above each survey screen (not every step is a rated "question"). */
function getSurveyStepCounterLabel(step, stepIndex, steps, followupInterviewYes = false) {
  if (!step) return '';

  const isRatedQuestion = (s) =>
    s.type === 'awareness-question' || s.type === 'experience-question';

  const ratedQuestionTotal = steps.filter(isRatedQuestion).length;

  if (step.type === 'intro') {
    return 'Introduction';
  }
  if (isRatedQuestion(step)) {
    const n = steps.slice(0, stepIndex + 1).filter(isRatedQuestion).length;
    return `Question ${n} of ${ratedQuestionTotal}`;
  }
  if (step.type === 'feedback') {
    return 'Additional feedback';
  }
  const isOptionalQuestion = (s) => typeof s.type === 'string' && s.type.startsWith('optional-');
  const optionalQuestionTotal = steps.filter(isOptionalQuestion).length;

  if (step.type === 'optional-identity') {
    return 'Optional survey';
  }
  if (isOptionalQuestion(step)) {
    const n = steps.slice(0, stepIndex + 1).filter(isOptionalQuestion).length;
    if (step.type !== 'optional-followup' && followupInterviewYes) {
      return `Required question ${n} of ${optionalQuestionTotal}`;
    }
    return `Optional question ${n} of ${optionalQuestionTotal}`;
  }
  if (step.type === 'submitted') {
    return 'Survey complete';
  }
  return `Step ${stepIndex + 1} of ${steps.length}`;
}

/** Workplace survey certificate: template path in `utils/certificateCanvas.js` (`cert2.png`). */
/** Colors for word cloud (vibrant, varied palette like reference). */
const REFLECTION_WORD_CLOUD_COLORS = [
  '#0e7490', '#0891b2', '#06b6d4',
  '#be185d', '#db2777', '#ec4899',
  '#d97706', '#f59e0b', '#eab308',
  '#dc2626', '#ea580c', '#b91c1c',
  '#b45309', '#c2410c', '#a16207',
  '#059669', '#10b981', '#15803d',
  '#64748b', '#475569', '#94a3b8',
];

/** Seed words for the reflection word cloud (prefill / starter vocabulary). */
const REFLECTION_WORD_CLOUD_SEED = [
  { text: 'heartbreaking', value: 18 },
  { text: 'relatable', value: 17 },
  { text: 'emotional', value: 16 },
  { text: 'inspiring', value: 15 },
  { text: 'heavy', value: 14 },
  { text: 'honest', value: 13 },
  { text: 'painful', value: 12 },
  { text: 'powerful', value: 11 },
  { text: 'human', value: 10 },
  { text: 'moving', value: 9 },
];

/** Workplace Part 1 slide 1 - module overview video (replaces learning-goals reveal on slide 0). */
const WORKPLACE_M1_OVERVIEW_YOUTUBE_VIDEO_ID = 'HdeTW7unzwA';

const WORKPLACE_M1_SLIDES = [
  {
    title: '',
    youtubeVideoId: WORKPLACE_M1_OVERVIEW_YOUTUBE_VIDEO_ID,
  },
];

/** Workplace Part 2 slide 1 - Anika case study video (YouTube embed; replaces local MP4). */
const WORKPLACE_M2_ANIKA_STORY_YOUTUBE_VIDEO_ID = 'r6n7ZgHJdl0';

const WORKPLACE_M2_SLIDES = [
  {
    title: 'Why This Story Matters',
    youtubeVideoId: WORKPLACE_M2_ANIKA_STORY_YOUTUBE_VIDEO_ID,
    lines: [
      'This module introduces the animated story of "Anika" - not to portray a single individual as an outlier; but to reflect patterned dynamics identified in our study.',
      'As you watch the video, resist the urge to diagnose Anika.',
      'Instead, notice where organizational expectations, evaluation norms, and informal leadership criteria shape what becomes possible for her.',
      "Let's begin.",
    ],
  },
  /** Slides 2-4: former Module 3 (reflection); `lines` unused - content comes from reflection UI. */
  { title: 'Pause & Reflect', lines: [] },
  { title: 'Pause & Reflect', lines: [] },
  { title: 'Why this Matters (The Research Connection)', lines: [] },
];

const WORKPLACE_M3_REFLECTION_SLIDES = [
  'What are your initial reactions to Anika\'s story? Share your reaction in one word.',
  'None of these are "wrong" signals. They are useful signposts that help you look deeper.',
  'Discomfort can signal that a value or belief-like your sense of fairness or your definition of leadership-is being challenged by system realities.',
];

const WORKPLACE_M3_RESEARCH_CONNECTION_SLIDE = {
  title: 'Why this Matters (The Research Connection)',
  subtitle: 'Unpacking the Story: What\'s Really Going On?',
  lines: [
    'In the video, we see Anika trying to solve the problem by changing herself-straightening her posture, raising her voice, believing she needed more "leadership presence." What was missing was not her performance on the job, but the system\'s willingness to endorse her at the moment authority became possible. Putting yourself in the shoes of her colleague or manager, let\'s look now past surface-level cues and see the internalized barriers the system is triggering.',
  ],
  researchTitle: 'What our research shows: The Leaky Pipeline Is Not Accidental',
  researchLines: [
    'Our research uncovered six internalized barriers that South Asian women in STEMM report navigating. These are not isolated "personality traits"; they are adaptive responses to workplace environments that weren\'t built with them in mind. And this is connected to the leaky pipeline-our data shows that "leaks" or attrition intensify at specific transition points-education to workforce entry, early career advancement, and mid-career leadership. At these specific transition points, advancement depends on sponsorship, evaluation, or endorsement.',
  ],
};

const WORKPLACE_M4_GUIDED_STEPS = [
  'Start with Scene 1 and click each numbered hotspot to uncover barriers.',
  'After exploring Scene 1, move to Scene 2 to continue the pattern map.',
  'Use each barrier micro-slide to connect story signal and system trigger.',
];

const WORKPLACE_M4_RESEARCH_INSIGHT = {
  title: 'Research Insight: The Representation-Mentorship Gap',
  body:
    "Our quantitative analysis shows that leadership representation gaps co-occur with a lack of mentorship in 62.7% of cases. When employees like Anika don't see representation in leadership, it is significantly harder for them-and their managers-to envision them in those roles.",
  quoteTitle: 'In Her Own Words',
  quote:
    '"I think one thing I could change is seeing South Asian women as leaders... there\'s some sort of stereotype with the fact that South Asian women are just not perceived as leaders or getting promoted to positions of leadership where we definitely know we\'re capable. We just haven\'t been given the opportunity."',
};

/** Workplace syllabus Part 4, slide 1 - video beside PDF Lottie (replaces side copy). */
const WORKPLACE_M5_SLIDE0_YOUTUBE_VIDEO_ID = '8Cix1etGt3g';

const WORKPLACE_M5_SLIDES = [
  {
    title: 'Meta-Reflexivity Framework',
    lines: [
      // "Let's now apply this lens to your own context using the Meta-Reflexivity Framework.",
      // 'You can use this tool in whatever way feels most comfortable: by writing freehand, or typing into the PDF.',
    ],
  },
  {
    title: 'Were you able to think of an example/a moment?',
    lines: [
      'Were you able to think of an example/a moment?',
      'Share your answer in the poll below to see the results.',
    ],
  },
];

const WORKPLACE_M5_HEADER = '';

/** Workplace Part 5 - Revisiting Anika's Story video popup (replaces local MP4). */
const WORKPLACE_M6_REVISITING_ANIKA_YOUTUBE_VIDEO_ID = 'TXAFuThobS4';

const WORKPLACE_M6_SLIDES = [
  {
    title: "Revisiting Anika's Story: The Meta-Reflexive Manager",
    videoLayout: true,
    youtubeVideoId: WORKPLACE_M6_REVISITING_ANIKA_YOUTUBE_VIDEO_ID,
    lines: [
      'In the original story, Anika\'s manager praised her "technical brilliance" but failed to advocate for her growth, ultimately relying on subjective perceptions of "readiness" and "presence" to deny her a leadership role.',
      'If that manager had applied Meta-Reflexivity, the outcome could look very different.',
    ],
  },
  {
    title: "The Manager's Internal Shift",
    beforeTitleLayout: 'what-you-saw',
    linesBeforeTitle: [
      'What you just saw',
      'The manager did not become more supportive or empathetic. They became more intentional about how power operates.',
      'They:',
      'interrupted informal invalidation',
      'made sponsorship visible',
      'clarified what counts as leadership',
    ],
    lines: [],
    stages: [
      {
        label: 'The Observation',
        text: 'The manager notices that while Anika produces the work, junior male colleagues are the ones presenting it and gaining visibility.',
      },
      {
        label: 'The Assumption',
        text: 'The manager catches the internal thought: "Anika is too soft-spoken to lead a client meeting".',
      },
      {
        label: 'The Origin',
        text: 'Using meta-reflexivity, the manager asks: "Who taught me that leadership requires a loud, aggressive style?" They recognize this "niceness norm" is a systemic barrier that rewards one cultural style while punishing another.',
      },
      {
        label: 'The Shift',
        text: 'The manager realizes that self-doubt is often system-induced rather than self-generated. They decide to stop relying on subjective "readiness" and instead create a structured pathway for Anika.',
      },
    ],
  },
];

/** Workplace syllabus Part 6 - overview video (slide 2) + at-a-glance hotspots (slide 1) + commitment (slide 3). */
const WORKPLACE_M7_SLIDE0_YOUTUBE_VIDEO_ID = 'b0wLpjEfYTY';
const M7_HOTSPOT_SLIDE_INDEX = 0;
const M7_VIDEO_SLIDE_INDEX = 1;
const M7_SHIFT_SLIDE_INDEX = 2;

/** Resolve Part 6 slide roles from content (community orders video before hotspots). */
function getM7SlideIndices(slides) {
  const list = Array.isArray(slides) ? slides : [];
  const hotspot = list.findIndex((s) => /At-a-Glance/i.test(String(s?.title || '')));
  const video = list.findIndex((s) => Boolean(s?.youtubeVideoId));
  const shift = list.findIndex((s) => /Intentional Shift/i.test(String(s?.title || '')));
  return {
    hotspot: hotspot >= 0 ? hotspot : M7_HOTSPOT_SLIDE_INDEX,
    video: video >= 0 ? video : M7_VIDEO_SLIDE_INDEX,
    shift: shift >= 0 ? shift : M7_SHIFT_SLIDE_INDEX,
  };
}

const WORKPLACE_M7_SLIDES = [
  {
    title: 'At-a-Glance: Barriers and Employer Leverage Points',
    lines: [
      'Click the numbered spots on the image to explore.',
    ],
  },
  {
    title: 'Small Actions, Big Shifts - Your Role in the System',
    youtubeVideoId: WORKPLACE_M7_SLIDE0_YOUTUBE_VIDEO_ID,
    lines: [],
  },
  {
    title: 'One Intentional Shift',
    lines: [
      'Feel free to share your commitment statement here to inspire others like you on the journey!',
      'One thing I commit to examining or shifting in how I make or influence decisions is:',
    ],
  },
];
  
/** Poster for “Module intro” video: intro + workplace overview (Part 1 style UI). */
const INTRO_MODULE_VIDEO_POSTER_PATH = 'images/introThumb.jpg';
/** Intro video file when no YouTube ID is set (intro + workplace module intro). */
const INTRO_MODULE_VIDEO_PATH = 'videos/intro.mp4';
/** YouTube video ID for intro + workplace module intro; empty string uses MP4 above. */
const INTRO_MODULE_YOUTUBE_VIDEO_ID = 'Lf6Gkijy7uM';

function getIntroModuleYoutubeEmbedUrl(videoId, { captions = true } = {}) {
  const id = String(videoId || '').trim();
  if (!id) return '';
  const ccParams = captions ? 'cc_load_policy=1&cc_lang_pref=en' : 'cc_load_policy=0';
  return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&${ccParams}`;
}

const YOUTUBE_POSTER_FILES = ['maxresdefault.jpg', 'sddefault.jpg', 'hqdefault.jpg'];

function getYoutubePosterUrl(videoId, { quality = 'max' } = {}) {
  const id = String(videoId || '').trim();
  if (!id) return '';
  const fileByQuality = {
    max: 'maxresdefault.jpg',
    sd: 'sddefault.jpg',
    hq: 'hqdefault.jpg',
  };
  const file = fileByQuality[quality] || fileByQuality.max;
  return `https://img.youtube.com/vi/${id}/${file}`;
}

function advanceYoutubePosterFallback(img) {
  const match = String(img.src || '').match(/\/vi\/([^/]+)\/([^/?#]+)/);
  const id = match?.[1];
  const currentFile = match?.[2];
  if (!id) return false;
  const idx = YOUTUBE_POSTER_FILES.indexOf(currentFile);
  const nextIdx = idx < 0 ? 1 : idx + 1;
  if (nextIdx >= YOUTUBE_POSTER_FILES.length) return false;
  img.src = `https://img.youtube.com/vi/${id}/${YOUTUBE_POSTER_FILES[nextIdx]}`;
  return true;
}

function handleYoutubePosterImgError(event) {
  const img = event.currentTarget;
  if (!img) return;
  advanceYoutubePosterFallback(img);
}

/** maxresdefault often 404s or returns a tiny branded placeholder; step down to sd/hq. */
function handleYoutubePosterImgLoad(event) {
  const img = event.currentTarget;
  if (!img || img.naturalWidth >= 200) return;
  advanceYoutubePosterFallback(img);
}

const youtubePosterImgHandlers = {
  onError: handleYoutubePosterImgError,
  onLoad: handleYoutubePosterImgLoad,
};

/**
 * YouTube iframe shows its own chrome until play; show a thumbnail until click-to-load.
 * Pass `posterSrc` to keep a custom image (e.g. previous module artwork) instead of the YouTube frame.
 */
function IntroYoutubePosterPlay({
  embedUrl,
  videoId,
  posterSrc: posterSrcProp,
  posterImgProps = {},
  isPlaying,
  onPlay,
  title,
  mode,
}) {
  if (!embedUrl) return null;
  const customPoster = posterSrcProp ? String(posterSrcProp).trim() : '';
  const posterSrc = customPoster || getYoutubePosterUrl(videoId);
  if (isPlaying) {
    const src = `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1`;
    return (
      <div
        className={`intro-youtube-embed-wrap intro-youtube-embed-wrap--${mode} intro-youtube-chrome-crop`}
      >
        <iframe
          title={title}
          className={`intro-youtube-iframe${mode === 'overview' ? ' module-intro-video-iframe' : ''}`}
          src={src}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`intro-youtube-poster-btn intro-youtube-poster-btn--${mode}`}
      onClick={onPlay}
      aria-label={`Play video: ${title}`}
    >
      <img
        src={posterSrc}
        alt=""
        className="intro-youtube-poster-img"
        {...posterImgProps}
        {...(customPoster ? {} : youtubePosterImgHandlers)}
      />
      <span className="intro-youtube-poster-overlay" aria-hidden="true">
        <span className="intro-youtube-poster-play">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
    </button>
  );
}

const VIDEO_FOOTNOTE_TEXT = 'Visuals were developed using a combination of human-led animation design and AI-assisted tools. All narratives, dialogue, and scenarios are grounded in original research and lived experiences; and were developed by the researcher to highlight systemic patterns identified in the data through thematic analysis.';

/** Self-hosted MP4 only - hides download in native controls (YouTube iframes unchanged). */
const LOCAL_VIDEO_PLAYER_PROPS = {
  controls: true,
  controlsList: 'nodownload',
  playsInline: true,
  disablePictureInPicture: true,
};


const INTRO_MODULE_CONTENT = {
  id: 'intro-module',
  title: 'Introduction Module',
  subtitle: 'Breaking Barriers & Building Opportunity: Foundations for Systems Change',
  description: 'This introductory module is designed for leaders, educators, and community members who influence systems that shape access, opportunity, or advancement.',
  courseDescriptionLong: 'Welcome to Breaking Barriers & Building Opportunity. Our three-year, national research project explores the journeys of South Asian women in STEMM fields in Canada, and offers a practical, systems-informed way to understand how inequity is produced through patterns, norms and structures-not just through individual behaviour or intent. This introductory module provides the foundation for all the modules to follow in our learning series, which include interactive stories and activities. You may take them individually or concurrently, depending on your role and interest. Together, this innovative learning experience is designed to support a different way of seeing-one that makes systemic patterns more visible, and action more intentional.',
  learningGoals: [
    'Recognize how barriers are produced by systemic patterns, rather than individual fixes alone.',
    'Understand how barriers interact to shape belonging (or exclusion) through a deeper practice called meta-reflexivity.',
    'Identify leverage points within your spheres of influence-micro-moments where your decisions, assumptions, or actions can mitigate barriers.',
  ],
  sections: [
    {
      id: 'start-here',
      title: 'Start Here (Video)',
      content: '<p>This video sets the context for why this work began, how it was shaped, who\'s behind the scenes, and the future possibilities we look forward to unlocking!</p><div class="video-placeholder intro-video-placeholder"><h4>Introduction Video</h4><p><em>Video placeholder - embed the intro video here.</em></p></div>',
    },
    {
      id: 'learning-goals',
      title: 'Learning Goals',
      content: '<h3>Why this module exists - and how it will help you</h3><p>This module introduces you to the <strong>Breaking Barriers & Building Opportunity</strong> research as not just a repository of findings but as an innovative tool that you can apply in your own context. Shaped by systemic change goals of WAGE, it offers a shared language to:</p><ul><li><strong>Recognize</strong> how barriers are produced by systemic patterns, rather than individual fixes alone.</li><li><strong>Understand</strong> how barriers interact to shape belonging (or exclusion) through a deeper practice called meta-reflexivity.</li><li><strong>Identify</strong> leverage points within your spheres of influence - micro-moments where your decisions, assumptions, or actions can mitigate barriers.</li></ul>',
    },
    {
      id: 'meta-reflexivity',
      title: "The Lens We'll Use - Meta-Reflexivity",
      content: '<h3>Before moving forward</h3><p>It helps to name the lens this module is built on. <strong>Meta-reflexivity</strong> is a practice that goes beyond personal reflection. Rather than asking only what we think or feel, meta-reflexivity asks us to step back and examine:</p><ul><li>How did this way of thinking become "normal"?</li><li>What rules or norms are shaping this decision?</li><li>How do identity and context influence who is heard, trusted, or advanced?</li></ul><p>Our understanding of meta-reflexivity draws primarily from the work of sociologist <strong>Margaret Archer</strong>, whose research examines how individuals reflect on-and act within-the social structures that shape their choices. We have intentionally embedded this approach into the curriculum because our experience with equity and inclusion work shows that systems rarely change simply because of "good intentions". They change when people begin to <strong>notice - and interrupt -</strong> the patterns they participate in, often without realizing it.</p><h3>Shifting the focus</h3><p>The lens of meta-reflexivity helps shift the focus:</p><p><strong>From:</strong> "What should people do differently?"<br /><strong>To:</strong> "What is the system rewarding, discouraging, or making invisible?"</p><h3>How meta-reflexivity shows up in this learning series</h3><p>Throughout subsequent modules, you\'ll encounter real stories grounded in lived experiences from the research with South Asian women in STEMM fields across Canada. These stories are not case studies with "right answers", but invitations to practice meta-reflexivity by noticing:</p><ul><li>why researched barriers often go unseen or unnamed</li><li>how everyday systems and norms shape those barriers</li><li>where your role, decisions, or assumptions may influence what happens next</li></ul>',
    },
    {
      id: 'carry-forward',
      title: 'How to Carry This Lens Forward',
      content: '<h3>Across the learning series</h3><p>You\'ll encounter different contexts - workplaces, educational settings, community systems.</p><p>As you move through the stories, data points, and activities that follow, we invite you to practice meta-reflexivity through a specific orientation.</p><p>As you move into the module suited to the role you identify with, you\'re not being asked to remember definitions or frameworks. You\'re being asked to <strong>carry a way of seeing</strong>.</p><h3>An orientation for what follows</h3><p>Rather than asking: <em>Do I agree with this?</em> or <em>Would I have handled this differently?</em></p><p>Instead, notice: <strong>Where do systems succeed or fail in converting effort into opportunity</strong> - and, taken together, <strong>where do barriers appear to migrate rather than disappear?</strong></p><p>The research behind Breaking Barriers & Building Opportunity shows that inequity is rarely the result of a single moment or decision. Instead, it emerges through patterns that repeat across systems, roles, and time. Our curriculum is thus guided by the belief that when we can name such problems more clearly, we can name the solutions-and our subsequent actions-more clearly too.</p><h3>Using the Workbook</h3><p>To support this process, you\'ll also be invited to download a <strong>workbook</strong> that accompanies the module. The workbook is a space to pause, reflect, and make sense of what you\'re noticing-privately and at your own pace. You can use it to:</p><ul><li>track reflections prompted throughout the module</li><li>surface questions or tensions that matter in your own context</li><li>identify leverage points you may apply to your own work</li></ul><p>If you\'d like a deeper overview of the research findings that inform this work, you can refer to the <strong>Executive Summary</strong>, available as a downloadable resource.</p><p><strong>When you\'re ready, continue to the next module and begin applying this lens in practice.</strong></p>',
    },
  ],
};

/** Introduction module: slide decks per syllabus section (mirrors workplace M1 pattern). */
const INTRO_SECTION_SLIDES = {
  'start-here': [
    {
      title: 'Start Here (Video)',
      variant: 'video',
      lines: [
        "This video sets the context for why this work began, how it was shaped, who's behind the scenes, and the future possibilities we look forward to unlocking!",
      ],
    },
  ],
  'learning-goals': [
    {
      title: 'Why this module exists',
      lines: [
        'This module introduces you to the Breaking Barriers & Building Opportunity research as not just a repository of findings but as an innovative tool that you can apply in your own context.',
        'Shaped by systemic change goals of WAGE, it offers a shared language to:',
      ],
    },
    {
      title: 'How it will help you',
      lines: [
        'Recognize how barriers are produced by systemic patterns, rather than individual fixes alone.',
        'Understand how barriers interact to shape belonging (or exclusion) through a deeper practice called meta-reflexivity.',
        'Identify leverage points within your spheres of influence - micro-moments where your decisions, assumptions, or actions can mitigate barriers.',
      ],
    },
  ],
  'meta-reflexivity': [
    {
      title: "The lens we'll use - meta-reflexivity",
      lines: [
        'It helps to name the lens this module is built on. Meta-reflexivity is a practice that goes beyond personal reflection. Rather than asking only what we think or feel, meta-reflexivity asks us to step back and examine:',
        'How did this way of thinking become "normal"?',
        'What rules or norms are shaping this decision?',
        'How do identity and context influence who is heard, trusted, or advanced?',
      ],
    },
    {
      title: 'Research grounding',
      lines: [
        'Our understanding of meta-reflexivity draws primarily from the work of sociologist Margaret Archer, whose research examines how individuals reflect on-and act within-the social structures that shape their choices.',
        'We have intentionally embedded this approach into the curriculum because our experience with equity and inclusion work shows that systems rarely change simply because of "good intentions".',
        'They change when people begin to notice-and interrupt-the patterns they participate in, often without realizing it.',
      ],
    },
    {
      title: 'How meta-reflexivity shows up in this series',
      lines: [
        'The lens of meta-reflexivity helps shift the focus from "What should people do differently?" to "What is the system rewarding, discouraging, or making invisible?"',
        "Throughout subsequent modules, you'll encounter real stories grounded in lived experiences from the research with South Asian women in STEMM fields across Canada.",
        'These stories are not case studies with "right answers", but invitations to practice meta-reflexivity by noticing: why researched barriers often go unseen or unnamed; how everyday systems and norms shape those barriers; where your role, decisions, or assumptions may influence what happens next.',
      ],
    },
  ],
  'carry-forward': [
    {
      title: 'Across the learning series',
      lines: [
        "You'll encounter different contexts - workplaces, educational settings, community systems.",
        'As you move through the stories, data points, and activities that follow, we invite you to practice meta-reflexivity through a specific orientation.',
        "As you move into the module suited to the role you identify with, you're not being asked to remember definitions or frameworks. You're being asked to carry a way of seeing.",
      ],
    },
    {
      title: 'An orientation for what follows',
      lines: [
        'Rather than asking: "Do I agree with this?" or "Would I have handled this differently?"',
        'Instead, notice: Where do systems succeed or fail in converting effort into opportunity - and, taken together, where do barriers appear to migrate rather than disappear?',
        'The research behind Breaking Barriers & Building Opportunity shows that inequity is rarely the result of a single moment or decision. Instead, it emerges through patterns that repeat across systems, roles, and time.',
        'Our curriculum is guided by the belief that when we can name such problems more clearly, we can name the solutions - and our subsequent actions - more clearly too.',
      ],
    },
    {
      title: 'Using the workbook - and next steps',
      lines: [
        "To support this process, you'll also be invited to download a workbook that accompanies the module. The workbook is a space to pause, reflect, and make sense of what you're noticing - privately and at your own pace. You can use it to track reflections prompted throughout the module, surface questions or tensions that matter in your own context, and identify leverage points you may apply to your own work.",
        "If you'd like a deeper overview of the research findings that inform this work, you can refer to the Executive Summary, available as a downloadable resource.",
        "When you're ready, continue to the next module and begin applying this lens in practice.",
      ],
    },
  ],
};

/** Introduction workshop: last syllabus section (Part 4 of 4) - show exit choices after final slide. */
const INTRO_MODULE_FINAL_SECTION_ID = 'carry-forward';

const WORKPLACE_CONTENT = {
  id: 'workplace-module',
  title: 'Building Workplace Bridges',
  subtitle: 'Breaking Barriers: Understanding Systemic Workplace Challenges',
  description: 'This module is designed for employers - leaders with informal or formal accountability in shaping hiring, evaluation, and work design decisions.',
  courseDescriptionLong: 'Drawing on national research with South Asian women across Canada, this learning experience uses story, reflection, and practical application to apply meta-reflexivity.',
  learningGoals: [
    'Recognize how workplace barriers are produced by systemic patterns, rather than individual issues alone.',
    'Identify where your own practices-hiring, mentoring, or promoting-may unintentionally activate those barriers.',
    'Redesign decisions and structures so talent reliably converts into progression and leadership.',
  ],
  sections: [
    { id: 'overview', title: 'Module Purpose & Learning Goals', content: '<h3>How to Use this Module:</h3><p>This module is designed for <strong>employers - leaders with informal or formal accountability</strong> in shaping hiring, evaluation, and work design decisions.</p><ul><li><strong>Recognize</strong> how workplace barriers are produced by systemic patterns, rather than individual issues alone.</li><li><strong>Identify</strong> where your own practices-hiring, mentoring, or promoting-may unintentionally activate those barriers.</li><li><strong>Redesign</strong> decisions and structures so talent reliably converts into progression and leadership.</li></ul><p>In this module, you&apos;ll be introduced to real stories - based on lived experiences from our research - which are designed to invite meta-reflexivity: deeper understanding of why certain barriers go unseen, how those barriers are shaped by systems, what role you might play in shifting them.</p>' },
    { id: 'anika-story', title: "Anika's Case Study & Pause & Reflect", content: '<h3>Why This Story Matters</h3><p>This module introduces the animated story of &ldquo;Anika&rdquo; - not to portray a single individual as an outlier; but to reflect patterned dynamics identified in our study.</p><p>As you watch the video, resist the urge to diagnose Anika. Instead, notice where organizational expectations, evaluation norms, and informal leadership criteria shape what becomes possible for her.</p><p>Let&apos;s begin.</p>' },
    { id: 'barriers', title: 'The Six Barriers', content: '<p>Our research uncovered six internalized barriers that South Asian women in STEMM report navigating.</p>' },
    { id: 'meta-reflexivity', title: 'Meta-Reflexivity Framework', content: '' },
    { id: 'meta-reflexive-manager', title: "Revisiting Anika's Story", content: '' },
    { id: 'actions', title: 'Small Actions, Big Shifts', content: '<h3>Small Actions, Big Shifts</h3><p>Every decision you make touches the system. Map your role: where do you touch the system? Where do barriers surface? Identify one point of influence.</p>' },
    { id: 'workplace-survey', title: 'Building Workplace Bridges Survey', content: '' },
  ],
};

function getPlaceholderContent(moduleId) {
  const info = ALL_MODULES.find((m) => m.id === moduleId) || { title: 'Module', id: moduleId };
  const titles = {
    'educator-module': 'Educator Module',
    'community-module': 'Community Module',
    'parent-module': 'Parent Module',
  };
  const descs = {
    'educator-module': 'Supporting learners and fostering inclusive STEMM education.',
    'community-module': 'Building community connections and collective impact.',
    'parent-module': 'Guiding and supporting the next generation in STEMM.',
  };
  const title = titles[moduleId] || info.title;
  const desc = descs[moduleId] || 'Content for this module is coming soon.';
  return {
    id: moduleId,
    title,
    subtitle: desc,
    description: `This module is designed for ${title.replace(' Module', '')}. Content will be available soon.`,
    courseDescriptionLong: desc,
    learningGoals: ['Content coming soon.'],
    sections: [
      { id: 'overview', title: 'Overview', content: `<h3>${title}</h3><p>${desc}</p><p>Detailed content for this module will be added here.</p>` },
    ],
  };
}

const StudyModule = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { firebaseUser, moduleProgress, refreshModuleProgress, loading: authLoading } = useAuth();

  const isModuleCompleted = useMemo(() => {
    const prog = moduleProgress?.find((p) => p.moduleId === moduleId);
    return isModuleProgressComplete(prog);
  }, [moduleProgress, moduleId]);
  const isCommunityModule = moduleId === 'community-module';
  const isEducatorModule = moduleId === 'educator-module';
  const isParentModule = moduleId === 'parent-module';
  const isFiveBarrierModule = isEducatorModule || isCommunityModule;
  const isStudyFlowModule = moduleId === 'workplace-module' || isFiveBarrierModule || isParentModule;
  const participantReviewEnabled = false;
  const surveyStageId = isParentModule
    ? 'parent-survey'
    : isCommunityModule
      ? 'community-survey'
      : isEducatorModule
        ? 'educator-survey'
        : 'workplace-survey';
  const metaPrivateNoteKey = metaPrivateNoteKeyForModule(moduleId);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'learning'
  const [activeSection, setActiveSection] = useState('overview');
  const [reflectionAnswers, setReflectionAnswers] = useState({});
  const [reflectionSubmitted, setReflectionSubmitted] = useState(false);
  const [revealedBarriers, setRevealedBarriers] = useState([]); // indices 0-5
  /** Module 4: which barrier is shown in the slide-out (null = closed). */
  const [barrierSlideOutIndex, setBarrierSlideOutIndex] = useState(null);
  /** Module 4: which carousel slide (0 = barriers 1-3, 1 = 4-6). */
  const [barriersCarouselSlide, setBarriersCarouselSlide] = useState(0);
  const [barriersGuideRevealCount, setBarriersGuideRevealCount] = useState(1);
  const [barriersLearningSlide, setBarriersLearningSlide] = useState(0);
  const [barriersExploreModalOpen, setBarriersExploreModalOpen] = useState(false);
  /** Module 7 (Small Actions): which spot modal is open (null = closed). */
  const [actionModalIndex, setActionModalIndex] = useState(null);
  const [actionsTableModalOpen, setActionsTableModalOpen] = useState(false);
  const [revealedActionSpots, setRevealedActionSpots] = useState([]); // spot indices shown in image
  const [intentionalShiftText, setIntentionalShiftText] = useState('');
  const [intentionalShiftSubmitted, setIntentionalShiftSubmitted] = useState(false);
  const [intentionalShiftSaving, setIntentionalShiftSaving] = useState(false);
  const [surveyResponses, setSurveyResponses] = useState(createEmptySurveyResponses);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [surveySaving, setSurveySaving] = useState(false);
  const [certificateDownloading, setCertificateDownloading] = useState(false);
  const [participantReviewModalOpen, setParticipantReviewModalOpen] = useState(false);
  const [participantReviewForm, setParticipantReviewForm] = useState(() => initialParticipantReviewForm());
  const [participantReviewErrors, setParticipantReviewErrors] = useState({});
  const [participantReviewSubmitting, setParticipantReviewSubmitting] = useState(false);
  const [participantReviewSubmitted, setParticipantReviewSubmitted] = useState(false);
  const [educatorMetaPrivateNote, setEducatorMetaPrivateNote] = useState('');
  const [educatorBarrierPollSelection, setEducatorBarrierPollSelection] = useState([]);
  const [educatorBarrierPollSubmitted, setEducatorBarrierPollSubmitted] = useState(false);
  const [educatorBarrierPollSaving, setEducatorBarrierPollSaving] = useState(false);
  const [educatorBarrierPollAgg, setEducatorBarrierPollAgg] = useState(null);
  const [educatorBarrierPollStatus, setEducatorBarrierPollStatus] = useState('idle');
  /** Module 5: prompt before PDF download - moment type + optional "Other" text */
  const [metaReflexivityMomentTypes, setMetaReflexivityMomentTypes] = useState([]);
  const [metaReflexivityMomentOther, setMetaReflexivityMomentOther] = useState('');
  const [workplaceM1Slide, setWorkplaceM1Slide] = useState(0);
  const [workplaceM1VideoPlaying, setWorkplaceM1VideoPlaying] = useState(false);
  const [workplaceM5Slide0VideoPlaying, setWorkplaceM5Slide0VideoPlaying] = useState(false);
  const [workplaceM1RevealCount, setWorkplaceM1RevealCount] = useState(1);
  const [workplaceM2Slide, setWorkplaceM2Slide] = useState(0);
  const [workplaceM2RevealCount, setWorkplaceM2RevealCount] = useState(1);
  const [workplaceM2VideoModalOpen, setWorkplaceM2VideoModalOpen] = useState(false);
  const [workplaceM2YoutubePlayingModal, setWorkplaceM2YoutubePlayingModal] = useState(false);
  const [parentStorySlide, setParentStorySlide] = useState(0);
  const [parentStoryRevealCount, setParentStoryRevealCount] = useState(1);
  const [parentStoryVideoModalOpen, setParentStoryVideoModalOpen] = useState(false);
  const [parentStoryYoutubePlayingModal, setParentStoryYoutubePlayingModal] = useState(false);
  const [parentResearchMoreChart, setParentResearchMoreChart] = useState(null);
  const [parentPauseAnswers, setParentPauseAnswers] = useState({ sara: '', anika: '', aman: '' });
  const [parentPauseSubmitted, setParentPauseSubmitted] = useState({ sara: false, anika: false, aman: false });
  const [parentActAnswers, setParentActAnswers] = useState({ sara: '', anika: '', aman: '' });
  const [parentActSubmitted, setParentActSubmitted] = useState({ sara: false, anika: false, aman: false });
  const [parentActSaving, setParentActSaving] = useState(false);
  const [parentWordCloudCounts, setParentWordCloudCounts] = useState(null);
  const parentWordCloudFetchRef = useRef(0);
  const parentWordCloudContainerRef = useRef(null);
  const [parentWordCloudSize, setParentWordCloudSize] = useState([640, 280]);
  const [workplaceM3RevealCount, setWorkplaceM3RevealCount] = useState(1);
  const [workplaceM3FigureModalOpen, setWorkplaceM3FigureModalOpen] = useState(false);
  const [reflectionCelebrate, setReflectionCelebrate] = useState(false);
  const [workplaceM5Slide, setWorkplaceM5Slide] = useState(0);
  const [workplaceM5RevealCount, setWorkplaceM5RevealCount] = useState(1);
  const [workplaceM5InputStep, setWorkplaceM5InputStep] = useState(0);
  const [metaReflexivityCelebrate, setMetaReflexivityCelebrate] = useState(false);
  const [metaReflexivityPoll, setMetaReflexivityPoll] = useState(null);
  const [metaReflexivityPollStatus, setMetaReflexivityPollStatus] = useState('idle');
  const [metaReflexivityUserChoice, setMetaReflexivityUserChoice] = useState(null);
  const [workplaceM6Slide, setWorkplaceM6Slide] = useState(0);
  const [workplaceM6RevealCount, setWorkplaceM6RevealCount] = useState(1);
  const [workplaceM6VideoModalOpen, setWorkplaceM6VideoModalOpen] = useState(false);
  const [workplaceM6YoutubePlayingModal, setWorkplaceM6YoutubePlayingModal] = useState(false);
  const [workplaceM7Slide, setWorkplaceM7Slide] = useState(0);
  const [workplaceM7Slide0VideoPlaying, setWorkplaceM7Slide0VideoPlaying] = useState(false);
  const [workplaceM7RevealCount, setWorkplaceM7RevealCount] = useState(1);
  const [workplaceM7InputReady, setWorkplaceM7InputReady] = useState(false);
  const [workplaceSurveyStep, setWorkplaceSurveyStep] = useState(0);
  const [introSlideIndex, setIntroSlideIndex] = useState(0);
  const [introRevealCount, setIntroRevealCount] = useState(1);
  const [introVideoModalOpen, setIntroVideoModalOpen] = useState(false);
  const [introYoutubePlayingOverview, setIntroYoutubePlayingOverview] = useState(false);
  const [introYoutubePlayingModal, setIntroYoutubePlayingModal] = useState(false);
  const [contentKey, setContentKey] = useState(0);
  const [suggestedPage, setSuggestedPage] = useState(1);
  const [syllabusModalSectionId, setSyllabusModalSectionId] = useState(null);
  const [isDesktopOverviewLayout, setIsDesktopOverviewLayout] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1025px)').matches;
  });
  const [mobileOverviewInfoOpen, setMobileOverviewInfoOpen] = useState(false);
  /** Scroll containers: syllabus step content (overview) and topic content (learning). */
  const overviewStepContentRef = useRef(null);
  const moduleMainScrollRef = useRef(null);
  const introModalVideoRef = useRef(null);
  const participantReviewAutoOpenRef = useRef(false);

  /** Overview: step content may scroll inside the right panel, or the window/main on stacked layouts. */
  const scrollOverviewStepSurfacesToTop = () => {
    const opts = { top: 0, left: 0, behavior: 'smooth' };
    window.scrollTo(opts);
    document.documentElement.scrollTo(opts);
    document.body.scrollTo(opts);
    document.getElementById('main-content')?.scrollTo(opts);
    overviewStepContentRef.current?.scrollTo(opts);
  };
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1025px)');
    const syncOverviewLayout = (event) => {
      setIsDesktopOverviewLayout(event.matches);
    };
    syncOverviewLayout(mq);
    mq.addEventListener('change', syncOverviewLayout);
    return () => mq.removeEventListener('change', syncOverviewLayout);
  }, []);

  const useOverviewStepperLayout = OVERVIEW_PARTS_STEPPER_EXPERIMENT && !isDesktopOverviewLayout;

  const overviewDetailsOpen = useOverviewStepperLayout
    ? (mobileOverviewInfoOpen || Boolean(syllabusModalSectionId))
    : (isDesktopOverviewLayout || mobileOverviewInfoOpen || Boolean(syllabusModalSectionId));

  const progressRefreshInFlightRef = useRef(false);
  const progressRefreshQueuedRef = useRef(false);
  const progressRefreshTimerRef = useRef(null);
  const lastProgressRefreshAtRef = useRef(0);
  /** Step-wise progression: only the next step is clickable after completing the previous (workplace + educator modules) */
  const [completedSyllabusSteps, setCompletedSyllabusSteps] = useState([]);
  const isCaseStudySectionVisible = isStudyFlowModule && !isParentModule
    && (activeSection === 'anika-story' || syllabusModalSectionId === 'anika-story');
  const isMetaReflexivitySectionVisible = isStudyFlowModule
    && (activeSection === 'meta-reflexivity' || syllabusModalSectionId === 'meta-reflexivity');
  const isMetaReflexiveManagerSectionVisible = isStudyFlowModule
    && (activeSection === 'meta-reflexive-manager' || syllabusModalSectionId === 'meta-reflexive-manager');
  const isActionsSectionVisible = isStudyFlowModule
    && (activeSection === 'actions' || syllabusModalSectionId === 'actions');
  const isParentStorySectionActive = isParentModule
    && PARENT_STORY_SECTION_IDS.includes(viewMode === 'overview' ? syllabusModalSectionId : activeSection);
  const activeParentStorySectionId = isParentStorySectionActive
    ? (viewMode === 'overview' ? syllabusModalSectionId : activeSection)
    : null;
  const isEndSurveySectionVisible = isStudyFlowModule
    && (activeSection === surveyStageId || syllabusModalSectionId === surveyStageId);

  const moduleContent =
    moduleId === 'intro-module'
      ? INTRO_MODULE_CONTENT
      : moduleId === 'workplace-module'
        ? WORKPLACE_CONTENT
        : moduleId === 'educator-module'
          ? EDUCATOR_CONTENT
          : moduleId === 'community-module'
            ? COMMUNITY_CONTENT
            : moduleId === 'parent-module'
              ? PARENT_CONTENT
              : getPlaceholderContent(moduleId);

  /** Removed syllabus id `reflection` (merged into `anika-story`); normalize stale UI state. */
  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (activeSection === 'reflection') setActiveSection('anika-story');
    if (syllabusModalSectionId === 'reflection') setSyllabusModalSectionId('anika-story');
    setCompletedSyllabusSteps((prev) => {
      if (!prev.includes('reflection')) return prev;
      const without = prev.filter((id) => id !== 'reflection');
      return without.includes('anika-story') ? without : [...without, 'anika-story'];
    });
  }, [moduleId, activeSection, syllabusModalSectionId]);

  const suggestedModules = ALL_MODULES.filter((m) => m.id !== moduleId);

  const catalogModule = useMemo(
    () => MODULES_CATALOG.find((m) => m.id === moduleId) ?? null,
    [moduleId]
  );
  const isComingSoonModule = Boolean(catalogModule?.comingSoon);

  const introVideoPosterSrc = useMemo(() => {
    const base = process.env.PUBLIC_URL || '';
    if (moduleId === 'intro-module' || moduleId === 'workplace-module') {
      return `${base}/${INTRO_MODULE_VIDEO_POSTER_PATH}`;
    }
    const fromCatalog = MODULES_CATALOG.find((m) => m.id === moduleId)?.imageUrl;
    return fromCatalog || `${base}/images/module_2.png`;
  }, [moduleId]);


  const introYoutubeEmbedUrl = useMemo(
    () =>
      moduleId === 'intro-module' || moduleId === 'workplace-module'
        ? getIntroModuleYoutubeEmbedUrl(INTRO_MODULE_YOUTUBE_VIDEO_ID)
        : '',
    [moduleId]
  );

  const introVideoSrc = useMemo(() => {
    const base = process.env.PUBLIC_URL || '';
    if (moduleId === 'intro-module' || moduleId === 'workplace-module') {
      return `${base}/${INTRO_MODULE_VIDEO_PATH}`;
    }
    return '';
  }, [moduleId]);

  useEffect(() => {
    if (introVideoModalOpen) return;
    const el = introModalVideoRef.current;
    if (!el || typeof el.pause !== 'function') return;
    try {
      el.pause();
      el.currentTime = 0;
    } catch (_) {}
  }, [introVideoModalOpen]);

  useEffect(() => {
    if (introVideoModalOpen) setIntroYoutubePlayingModal(false);
  }, [introVideoModalOpen]);

  useEffect(() => {
    setIntroYoutubePlayingOverview(false);
  }, [moduleId]);

  const handleReflectionChange = (questionId, value) => {
    setReflectionAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleReflectionSubmit = async () => {
    const text = (reflectionAnswers.reflection1 || '').trim();
    if (!text) return;
    const rk = reflectionStorageKeyForModule(moduleId);
    try {
      const stored = JSON.parse(localStorage.getItem(rk) || '[]');
      stored.push({ text, at: new Date().toISOString() });
      localStorage.setItem(rk, JSON.stringify(stored));
    } catch (_) {}
    const saved = await writeReflectionSubmission(text, moduleId);
    setReflectionSubmitted(true);
    if (!saved) {
      window.alert(
        'Your word was saved on this device, but could not be saved to your account. Please sign in so it is stored with your progress and included in the shared word cloud.'
      );
    }
  };

  const showWordCloud = reflectionSubmitted;

  const getReflectionSubmissions = () => {
    try {
      return JSON.parse(localStorage.getItem(reflectionStorageKeyForModule(moduleId)) || '[]');
    } catch {
      return [];
    }
  };

  const requestProgressRefresh = () => {
    if (!firebaseUser?.uid || typeof refreshModuleProgress !== 'function') return;
    const MIN_REFRESH_INTERVAL_MS = 1200;

    const runRefresh = async () => {
      if (progressRefreshInFlightRef.current) {
        progressRefreshQueuedRef.current = true;
        return;
      }
      progressRefreshInFlightRef.current = true;
      try {
        await refreshModuleProgress();
        lastProgressRefreshAtRef.current = Date.now();
      } finally {
        progressRefreshInFlightRef.current = false;
        if (progressRefreshQueuedRef.current) {
          progressRefreshQueuedRef.current = false;
          requestProgressRefresh();
        }
      }
    };

    const elapsed = Date.now() - lastProgressRefreshAtRef.current;
    if (elapsed >= MIN_REFRESH_INTERVAL_MS) {
      void runRefresh();
      return;
    }
    if (progressRefreshTimerRef.current) return;
    progressRefreshTimerRef.current = setTimeout(() => {
      progressRefreshTimerRef.current = null;
      void runRefresh();
    }, MIN_REFRESH_INTERVAL_MS - elapsed);
  };


  useEffect(() => {
    if (!isStudyFlowModule) return;
    const existing = getReflectionSubmissions();
    if (!existing.length) return;
    // User has already submitted at least once on this device/session:
    // skip prompt and show the cloud directly when revisiting.
    setReflectionSubmitted(true);
  }, [moduleId]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    try {
      const existing = JSON.parse(localStorage.getItem(intentionalShiftStorageKeyForModule(moduleId)) || '[]');
      if (!Array.isArray(existing) || !existing.length) return;
      const latest = existing[existing.length - 1];
      if (!latest?.text) return;
      setIntentionalShiftText(String(latest.text));
      setIntentionalShiftSubmitted(true);
    } catch {
      // no-op
    }
  }, [moduleId]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    const prog = moduleProgress?.find((p) => p.moduleId === moduleId);
    const moduleSurveyComplete = isModuleProgressComplete(prog);
    try {
      const storageKey = surveyStorageKeyForModule(moduleId);
      const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!moduleSurveyComplete) {
        if (Array.isArray(existing) && existing.length) {
          localStorage.removeItem(storageKey);
        }
        setSurveySubmitted(false);
        setWorkplaceSurveyStep(0);
        setSurveyResponses(createEmptySurveyResponses());
        setParticipantReviewSubmitted(false);
        return;
      }
      if (!Array.isArray(existing) || !existing.length) return;
      const latest = existing[existing.length - 1];
      if (!latest) return;
      setSurveyResponses({
        awarenessBefore: latest.awarenessBefore || {},
        awarenessAfter: latest.awarenessAfter || {},
        experience: latest.experience || {},
        additionalFeedback: latest.additionalFeedback || '',
        optional: {
          identitySelections: Array.isArray(latest.optional?.identitySelections)
            ? latest.optional.identitySelections
            : [],
          identityOther: latest.optional?.identityOther || '',
          followupInterview: latest.optional?.followupInterview || '',
          currentRole: latest.optional?.currentRole || '',
          currentRoleOther: latest.optional?.currentRoleOther || '',
          yearsExperience: latest.optional?.yearsExperience || '',
          workContext: latest.optional?.workContext || '',
          workContextOther: latest.optional?.workContextOther || '',
        },
      });
      setSurveySubmitted(true);
      setWorkplaceSurveyStep(getWorkplaceSurveyTotalSteps(moduleId) - 1);
    } catch {
      // no-op
    }
  }, [moduleId, moduleProgress, isStudyFlowModule]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    try {
      const raw = localStorage.getItem(participantReviewStorageKeyForModule(moduleId));
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data?.submitted) setParticipantReviewSubmitted(true);
    } catch {
      // no-op
    }
  }, [moduleId]);

  useEffect(() => {
    if (!participantReviewEnabled) return;
    if (!isEndSurveySectionVisible) {
      participantReviewAutoOpenRef.current = false;
      return;
    }
    if (isModuleCompleted) return;
    if (participantReviewSubmitted) return;
    const lastIdx = getWorkplaceSurveyTotalSteps(moduleId) - 1;
    if (workplaceSurveyStep !== lastIdx) return;
    if (participantReviewAutoOpenRef.current) return;
    participantReviewAutoOpenRef.current = true;
    setParticipantReviewModalOpen(true);
  }, [participantReviewEnabled, isEndSurveySectionVisible, isModuleCompleted, participantReviewSubmitted, workplaceSurveyStep]);

  const handleIntentionalShiftSubmit = async () => {
    const trimmed = String(intentionalShiftText || '').trim();
    if (!trimmed || intentionalShiftSaving) return;
    setIntentionalShiftSaving(true);
    try {
      try {
        const existing = JSON.parse(localStorage.getItem(intentionalShiftStorageKeyForModule(moduleId)) || '[]');
        const next = Array.isArray(existing) ? existing : [];
        next.push({ text: trimmed, at: new Date().toISOString(), moduleId });
        localStorage.setItem(intentionalShiftStorageKeyForModule(moduleId), JSON.stringify(next));
      } catch (_) {}
      const saved = await writeIntentionalShiftCommitment({ moduleId, text: trimmed });
      setIntentionalShiftSubmitted(true);
      if (!saved) {
        window.alert(
          'Your commitment was saved on this device, but could not be saved to your account. Please sign in so it is stored with your progress.'
        );
      }
    } finally {
      setIntentionalShiftSaving(false);
    }
  };

  const setSurveyAwareness = (phase, statementId, value) => {
    setSurveyResponses((prev) => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        [statementId]: Number(value),
      },
    }));
  };

  const setSurveyExperience = (statementId, value) => {
    setSurveyResponses((prev) => ({
      ...prev,
      experience: {
        ...prev.experience,
        [statementId]: Number(value),
      },
    }));
  };

  const isSurveyComplete = useMemo(() => {
    const experienceStatements = getSurveyExperienceStatementsForModule(moduleId);
    const awarenessDone = SURVEY_AWARENESS_STATEMENTS.every(
      (s) => Number(surveyResponses.awarenessBefore?.[s.id]) >= 1 && Number(surveyResponses.awarenessAfter?.[s.id]) >= 1
    );
    const experienceDone = experienceStatements.every((s) => {
      const selected = Number(surveyResponses.experience?.[s.id]);
      if (SURVEY_EXPERIENCE_NA_IDS.has(s.id) && selected === 0) return true;
      return selected >= 1;
    });
    return awarenessDone && experienceDone;
  }, [surveyResponses, moduleId]);

  const handleSurveySubmit = async () => {
    if (!isSurveyComplete || surveySaving) return;
    setSurveySaving(true);
    try {
      const payload = {
        moduleId,
        awarenessBefore: surveyResponses.awarenessBefore,
        awarenessAfter: surveyResponses.awarenessAfter,
        experience: surveyResponses.experience,
        additionalFeedback: String(surveyResponses.additionalFeedback || '').trim() || null,
        optional: {
          identitySelections: Array.isArray(surveyResponses.optional?.identitySelections)
            ? surveyResponses.optional.identitySelections
            : [],
          identityOther: String(surveyResponses.optional?.identityOther || '').trim() || null,
          followupInterview: surveyResponses.optional?.followupInterview || null,
          currentRole: surveyResponses.optional?.currentRole || null,
          currentRoleOther: String(surveyResponses.optional?.currentRoleOther || '').trim() || null,
          yearsExperience: surveyResponses.optional?.yearsExperience || null,
          workContext: surveyResponses.optional?.workContext || null,
          workContextOther: String(surveyResponses.optional?.workContextOther || '').trim() || null,
        },
      };
      try {
        const existing = JSON.parse(localStorage.getItem(surveyStorageKeyForModule(moduleId)) || '[]');
        const next = Array.isArray(existing) ? existing : [];
        next.push({ ...payload, at: new Date().toISOString() });
        localStorage.setItem(surveyStorageKeyForModule(moduleId), JSON.stringify(next));
      } catch (_) {}
      await writeWorkplaceSurveyResponse(payload);
      setSurveySubmitted(true);
      if (firebaseUser?.uid) {
        const surveyPosition = Math.max(1, sectionsWithSteps.findIndex((s) => s.id === surveyStageId) + 1);
        void trackModuleStageProgress(
          firebaseUser.uid,
          moduleId,
          surveyStageId,
          sectionsWithSteps.length,
          surveyPosition,
          true
        )
          .then(() => markCurrentModuleCompleted())
          .finally(requestProgressRefresh);
      }
    } finally {
      setSurveySaving(false);
    }
  };

  const handleCertificateDownload = async () => {
    if (certificateDownloading) return;
    setCertificateDownloading(true);
    await markCurrentModuleCompleted();
    try {
      const baseUrl = process.env.PUBLIC_URL || '';
      const templateUrl = `${baseUrl}/${CERTIFICATE_TEMPLATE_PATH}`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const loaded = await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = templateUrl;
      });
      if (!loaded) throw new Error('Certificate template failed to load');

      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const email = String(firebaseUser?.email || '').trim();
      const participantName = resolveParticipantDisplayName({
        displayNameRaw: firebaseUser?.displayNameRaw,
        displayName: firebaseUser?.displayName,
        email,
        fallbackLabel: 'Participant',
      });
      const moduleDisplayName =
        moduleContent?.title
        || (moduleId === 'intro-module'
          ? 'Introduction Module'
          : moduleId === 'educator-module'
            ? 'Educator Module'
            : moduleId === 'community-module'
              ? 'Community Module'
              : 'Building Workplace Bridges');

      paintCertificateDynamicText(ctx, canvas, { participantName, moduleDisplayName });

      const safeName = participantName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'participant';
      const safeModule = String(moduleDisplayName)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40) || 'module';
      const filename = `${safeModule}-certificate-${safeName}.png`;

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.warn('[certificate] download failed, falling back to template', err);
      const fallback = document.createElement('a');
      fallback.href = `${process.env.PUBLIC_URL || ''}/${CERTIFICATE_TEMPLATE_PATH}`;
      fallback.download = 'certificate.png';
      document.body.appendChild(fallback);
      fallback.click();
      fallback.remove();
    } finally {
      setCertificateDownloading(false);
    }
  };

  const validateParticipantReviewFormState = () => ({});

  const handleParticipantReviewSubmit = async () => {
    if (participantReviewSubmitting) return;
    const errs = validateParticipantReviewFormState();
    setParticipantReviewErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setParticipantReviewSubmitting(true);
    const payload = {
      moduleId,
      q1IntroClarity: participantReviewForm.q1IntroClarity,
      q1Explain: String(participantReviewForm.q1Explain).trim(),
      scaleRatings: { ...participantReviewForm.ratings },
      scaleExplains: Object.fromEntries(
        Object.entries(participantReviewForm.ratingExplains).map(([k, v]) => [k, String(v || '').trim()])
      ),
      q9MindfulEmployers: String(participantReviewForm.q9Mindful).trim(),
      q10YearsStemmProfession: participantReviewForm.q10YearsStemm,
      q11StemmProfessions: [...participantReviewForm.stemmProfessions],
    };
    try {
      try {
        localStorage.setItem(
          participantReviewStorageKeyForModule(moduleId),
          JSON.stringify({ submitted: true, at: new Date().toISOString(), ...payload })
        );
      } catch (_) {}
      await writeWorkplaceParticipantReview(payload);
      setParticipantReviewSubmitted(true);
      setParticipantReviewModalOpen(false);
      setParticipantReviewErrors({});
    } finally {
      setParticipantReviewSubmitting(false);
    }
  };

  const toggleParticipantStemmProfession = (id) => {
    setParticipantReviewForm((prev) => {
      const next = new Set(prev.stemmProfessions);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, stemmProfessions: [...next] };
    });
  };

  const getBarrierIndicesInImage = () =>
    pickFlowModule(moduleId, ALL_BARRIER_HOTSPOTS, EDUCATOR_BARRIER_HOTSPOTS, COMMUNITY_BARRIER_HOTSPOTS).map((spot) => spot.barrierIndex);

  const openBarrierSlideOut = (barrierIndex) => {
    setRevealedBarriers((prev) => {
      const next = prev.includes(barrierIndex) ? prev : [...prev, barrierIndex];
      const required = getBarrierIndicesInImage();
      const allRevealed = required.every((id) => next.includes(id));

      // Module 4 should only be marked complete once all shown barriers are clicked.
      if (allRevealed) {
        setCompletedSyllabusSteps((done) => (done.includes('barriers') ? done : [...done, 'barriers']));
        if (firebaseUser?.uid) {
          void trackModuleStageProgress(
            firebaseUser.uid,
            moduleId,
            'barriers',
            sectionsWithSteps.length,
            Math.max(1, sectionsWithSteps.findIndex((s) => s.id === 'barriers') + 1),
            true
          ).finally(requestProgressRefresh);
        }
      }
      return next;
    });
    setBarrierSlideOutIndex(barrierIndex);
  };

  const closeBarrierSlideOut = () => {
    const carouselSlides = pickFlowModule(moduleId, BARRIER_CAROUSEL_SLIDES, EDUCATOR_BARRIER_CAROUSEL_SLIDES, COMMUNITY_BARRIER_CAROUSEL_SLIDES);
    const allExplored = getBarrierIndicesInImage().every((id) => revealedBarriers.includes(id));
    setBarrierSlideOutIndex(null);
    const slide0Ids = carouselSlides[0].hotspots.map((h) => h.barrierIndex);
    setBarriersCarouselSlide((slide) => {
      if (slide !== 0) return slide;
      const firstSceneDone = slide0Ids.every((id) => revealedBarriers.includes(id));
      if (carouselSlides.length <= 1) return 0;
      return firstSceneDone ? 1 : slide;
    });
  };

  const closeBarriersExploreModal = () => {
    setBarriersExploreModalOpen(false);
    setBarrierSlideOutIndex(null);
  };

  const toggleEducatorBarrierPollOption = (value) => {
    setEducatorBarrierPollSelection((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      return [...prev, value];
    });
  };

  const handleEducatorBarrierPollSubmit = async () => {
    if (!educatorBarrierPollSelection.length || educatorBarrierPollSaving) return;
    setEducatorBarrierPollSaving(true);
    try {
      await writeEducatorBarrierRecognitionResponse({
        barrierKeys: educatorBarrierPollSelection,
        moduleId,
      });
      setEducatorBarrierPollSubmitted(true);
      const agg = await fetchEducatorBarrierRecognitionAggregates(moduleId);
      if (agg) {
        setEducatorBarrierPollAgg(agg);
        setEducatorBarrierPollStatus('ready');
      }
    } catch (_) {
      // keep prior aggregates if any
    } finally {
      setEducatorBarrierPollSaving(false);
    }
  };

  const goToBarriersCarouselSlide = (idx) => {
    setBarrierSlideOutIndex(null);
    setBarriersCarouselSlide(idx);
  };

  const openActionModal = (spotIndex) => {
    setRevealedActionSpots((prev) => {
      const next = prev.includes(spotIndex) ? prev : [...prev, spotIndex];
      const required = pickFlowModule(moduleId, ACTIONS_HOTSPOTS, EDUCATOR_ACTIONS_HOTSPOTS, COMMUNITY_ACTIONS_HOTSPOTS).map((spot) => spot.spotIndex);
      const allRevealed = required.every((id) => next.includes(id));

      // Module 6 should only be marked complete once all shown hotspots are clicked.
      if (allRevealed) {
        setCompletedSyllabusSteps((done) => (done.includes('actions') ? done : [...done, 'actions']));
        if (firebaseUser?.uid) {
          void trackModuleStageProgress(
            firebaseUser.uid,
            moduleId,
            'actions',
            sectionsWithSteps.length,
            Math.max(1, sectionsWithSteps.findIndex((s) => s.id === 'actions') + 1),
            true
          ).finally(requestProgressRefresh);
        }
      }
      return next;
    });
    setActionModalIndex(spotIndex);
  };
  const closeActionModal = () => setActionModalIndex(null);

  const handleMetaReflexivitySurveySubmit = async () => {
    const momentTypes = normalizeMetaReflexivityMomentTypesForSubmit(metaReflexivityMomentTypes);
    const otherText = momentTypes.includes('other')
      ? String(metaReflexivityMomentOther || '').trim() || null
      : null;
    setMetaReflexivityUserChoice({ momentTypes, otherText });
    try {
      const payload = { momentTypes, otherText, at: new Date().toISOString() };
      const stored = JSON.parse(localStorage.getItem(META_REFLEXIVITY_MOMENT_STORAGE_KEY) || '[]');
      stored.push(payload);
      localStorage.setItem(META_REFLEXIVITY_MOMENT_STORAGE_KEY, JSON.stringify(stored));
    } catch (_) {}
    await writeMetaReflexivityResponse({ momentTypes, otherText, moduleId });
    setWorkplaceM5InputStep(2);
    setMetaReflexivityCelebrate(true);
    setTimeout(() => setMetaReflexivityCelebrate(false), 2200);
    setMetaReflexivityMomentTypes([]);
    setMetaReflexivityMomentOther('');
  };

  const toggleMetaReflexivityMomentType = (value) => {
    setMetaReflexivityMomentTypes((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  };

  /** Always open a section from its first slide/state. */
  const resetSectionInteractiveState = (sectionId) => {
    switch (sectionId) {
      case 'overview':
        setWorkplaceM1Slide(0);
        setWorkplaceM1RevealCount(1);
        break;
      case 'sara-story':
      case 'aman-story':
        setParentStorySlide(0);
        setParentStoryRevealCount(1);
        setParentStoryVideoModalOpen(false);
        setParentStoryYoutubePlayingModal(false);
        break;
      case 'anika-story':
        if (isParentModule) {
          setParentStorySlide(0);
          setParentStoryRevealCount(1);
          setParentStoryVideoModalOpen(false);
          setParentStoryYoutubePlayingModal(false);
          break;
        }
        setWorkplaceM2Slide(0);
        setWorkplaceM2RevealCount(1);
        setWorkplaceM2VideoModalOpen(false);
        setWorkplaceM2YoutubePlayingModal(false);
        setWorkplaceM3RevealCount(1);
        setWorkplaceM3FigureModalOpen(false);
        break;
      case 'barriers':
        setBarriersCarouselSlide(0);
        setBarriersLearningSlide(0);
        setBarrierSlideOutIndex(null);
        setBarriersGuideRevealCount(1);
        if (isFiveBarrierModule) {
          setEducatorBarrierPollSelection([]);
          setEducatorBarrierPollSubmitted(false);
          setEducatorBarrierPollAgg(null);
          setEducatorBarrierPollStatus('idle');
        }
        break;
      case 'meta-reflexivity':
        if (isStudyFlowModule) {
          setWorkplaceM5Slide(0);
          setWorkplaceM5RevealCount(1);
          setWorkplaceM5InputStep(0);
          setWorkplaceM5Slide0VideoPlaying(false);
          setEducatorMetaPrivateNote('');
        }
        if (moduleId === 'intro-module') {
          setIntroSlideIndex(0);
          setIntroRevealCount(1);
          setIntroVideoModalOpen(false);
        }
        break;
      case 'meta-reflexive-manager':
        setWorkplaceM6Slide(0);
        setWorkplaceM6RevealCount(1);
        setWorkplaceM6VideoModalOpen(false);
        setWorkplaceM6YoutubePlayingModal(false);
        break;
      case 'actions':
        setWorkplaceM7Slide(0);
        setWorkplaceM7RevealCount(1);
        setWorkplaceM7InputReady(false);
        setWorkplaceM7Slide0VideoPlaying(false);
        setActionModalIndex(null);
        break;
      case 'workplace-survey':
      case 'educator-survey':
      case 'community-survey':
      case 'parent-survey': {
        const progDoc = moduleProgress?.find((p) => p.moduleId === moduleId);
        if (isModuleProgressComplete(progDoc)) {
          setWorkplaceSurveyStep(getWorkplaceSurveyTotalSteps(moduleId) - 1);
          setSurveySubmitted(true);
        } else {
          setSurveySubmitted(false);
          setWorkplaceSurveyStep(0);
          setSurveyResponses(createEmptySurveyResponses());
          setParticipantReviewSubmitted(false);
          setParticipantReviewModalOpen(false);
        }
        break;
      }
      case 'start-here':
      case 'learning-goals':
      case 'carry-forward':
        if (moduleId === 'intro-module') {
          setIntroSlideIndex(0);
          setIntroRevealCount(1);
          setIntroVideoModalOpen(false);
        }
        break;
      default:
        break;
    }
  };

  const handleStepSelect = (sectionId) => {
    resetSectionInteractiveState(sectionId);
    setActiveSection(sectionId);
    setContentKey(k => k + 1);
    if (firebaseUser?.uid) {
      const stagePosition = Math.max(1, sectionsWithSteps.findIndex((s) => s.id === sectionId) + 1);
      void trackModuleStageProgress(
        firebaseUser.uid,
        moduleId,
        sectionId,
        moduleContent.sections.length,
        stagePosition,
        false
      ).finally(requestProgressRefresh);
    }
  };

  const goToNextSectionFrom = (sectionId) => {
    const currentIndex = sectionsWithSteps.findIndex((s) => s.id === sectionId);
    const nextSection = currentIndex >= 0 ? sectionsWithSteps[currentIndex + 1] : null;
    if (!nextSection) return false;
    if (isStudyFlowModule || moduleId === 'intro-module') {
      setCompletedSyllabusSteps((prev) => {
        let next = prev.includes(sectionId) ? prev : [...prev, sectionId];
        next = next.includes(nextSection.id) ? next : [...next, nextSection.id];
        return next;
      });
    }
    resetSectionInteractiveState(nextSection.id);
    if (viewMode === 'overview') {
      setSyllabusModalSectionId(nextSection.id);
    } else {
      handleStepSelect(nextSection.id);
    }
    return true;
  };

  const hasPrevSection = (sectionId) => {
    const idx = sectionsWithSteps.findIndex((s) => s.id === sectionId);
    return idx > 0;
  };

  const goToPrevSectionFrom = (sectionId) => {
    const currentIndex = sectionsWithSteps.findIndex((s) => s.id === sectionId);
    const prevSection = currentIndex > 0 ? sectionsWithSteps[currentIndex - 1] : null;
    if (!prevSection) return false;
    resetSectionInteractiveState(prevSection.id);
    if (viewMode === 'overview') {
      setSyllabusModalSectionId(prevSection.id);
      setContentKey((k) => k + 1);
      setTimeout(() => scrollOverviewStepSurfacesToTop(), 0);
    } else {
      handleStepSelect(prevSection.id);
    }
    return true;
  };

  const markCurrentModuleCompleted = () => {
    if (!firebaseUser?.uid || !moduleId) return Promise.resolve();
    const stageIds = sectionsWithSteps.map((s) => s.id);
    const finalId = stageIds[stageIds.length - 1] || sectionsWithSteps[0]?.id;
    return markModuleProgressCompleted(firebaseUser.uid, moduleId, {
      finalStageId: finalId,
      totalStages: sectionsWithSteps.length,
      allStageIds: stageIds,
    }).finally(requestProgressRefresh);
  };

  /** Whether a syllabus step is unlocked: next parts stay locked until previous is completed (intro + workplace). */
  const isSyllabusStepUnlocked = (section, index) => {
    if ((!isStudyFlowModule && moduleId !== 'intro-module') || !sectionsWithSteps.length) return true;
    if (index === 0) return true;
    const prevSection = sectionsWithSteps[index - 1];
    const prevDone = completedSyllabusSteps.includes(prevSection.id)
      || (prevSection.id === 'anika-story' && completedSyllabusSteps.includes('reflection'));
    return prevDone;
  };

  const handleSyllabusStepClick = (section, index) => {
    if (!isSyllabusStepUnlocked(section, index)) return;
    const reselectSameStep = syllabusModalSectionId === section.id;
    resetSectionInteractiveState(section.id);
    setSyllabusModalSectionId(section.id);
    setCompletedSyllabusSteps((prev) => (prev.includes(section.id) ? prev : [...prev, section.id]));
    if (firebaseUser?.uid) {
      const stagePosition = Math.max(1, index + 1);
      void trackModuleStageProgress(
        firebaseUser.uid,
        moduleId,
        section.id,
        sectionsWithSteps.length,
        stagePosition,
        !STAGES_NOT_AUTO_COMPLETE_ON_VISIT.has(section.id)
      ).finally(requestProgressRefresh);
    }
    if (reselectSameStep) {
      setTimeout(() => scrollOverviewStepSurfacesToTop(), 0);
    }
  };

  useLayoutEffect(() => {
    if (viewMode !== 'overview' || !syllabusModalSectionId) return;
    scrollOverviewStepSurfacesToTop();
  }, [viewMode, syllabusModalSectionId]);

  useLayoutEffect(() => {
    if (viewMode === 'overview') return;
    moduleMainScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [viewMode, activeSection, contentKey]);

  const stepLabels = useMemo(() => {
    if (moduleId === 'parent-module') {
      return ['Purpose & Goals', "Sara's Story", "Anika's Story", "Aman's Story", 'Meta-Reflexivity', 'Survey'];
    }
    if (moduleId === 'community-module') {
      return ['Purpose & Goals', "Aman's Story & Reflect", 'Five Barriers', 'Meta-Reflexivity', "Revisiting Aman's Story", 'Small Actions', 'Survey'];
    }
    if (moduleId === 'educator-module') {
      return ['Purpose & Goals', "Sara's Story & Reflect", 'Five Barriers', 'Meta-Reflexivity', "Revisiting Sara's Story", 'Small Actions', 'Survey'];
    }
    return ['Purpose & Goals', "Anika's Story & Reflect", 'Six Barriers', 'Meta-Reflexivity', "Revisiting Anika's Story", 'Small Actions', 'Survey'];
  }, [moduleId]);

  const sectionsWithSteps = useMemo(
    () =>
      moduleContent.sections.map((section, index) => ({
        ...section,
        stepNumber: index + 1,
        shortLabel:
          moduleContent.sections.length === 1 || moduleId === 'intro-module'
            ? section.title
            : (stepLabels[index] || section.title),
        color: STEP_COLORS[index] || STEP_COLORS[0],
      })),
    [moduleContent, moduleId, stepLabels]
  );
  const currentModuleProgress = useMemo(
    () => moduleProgress?.find((p) => p.moduleId === moduleId) || null,
    [moduleProgress, moduleId]
  );

  /** Completed / submitted survey: always land on certificate screen, not slide 1. */
  useEffect(() => {
    if (!isStudyFlowModule || !isEndSurveySectionVisible) return;
    if (!isModuleCompleted && !surveySubmitted) return;
    setSurveySubmitted(true);
    setWorkplaceSurveyStep(getWorkplaceSurveyTotalSteps(moduleId) - 1);
  }, [isStudyFlowModule, isEndSurveySectionVisible, isModuleCompleted, surveySubmitted, moduleId]);

  /** Survey done but Firestore still in-progress at 100% (legacy writes) → mark completed for hub + profile. */
  useEffect(() => {
    if (!firebaseUser?.uid || !isStudyFlowModule || !surveySubmitted) return;
    const prog = currentModuleProgress;
    if (prog?.status === 'completed' || prog?.isCompleted) return;
    const pct = Number(prog?.progressPercent) || 0;
    if (pct < 100) return;
    void markCurrentModuleCompleted();
  }, [
    firebaseUser?.uid,
    isStudyFlowModule,
    surveySubmitted,
    currentModuleProgress?.status,
    currentModuleProgress?.isCompleted,
    currentModuleProgress?.progressPercent,
    moduleId,
    sectionsWithSteps,
  ]);

  /**
   * Route stays on the same StudyComponent when only :moduleId changes (e.g. intro → workplace).
   * Clear overview selection and completion hints from the previous module so we don't render
   * with a stale syllabusModalSectionId (e.g. carry-forward) that has no workplace content → blank panel.
   */
  useEffect(() => {
    setSyllabusModalSectionId(null);
    setActiveSection('overview');
    setViewMode('overview');
    setCompletedSyllabusSteps([]);
  }, [moduleId]);

  useEffect(() => {
    if (isStudyFlowModule) return;
    const lastStageId = currentModuleProgress?.currentStageId;
    if (!lastStageId) return;
    if (syllabusModalSectionId) return;
    const normalizedStageId = lastStageId;
    const stageIndex = sectionsWithSteps.findIndex((s) => s.id === normalizedStageId);
    if (stageIndex < 0) return;
    setSyllabusModalSectionId(normalizedStageId);
    setCompletedSyllabusSteps(
      sectionsWithSteps.slice(0, stageIndex + 1).map((s) => s.id)
    );
  }, [currentModuleProgress?.currentStageId, moduleId, sectionsWithSteps, syllabusModalSectionId]);

  /**
   * Workplace: returning learners resume their last step; first visit skips the duplicate
   * intro video (same as Introduction Module) and opens Part 1 in the overview panel.
   */
  useLayoutEffect(() => {
    if (authLoading || !isStudyFlowModule || isComingSoonModule) return;
    if (syllabusModalSectionId) return;

    const lastStageId = currentModuleProgress?.currentStageId;
    if (lastStageId) {
      const normalizedStageId = lastStageId === 'reflection' ? 'anika-story' : lastStageId;
      const stageIndex = sectionsWithSteps.findIndex((s) => s.id === normalizedStageId);
      if (stageIndex < 0) return;
      setSyllabusModalSectionId(normalizedStageId);
      setCompletedSyllabusSteps(
        sectionsWithSteps.slice(0, stageIndex + 1).map((s) => s.id)
      );
      const moduleDone =
        currentModuleProgress?.status === 'completed' || currentModuleProgress?.isCompleted;
      if (moduleDone && SURVEY_STAGE_IDS.has(normalizedStageId)) {
        setSurveySubmitted(true);
        setWorkplaceSurveyStep(getWorkplaceSurveyTotalSteps(moduleId) - 1);
      }
      return;
    }

    const first = sectionsWithSteps[0];
    if (!first) return;
    resetSectionInteractiveState(first.id);
    setSyllabusModalSectionId(first.id);
    setCompletedSyllabusSteps((prev) => (prev.includes(first.id) ? prev : [...prev, first.id]));
    if (firebaseUser?.uid) {
      void trackModuleStageProgress(
        firebaseUser.uid,
        moduleId,
        first.id,
        sectionsWithSteps.length,
        1,
        !STAGES_NOT_AUTO_COMPLETE_ON_VISIT.has(first.id)
      ).finally(requestProgressRefresh);
    }
  }, [
    authLoading,
    moduleId,
    isComingSoonModule,
    syllabusModalSectionId,
    currentModuleProgress?.currentStageId,
    sectionsWithSteps,
    firebaseUser?.uid,
  ]);

  useEffect(() => {
    if (!firebaseUser?.uid || !moduleId || isComingSoonModule) return;
    void ensureModuleEnrollment(firebaseUser.uid, moduleId, sectionsWithSteps.length).finally(requestProgressRefresh);
  }, [firebaseUser?.uid, moduleId, sectionsWithSteps.length, isComingSoonModule]);

  useEffect(() => () => {
    if (progressRefreshTimerRef.current) {
      clearTimeout(progressRefreshTimerRef.current);
      progressRefreshTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (activeSection !== 'overview') {
      setWorkplaceM1Slide(0);
      setWorkplaceM1RevealCount(1);
    }
  }, [moduleId, activeSection]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (!isCaseStudySectionVisible) {
      setWorkplaceM2Slide(0);
      setWorkplaceM2RevealCount(1);
      setWorkplaceM2VideoModalOpen(false);
      setWorkplaceM2YoutubePlayingModal(false);
      setWorkplaceM3RevealCount(1);
      setWorkplaceM3FigureModalOpen(false);
    }
  }, [moduleId, isCaseStudySectionVisible]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (activeSection !== 'barriers') {
      setBarriersGuideRevealCount(1);
      setBarriersLearningSlide(0);
      setBarriersExploreModalOpen(false);
    }
  }, [moduleId, activeSection]);

  useEffect(() => {
    if (!isStudyFlowModule || isEducatorModule) return;
    if (activeSection !== 'meta-reflexivity') return;
    setWorkplaceM5Slide0VideoPlaying(false);
  }, [moduleId, isEducatorModule, activeSection, workplaceM5Slide, syllabusModalSectionId]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (!isMetaReflexivitySectionVisible) {
      setWorkplaceM5Slide(0);
      setWorkplaceM5RevealCount(1);
      setWorkplaceM5InputStep(0);
      setMetaReflexivityMomentTypes([]);
      setMetaReflexivityMomentOther('');
      setMetaReflexivityPoll(null);
      setMetaReflexivityPollStatus('idle');
      setMetaReflexivityUserChoice(null);
    }
  }, [moduleId, isMetaReflexivitySectionVisible]);

  useEffect(() => {
    if (moduleId !== 'workplace-module' || !isMetaReflexivitySectionVisible || workplaceM5Slide !== 1) return;
    try {
      const stored = JSON.parse(localStorage.getItem(META_REFLEXIVITY_MOMENT_STORAGE_KEY) || '[]');
      if (!Array.isArray(stored) || stored.length === 0) return;
      const last = stored[stored.length - 1];
      const restored = storedPayloadToUserChoice(last);
      if (restored) {
        setWorkplaceM5InputStep(2);
        setMetaReflexivityUserChoice(restored);
      }
    } catch (_) {}
  }, [moduleId, isMetaReflexivitySectionVisible, workplaceM5Slide]);

  useEffect(() => {
    if (moduleId !== 'workplace-module' || !isMetaReflexivitySectionVisible || workplaceM5Slide !== 1 || workplaceM5InputStep !== 2) {
      return;
    }
    let cancelled = false;
    setMetaReflexivityPollStatus('loading');
    (async () => {
      const resolveChoiceForFallback = () => {
        if (metaReflexivityUserChoice?.momentTypes?.length) return metaReflexivityUserChoice;
        try {
          const stored = JSON.parse(localStorage.getItem(META_REFLEXIVITY_MOMENT_STORAGE_KEY) || '[]');
          const last = Array.isArray(stored) ? stored[stored.length - 1] : null;
          return storedPayloadToUserChoice(last);
        } catch (_) {}
        return null;
      };

      const agg = await fetchMetaReflexivityPollAggregates('workplace-module');
      if (cancelled) return;
      if (agg) {
        setMetaReflexivityPoll({ ...agg, source: 'firebase' });
        setMetaReflexivityPollStatus('ready');
        return;
      }

      const choice = resolveChoiceForFallback();
      if (choice?.momentTypes?.length) {
        const counts = {
          hiring: 0,
          promotion: 0,
          mentorship: 0,
          sponsorship: 0,
          other: 0,
        };
        choice.momentTypes.forEach((t) => {
          const key = META_REFLEXIVITY_MOMENT_OPTIONS.some((o) => o.value === t) ? t : 'other';
          counts[key] = (counts[key] || 0) + 1;
        });
        setMetaReflexivityPoll({ counts, total: 1, source: 'local-only' });
        setMetaReflexivityPollStatus('ready');
        return;
      }

      setMetaReflexivityPoll(null);
      setMetaReflexivityPollStatus('error');
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, isMetaReflexivitySectionVisible, workplaceM5Slide, workplaceM5InputStep, metaReflexivityUserChoice]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (!isMetaReflexiveManagerSectionVisible) {
      setWorkplaceM6Slide(0);
      setWorkplaceM6RevealCount(1);
      setWorkplaceM6VideoModalOpen(false);
      setWorkplaceM6YoutubePlayingModal(false);
    }
  }, [moduleId, isMetaReflexiveManagerSectionVisible]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (!isActionsSectionVisible) {
      setWorkplaceM7Slide(0);
      setWorkplaceM7RevealCount(1);
      setWorkplaceM7InputReady(false);
      setWorkplaceM7Slide0VideoPlaying(false);
      setActionModalIndex(null);
    }
  }, [moduleId, isActionsSectionVisible]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (!isActionsSectionVisible) return;
    setWorkplaceM7Slide0VideoPlaying(false);
  }, [moduleId, isActionsSectionVisible, workplaceM7Slide, syllabusModalSectionId]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    if (isEndSurveySectionVisible) return;
    setWorkplaceSurveyStep(0);
  }, [moduleId, isEndSurveySectionVisible]);

  useEffect(() => {
    if (!isStudyFlowModule || activeSection !== 'overview') return;
    setWorkplaceM1RevealCount(1);
    setWorkplaceM1VideoPlaying(false);
  }, [moduleId, activeSection, workplaceM1Slide, syllabusModalSectionId]);

  useEffect(() => {
    if (!isStudyFlowModule) return;
    setWorkplaceM1VideoPlaying(false);
  }, [moduleId, isStudyFlowModule, viewMode]);

  useEffect(() => {
    if (!isStudyFlowModule || activeSection !== 'overview') return;
    const m1Slides = isParentModule
      ? PARENT_M1_SLIDES
      : pickFlowModule(moduleId, WORKPLACE_M1_SLIDES, EDUCATOR_M1_SLIDES, COMMUNITY_M1_SLIDES);
    const currentSlide = m1Slides[workplaceM1Slide];
    if (currentSlide?.youtubeVideoId) return;
    let revealTarget = 0;
    if (isParentModule && currentSlide?.goalLines) {
      revealTarget =
        currentSlide.goalLines.length
        + (currentSlide.bodyLines?.length || 0)
        + (currentSlide.closingLine ? 1 : 0);
    } else {
      if (!currentSlide?.lines?.length) return;
      revealTarget = currentSlide.lines.length;
      if (workplaceM1Slide === 0 && revealTarget > 0) revealTarget -= 1;
      if (workplaceM1Slide === 1 && revealTarget > 0) revealTarget -= 1;
    }
    if (!revealTarget || workplaceM1RevealCount >= revealTarget) return;

    const timer = setTimeout(() => {
      setWorkplaceM1RevealCount((c) => Math.min(revealTarget, c + 1));
    }, 550);

    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isParentModule, activeSection, workplaceM1Slide, workplaceM1RevealCount]);

  useEffect(() => {
    if (moduleId !== 'intro-module') {
      setIntroSlideIndex(0);
      setIntroRevealCount(1);
      setIntroVideoModalOpen(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (moduleId !== 'intro-module') return;
    const sid = viewMode === 'overview' ? syllabusModalSectionId : activeSection;
    if (!sid || !INTRO_SECTION_SLIDES[sid]) return;
    setIntroSlideIndex(0);
    setIntroRevealCount(1);
    setIntroVideoModalOpen(false);
  }, [moduleId, viewMode, syllabusModalSectionId, activeSection]);

  useEffect(() => {
    if (moduleId !== 'intro-module') return;
    const sid = viewMode === 'overview' ? syllabusModalSectionId : activeSection;
    if (!sid || !INTRO_SECTION_SLIDES[sid]) return;
    const slides = INTRO_SECTION_SLIDES[sid];
    const slide = slides[introSlideIndex];
    if (!slide?.lines?.length) return;
    const revealTarget = slide.lines.length;
    if (introRevealCount >= revealTarget) return;
    const timer = setTimeout(() => {
      setIntroRevealCount((c) => Math.min(revealTarget, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [moduleId, viewMode, syllabusModalSectionId, activeSection, introSlideIndex, introRevealCount]);

  /** Intro: at final part with full progress, mark module completed for profile certificate. */
  useEffect(() => {
    if (moduleId !== 'intro-module' || !firebaseUser?.uid) return;
    const prog = moduleProgress?.find((p) => p.moduleId === 'intro-module');
    if (prog?.status === 'completed' || prog?.isCompleted) return;
    const activeSid = viewMode === 'overview' ? syllabusModalSectionId : activeSection;
    if (activeSid !== INTRO_MODULE_FINAL_SECTION_ID) return;
    const slides = INTRO_SECTION_SLIDES[INTRO_MODULE_FINAL_SECTION_ID] || [];
    const lastIdx = Math.max(0, slides.length - 1);
    if (introSlideIndex < lastIdx) return;
    const lastLines = slides[lastIdx]?.lines || [];
    if (lastLines.length > 0 && introRevealCount < lastLines.length) return;
    const pct = Number(prog?.progressPercent) || 0;
    if (pct < 100) return;
    void markModuleProgressCompleted(firebaseUser.uid, 'intro-module', {
      finalStageId: INTRO_MODULE_FINAL_SECTION_ID,
      totalStages: sectionsWithSteps.length,
      allStageIds: sectionsWithSteps.map((s) => s.id),
    }).finally(requestProgressRefresh);
  }, [
    moduleId,
    firebaseUser?.uid,
    moduleProgress,
    viewMode,
    syllabusModalSectionId,
    activeSection,
    introSlideIndex,
    introRevealCount,
    sectionsWithSteps,
  ]);

  useEffect(() => {
    if (!isStudyFlowModule || !isCaseStudySectionVisible) return;
    const m2slides = pickFlowModule(moduleId, WORKPLACE_M2_SLIDES, EDUCATOR_M2_SLIDES, COMMUNITY_M2_SLIDES);
    const current = m2slides[workplaceM2Slide];
    if (!current) return;
    const revealTotal = getM2SlideRevealTotal(current);
    if (!revealTotal) return;
    if (workplaceM2RevealCount >= revealTotal) return;
    const timer = setTimeout(() => {
      setWorkplaceM2RevealCount((c) => Math.min(revealTotal, c + 1));
    }, 500);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isCaseStudySectionVisible, workplaceM2Slide, workplaceM2RevealCount]);

  useEffect(() => {
    if (!isStudyFlowModule || !isCaseStudySectionVisible) return;
    if (workplaceM2Slide !== 1) return;
    const m3slides = pickFlowModule(moduleId, WORKPLACE_M3_REFLECTION_SLIDES, EDUCATOR_M3_REFLECTION_SLIDES, COMMUNITY_M3_REFLECTION_SLIDES);
    if (workplaceM3RevealCount >= m3slides.length) return;
    const timer = setTimeout(() => {
      setWorkplaceM3RevealCount((c) => Math.min(m3slides.length, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isCaseStudySectionVisible, workplaceM2Slide, workplaceM3RevealCount]);

  useEffect(() => {
    if (!isParentModule || !activeParentStorySectionId) return;
    if (parentStorySlide !== 0) return;
    const config = PARENT_STORY_SECTIONS[activeParentStorySectionId];
    const introLines = config?.introSlide?.lines || [];
    const total = introLines.length + (config?.introSlide?.closingLine ? 1 : 0);
    if (!total || parentStoryRevealCount >= total) return;
    const timer = setTimeout(() => {
      setParentStoryRevealCount((c) => Math.min(total, c + 1));
    }, 500);
    return () => clearTimeout(timer);
  }, [isParentModule, activeParentStorySectionId, parentStorySlide, parentStoryRevealCount]);

  useEffect(() => {
    if (!isParentModule) return;
    const pauseFlags = { sara: false, anika: false, aman: false };
    const actFlags = { sara: false, anika: false, aman: false };
    const pauseAnswers = { sara: '', anika: '', aman: '' };
    const actAnswers = { sara: '', anika: '', aman: '' };
    PARENT_STORY_SECTION_IDS.forEach((sectionId) => {
      const storyKey = PARENT_STORY_SECTIONS[sectionId]?.storyKey;
      if (!storyKey) return;
      try {
        const pauseStored = JSON.parse(localStorage.getItem(parentPauseStorageKey(storyKey)) || '[]');
        if (pauseStored.length > 0) {
          pauseFlags[storyKey] = true;
          pauseAnswers[storyKey] = String(pauseStored[pauseStored.length - 1]?.text || '').trim();
        }
        const actStored = JSON.parse(localStorage.getItem(parentActStorageKey(storyKey)) || '[]');
        if (actStored.length > 0) {
          actFlags[storyKey] = true;
          actAnswers[storyKey] = String(actStored[actStored.length - 1]?.text || '').trim();
        }
      } catch (_) {
        // no-op
      }
    });
    setParentPauseSubmitted(pauseFlags);
    setParentActSubmitted(actFlags);
    setParentPauseAnswers((prev) => ({
      sara: prev.sara || pauseAnswers.sara,
      anika: prev.anika || pauseAnswers.anika,
      aman: prev.aman || pauseAnswers.aman,
    }));
    setParentActAnswers((prev) => ({
      sara: prev.sara || actAnswers.sara,
      anika: prev.anika || actAnswers.anika,
      aman: prev.aman || actAnswers.aman,
    }));

    let cancelled = false;
    void (async () => {
      const fromDb = await fetchLatestParentStoryResponsesForUser(moduleId);
      if (cancelled || !fromDb) return;
      setParentPauseAnswers((prev) => ({
        sara: prev.sara || fromDb.pause.sara || '',
        anika: prev.anika || fromDb.pause.anika || '',
        aman: prev.aman || fromDb.pause.aman || '',
      }));
      setParentActAnswers((prev) => ({
        sara: prev.sara || fromDb.act.sara || '',
        anika: prev.anika || fromDb.act.anika || '',
        aman: prev.aman || fromDb.act.aman || '',
      }));
      setParentPauseSubmitted((prev) => ({
        sara: prev.sara || Boolean(fromDb.pause.sara),
        anika: prev.anika || Boolean(fromDb.pause.anika),
        aman: prev.aman || Boolean(fromDb.pause.aman),
      }));
      setParentActSubmitted((prev) => ({
        sara: prev.sara || Boolean(fromDb.act.sara),
        anika: prev.anika || Boolean(fromDb.act.anika),
        aman: prev.aman || Boolean(fromDb.act.aman),
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, isParentModule, firebaseUser?.uid]);

  useEffect(() => {
    if (!isParentModule || parentStorySlide !== 1 || !activeParentStorySectionId) {
      setParentWordCloudCounts(null);
      return undefined;
    }
    const storyKey = PARENT_STORY_SECTIONS[activeParentStorySectionId]?.storyKey;
    if (!storyKey || !parentPauseSubmitted[storyKey]) {
      setParentWordCloudCounts(null);
      return undefined;
    }
    if (!firebaseUser?.uid) {
      setParentWordCloudCounts(null);
      return undefined;
    }
    const requestId = parentWordCloudFetchRef.current + 1;
    parentWordCloudFetchRef.current = requestId;
    let cancelled = false;
    void (async () => {
      const result = await fetchParentPauseWordCloudAggregates(storyKey, moduleId);
      if (cancelled || parentWordCloudFetchRef.current !== requestId) return;
      setParentWordCloudCounts(result?.counts ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    moduleId,
    isParentModule,
    parentStorySlide,
    activeParentStorySectionId,
    parentPauseSubmitted,
    firebaseUser?.uid,
  ]);

  useLayoutEffect(() => {
    if (!isParentModule || parentStorySlide !== 1 || !activeParentStorySectionId) return undefined;
    const storyKey = PARENT_STORY_SECTIONS[activeParentStorySectionId]?.storyKey;
    if (!storyKey || !parentPauseSubmitted[storyKey]) return undefined;
    const node = parentWordCloudContainerRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const width = Math.max(280, Math.floor(node.clientWidth));
      const height = Math.max(180, Math.floor(node.clientHeight));
      setParentWordCloudSize((prev) =>
        prev[0] === width && prev[1] === height ? prev : [width, height]
      );
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    isParentModule,
    parentStorySlide,
    activeParentStorySectionId,
    parentPauseSubmitted,
    parentWordCloudCounts,
    viewMode,
    syllabusModalSectionId,
  ]);

  useEffect(() => {
    if (!isStudyFlowModule || activeSection !== 'barriers') return;
    const guided = pickFlowModule(moduleId, WORKPLACE_M4_GUIDED_STEPS, EDUCATOR_M4_GUIDED_STEPS, COMMUNITY_M4_GUIDED_STEPS);
    if (barriersGuideRevealCount >= guided.length) return;
    const timer = setTimeout(() => {
      setBarriersGuideRevealCount((c) => Math.min(guided.length, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, activeSection, barriersGuideRevealCount]);

  useEffect(() => {
    if (!isStudyFlowModule || !isMetaReflexivitySectionVisible || isParentModule) return;
    const m5slides = pickFlowModule(moduleId, WORKPLACE_M5_SLIDES, EDUCATOR_M5_SLIDES, COMMUNITY_M5_SLIDES);
    const current = m5slides[workplaceM5Slide];
    if (!current || !current.lines.length) return;
    const revealTarget =
      !isFiveBarrierModule && workplaceM5Slide === 0 ? Math.min(2, current.lines.length) : current.lines.length;
    if (workplaceM5RevealCount >= revealTarget) return;
    const timer = setTimeout(() => {
      setWorkplaceM5RevealCount((c) => Math.min(revealTarget, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isMetaReflexivitySectionVisible, workplaceM5Slide, workplaceM5RevealCount]);

  useEffect(() => {
    if (!isParentModule || !isMetaReflexivitySectionVisible) return;
    const current = PARENT_M5_SLIDES[workplaceM5Slide];
    if (!current?.lines?.length) return;
    if (workplaceM5RevealCount >= current.lines.length) return;
    const timer = setTimeout(() => {
      setWorkplaceM5RevealCount((c) => Math.min(current.lines.length, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [isParentModule, isMetaReflexivitySectionVisible, workplaceM5Slide, workplaceM5RevealCount]);

  useEffect(() => {
    if (!activeParentStorySectionId) return;
    setParentStorySlide(0);
    setParentStoryRevealCount(1);
    setParentResearchMoreChart(null);
  }, [activeParentStorySectionId]);

  useEffect(() => {
    if (!isFiveBarrierModule || !isMetaReflexivitySectionVisible || workplaceM5Slide !== 1 || !metaPrivateNoteKey) return;
    try {
      const v = localStorage.getItem(metaPrivateNoteKey);
      if (v != null) setEducatorMetaPrivateNote(String(v));
    } catch (_) {}
  }, [isFiveBarrierModule, isMetaReflexivitySectionVisible, workplaceM5Slide, metaPrivateNoteKey]);

  useEffect(() => {
    if (!isFiveBarrierModule || activeSection !== 'barriers') return;
    let cancelled = false;
    setEducatorBarrierPollStatus('loading');
    (async () => {
      const agg = await fetchEducatorBarrierRecognitionAggregates(moduleId);
      if (cancelled) return;
      if (agg) {
        setEducatorBarrierPollAgg(agg);
        setEducatorBarrierPollStatus('ready');
      } else {
        setEducatorBarrierPollStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isFiveBarrierModule, activeSection, moduleId]);

  useEffect(() => {
    if (!isStudyFlowModule || !isMetaReflexiveManagerSectionVisible) return;
    const m6slides = pickFlowModule(moduleId, WORKPLACE_M6_SLIDES, EDUCATOR_M6_SLIDES, COMMUNITY_M6_SLIDES);
    const current = m6slides[workplaceM6Slide];
    const revealTarget =
      (current?.linesBeforeTitle?.length || 0)
      + (current?.lines?.length || 0)
      + (current?.stages?.length || 0)
      + (current?.postStagesLines?.length || 0)
      + (current?.closingLines?.length || 0);
    if (!current || !revealTarget) return;
    if (workplaceM6RevealCount >= revealTarget) return;
    const timer = setTimeout(() => {
      setWorkplaceM6RevealCount((c) => Math.min(revealTarget, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isMetaReflexiveManagerSectionVisible, workplaceM6Slide, workplaceM6RevealCount]);

  useEffect(() => {
    if (!isStudyFlowModule || !isActionsSectionVisible) return;
    const m7slides = pickFlowModule(moduleId, WORKPLACE_M7_SLIDES, EDUCATOR_M7_SLIDES, COMMUNITY_M7_SLIDES);
    const current = m7slides[workplaceM7Slide];
    if (current?.youtubeVideoId) return;
    const revealTarget = current?.lines?.length || 0;
    if (!current || !revealTarget) return;
    if (workplaceM7RevealCount >= revealTarget) return;
    const timer = setTimeout(() => {
      setWorkplaceM7RevealCount((c) => Math.min(revealTarget, c + 1));
    }, 520);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isActionsSectionVisible, workplaceM7Slide, workplaceM7RevealCount]);

  useEffect(() => {
    if (!isStudyFlowModule || !isActionsSectionVisible) return;
    const m7slides = pickFlowModule(moduleId, WORKPLACE_M7_SLIDES, EDUCATOR_M7_SLIDES, COMMUNITY_M7_SLIDES);
    const { shift: m7ShiftSlideIndex } = getM7SlideIndices(m7slides);
    if (workplaceM7Slide !== m7ShiftSlideIndex) {
      setWorkplaceM7InputReady(false);
      return;
    }
    const shiftSlide = m7slides[m7ShiftSlideIndex];
    const inputRevealTarget = Math.min(2, shiftSlide?.lines?.length || 0);
    if (workplaceM7RevealCount < inputRevealTarget) return;
    const timer = setTimeout(() => setWorkplaceM7InputReady(true), 380);
    return () => clearTimeout(timer);
  }, [moduleId, isEducatorModule, isActionsSectionVisible, workplaceM7Slide, workplaceM7RevealCount]);

  const handleIntroVideoPosterClick = () => {
    // Always open Module 1 of the current module directly.
    const first = sectionsWithSteps[0];
    if (first) handleSyllabusStepClick(first, 0);
  };

  const currentSection = moduleContent.sections.find(s => s.id === activeSection);

  const reflectionWordCloudOptions = useMemo(() => ({
    colors: REFLECTION_WORD_CLOUD_COLORS,
    fontSizes: [14, 42],
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontStyle: 'normal',
    fontWeight: '600',
    rotations: 0,
    padding: 2,
    enableTooltip: false,
    deterministic: true,
    randomSeed: 'reflection-cloud',
  }), []);

  const reflectionWordCloudContainerRef = useRef(null);
  const [reflectionWordCloudSize, setReflectionWordCloudSize] = useState([640, 360]);
  const [reflectionWordCloudCounts, setReflectionWordCloudCounts] = useState(null);
  const reflectionWordCloudFetchRef = useRef(0);

  useEffect(() => {
    if (!isStudyFlowModule || workplaceM2Slide !== 2 || !reflectionSubmitted) return undefined;
    if (!firebaseUser?.uid) {
      setReflectionWordCloudCounts(null);
      return undefined;
    }
    const requestId = reflectionWordCloudFetchRef.current + 1;
    reflectionWordCloudFetchRef.current = requestId;
    let cancelled = false;
    void (async () => {
      const result = await fetchReflectionWordCloudAggregates(moduleId);
      if (cancelled || reflectionWordCloudFetchRef.current !== requestId) return;
      setReflectionWordCloudCounts(result?.counts ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, isStudyFlowModule, workplaceM2Slide, reflectionSubmitted, firebaseUser?.uid]);

  useLayoutEffect(() => {
    if (workplaceM2Slide !== 2 || !reflectionSubmitted) return undefined;
    const node = reflectionWordCloudContainerRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const width = Math.max(280, Math.floor(node.clientWidth));
      const height = Math.max(200, Math.floor(node.clientHeight));
      setReflectionWordCloudSize((prev) =>
        prev[0] === width && prev[1] === height ? prev : [width, height]
      );
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [workplaceM2Slide, reflectionSubmitted, viewMode, syllabusModalSectionId, activeSection]);


  /** Seed vocabulary + all learners' Firebase submissions for this module. */
  const reflectionWordCloudWords = useMemo(() => {
    const countByWord = new Map();
    REFLECTION_WORD_CLOUD_SEED.forEach(({ text, value }) => {
      const key = text.trim().toLowerCase();
      if (key) countByWord.set(key, (countByWord.get(key) || 0) + value);
    });
    if (reflectionWordCloudCounts) {
      Object.entries(reflectionWordCloudCounts).forEach(([text, count]) => {
        const key = text.trim().toLowerCase();
        if (!key) return;
        countByWord.set(key, (countByWord.get(key) || 0) + count);
      });
    }
    return Array.from(countByWord.entries())
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value);
  }, [reflectionWordCloudCounts]);

  const parentWordCloudWords = useMemo(() => {
    const countByWord = new Map();
    if (parentWordCloudCounts) {
      Object.entries(parentWordCloudCounts).forEach(([text, count]) => {
        const key = text.trim().toLowerCase();
        if (!key) return;
        countByWord.set(key, (countByWord.get(key) || 0) + count);
      });
    }
    const activeKey = activeParentStorySectionId
      ? PARENT_STORY_SECTIONS[activeParentStorySectionId]?.storyKey
      : '';
    if (activeKey) {
      const ownWord = normalizeReflectionCloudWord(parentPauseAnswers[activeKey]);
      if (ownWord && !countByWord.has(ownWord)) {
        countByWord.set(ownWord, 1);
      }
    }
    return Array.from(countByWord.entries())
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value);
  }, [parentWordCloudCounts, activeParentStorySectionId, parentPauseAnswers]);

  const getLatestReflectionWord = () => {
    const fromInput = (reflectionAnswers.reflection1 || '').trim().split(/\s+/)[0];
    if (fromInput) return fromInput;
    const submissions = getReflectionSubmissions();
    const latest = submissions[submissions.length - 1]?.text || '';
    return latest.trim().split(/\s+/)[0] || '';
  };

  const getLatestStoredReflectionWord = () => {
    const submissions = getReflectionSubmissions();
    const latest = submissions[submissions.length - 1]?.text || '';
    return latest.trim().split(/\s+/)[0] || '';
  };

  const submitReflectionAndAdvance = () => {
    const latestStoredWord = getLatestStoredReflectionWord();
    const hasExistingReflection = reflectionSubmitted || Boolean(latestStoredWord);
    if (hasExistingReflection) {
      setWorkplaceM2Slide(2);
      return;
    }
    handleReflectionSubmit();
    setReflectionCelebrate(true);
    setTimeout(() => setReflectionCelebrate(false), 2200);
    setTimeout(() => setWorkplaceM2Slide(2), 220);
  };

  /** Former Module 3 (Pause & Reflect): slides 1+ inside Anika section; nav is `workplace-m2-nav` in `renderAnikaBlock`. */
  const renderReflectionInner = () => {
    const m3ReflectionSlides = pickFlowModule(moduleId, WORKPLACE_M3_REFLECTION_SLIDES, EDUCATOR_M3_REFLECTION_SLIDES, COMMUNITY_M3_REFLECTION_SLIDES);
    const researchSlide = pickFlowModule(moduleId, WORKPLACE_M3_RESEARCH_CONNECTION_SLIDE, EDUCATOR_M3_RESEARCH_CONNECTION_SLIDE, COMMUNITY_M3_RESEARCH_CONNECTION_SLIDE);
    const isPauseSlide = workplaceM2Slide === 1;
    const isWordCloudSlide = workplaceM2Slide === 2;
    const isExternalSignalSlide = isCommunityModule && workplaceM2Slide === 3;
    const isResearchSlide = isCommunityModule ? workplaceM2Slide === 4 : workplaceM2Slide === 3;
    const latestWord = getLatestReflectionWord();
    const latestStoredWord = getLatestStoredReflectionWord();
    const baseUrl = process.env.PUBLIC_URL || '';
    const stageFigureSrc = isCommunityModule
      ? `${baseUrl}/${COMMUNITY_BARRIER_ECOSYSTEM_FIGURE.src}`
      : `${baseUrl}/images/stage.png`;
    const stageFigureTitle = isCommunityModule
      ? COMMUNITY_BARRIER_ECOSYSTEM_FIGURE.title
      : isEducatorModule
        ? 'Figure: Each stage reflects a different system failure - not individual lack - and each failure corresponds to academic and program decisions you influence.'
        : 'Figure: Each stage reflects a different system failure - not individual lack - and each failure corresponds to decisions employers directly influence.';
    const stageFigureSideLabel = isCommunityModule
      ? 'Open the barrier ecosystem figure - tap to view full size.'
      : 'Open the pipeline figure - tap to view full size.';
    const hasExistingReflection = reflectionSubmitted || Boolean(latestStoredWord);
    const researchLinesAll = researchSlide.researchLines || [];
    const pathwayBullets = researchSlide.researchBullets;
    const hasPathwayBullets = Boolean(pathwayBullets?.length);
    const lastResearchLine = researchLinesAll[researchLinesAll.length - 1];
    const pathwayLead =
      hasPathwayBullets &&
      typeof lastResearchLine === 'string' &&
      lastResearchLine.trim().endsWith(':')
        ? lastResearchLine
        : hasPathwayBullets
          ? 'Across the pathway:'
          : null;
    const researchBodyLines =
      hasPathwayBullets && pathwayLead ? researchLinesAll.slice(0, -1) : researchLinesAll;

    const renderResearchFigureButton = (className = 'reflection-figure-side-trigger') => (
      <button
        type="button"
        className={className}
        onClick={() => setWorkplaceM3FigureModalOpen(true)}
        aria-label={stageFigureTitle}
        title={stageFigureTitle}
      >
        <div className="reflection-figure-side-lottie-wrap" aria-hidden="true">
          <Lottie
            animationData={imageAnimationData}
            loop
            autoplay
            className="reflection-figure-side-lottie"
          />
        </div>
        <span className="reflection-figure-side-label">{stageFigureSideLabel}</span>
      </button>
    );

    return (
      <>
        {reflectionCelebrate && (
          <div className="reflection-lottie-layer" aria-hidden="true">
            {badgeAnimationData && (
              <div className="reflection-lottie-card">
                <Lottie
                  animationData={badgeAnimationData}
                  loop={false}
                  autoplay
                  className="reflection-lottie-player"
                />
                <p className="reflection-lottie-text">Reflection submitted successfully</p>
              </div>
            )}
          </div>
        )}
        {isPauseSlide && (
          <div key="reflection-slide-intro" className="reflection-slide-card reflection-slide-card--focus reflection-slide-card--pause content-enter">
            <h3 className="reflection-slide-title">Pause & Reflect</h3>
            <div className="reflection-pause-wrap">
              <div className="reflection-pause-body-inner">
                <div className="reflection-pause-layout">
                  <div className="reflection-pause-content">
                    <ReflectionPauseReflectLines lines={m3ReflectionSlides} revealCount={workplaceM3RevealCount} />
                  </div>
                  <div className="reflection-pause-lottie-wrap" aria-hidden="true">
                    <ReflectionThinkLottie />
                  </div>
                </div>
                <ReflectionWordInputFooter
                  hasExistingReflection={hasExistingReflection}
                  latestWord={latestWord}
                  value={reflectionAnswers.reflection1 || ''}
                  onChange={(v) => handleReflectionChange('reflection1', v)}
                  onSubmit={submitReflectionAndAdvance}
                />
              </div>
            </div>
          </div>
        )}

        {isWordCloudSlide && (
          <div key="reflection-slide-cloud" className="reflection-slide-card reflection-slide-card--cloud content-enter">
            <div className="reflection-after-submit">
              {latestWord && (
                <p className="reflection-user-word-chip" aria-live="polite">
                  Your word: <strong>{latestWord}</strong>
                </p>
              )}
              {showWordCloud && (
                <div className="reflection-word-cloud-wrap" role="region" aria-label="Word cloud of reactions">
                  <h4 className="reflection-word-cloud-title">What others shared (one word)</h4>
                  <div
                    ref={reflectionWordCloudContainerRef}
                    className="reflection-word-cloud-react"
                  >
                    <ReactWordcloud
                      words={reflectionWordCloudWords}
                      options={reflectionWordCloudOptions}
                      size={reflectionWordCloudSize}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {isExternalSignalSlide && (
          <div key="reflection-slide-external" className="reflection-slide-card reflection-slide-card--focus reflection-slide-card--external-signal">
            <h3 className="reflection-slide-title">
              {COMMUNITY_M3_EXTERNAL_SIGNAL_SLIDE.unpackingSectionTitle || "Unpacking the Story: What's Really Going On?"}
            </h3>
            <div className="reflection-slide-content reflection-slide-content--stable">
              <div className="reflection-external-signal-body">
                <div className="reflection-slide-lines reflection-external-signal-unpacking-lines">
                  {COMMUNITY_M3_EXTERNAL_SIGNAL_SLIDE.unpackingLines?.map((line) => renderReflectionResearchLine(line))}
                </div>
              </div>
            </div>
          </div>
        )}

        {isResearchSlide && (
          <div key="reflection-slide-research" className="reflection-slide-card reflection-slide-card--focus reflection-slide-card--research content-enter">
            <h3 className="reflection-slide-title">{researchSlide.title}</h3>
            <div className="reflection-research-body">
              <div className="reflection-slide-content reflection-research-main">
                {researchSlide.introLines?.length > 0 && (
                  <div className="reflection-slide-lines reflection-research-intro">
                    {researchSlide.introLines.map((line) => (
                      <p key={line} className="reflection-slide-line">{line}</p>
                    ))}
                  </div>
                )}
                {researchSlide.subtitle ? (
                  <h4 className="reflection-slide-subtitle">{researchSlide.subtitle}</h4>
                ) : null}
                {researchSlide.lines?.length > 0 ? (
                  <div className="reflection-slide-lines">
                    {researchSlide.lines.map((line) => renderReflectionResearchLine(line))}
                  </div>
                ) : null}
                <h4 className="reflection-slide-subtitle">{researchSlide.researchTitle}</h4>
                <div className="reflection-slide-lines">
                  {researchBodyLines.map((line) => renderReflectionResearchLine(line))}
                </div>
              </div>
              {hasPathwayBullets ? (
                <div className="reflection-research-pathway-row">
                  <div className="reflection-research-pathway-copy">
                    {pathwayLead ? (
                      <p className="reflection-slide-line reflection-research-pathway-lead">{pathwayLead}</p>
                    ) : null}
                    <ul className="reflection-research-bullet-list">
                      {pathwayBullets.map((item) => (
                        <li key={item} className="reflection-slide-line reflection-research-bullet-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {renderResearchFigureButton()}
                </div>
              ) : (
                <div className="reflection-research-figure-row">{renderResearchFigureButton('reflection-figure-side-trigger reflection-figure-side-trigger--inline')}</div>
              )}
            </div>
          </div>
        )}

        {workplaceM3FigureModalOpen && (
          <div className="anika-video-modal-layer" role="dialog" aria-modal="true" aria-label={stageFigureTitle}>
            <button
              type="button"
              className="anika-video-modal-backdrop"
              onClick={() => setWorkplaceM3FigureModalOpen(false)}
              aria-label="Close figure modal"
            />
            <div
              className={`anika-video-modal-card reflection-figure-modal-card${
                isEducatorModule ? ' reflection-figure-modal-card--with-footer' : ''
              }`}
            >
              <button
                type="button"
                className="anika-video-modal-close"
                onClick={() => setWorkplaceM3FigureModalOpen(false)}
                aria-label="Close figure"
              >
                ×
              </button>
              <h3 className="animation-visual-title">{stageFigureTitle}</h3>
              <div className="animation-visual-frame intro-video-modal-placeholder reflection-figure-modal-frame">
                <img
                  src={stageFigureSrc}
                  alt={stageFigureTitle}
                  className="intro-video-modal-poster reflection-figure-modal-img"
                  {...contentImageProps({
                    width: 1400,
                    height: 900,
                    sizes: '(max-width: 900px) 100vw, min(90vw, 1100px)',
                    loading: 'lazy',
                    fetchPriority: 'low',
                  })}
                />
              </div>
              {isEducatorModule && EDUCATOR_PIPELINE_FIGURE_MODAL_FOOTER.length > 0 && (
                <div className="reflection-figure-modal-footer">
                  {EDUCATOR_PIPELINE_FIGURE_MODAL_FOOTER.map((line) => (
                    <p key={line} className="reflection-figure-modal-footer-line">{line}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  /** Module 4: carousel with hotspots; barrier detail modals (workplace six barriers / educator five). */
  const renderBarriersBlock = () => {
    const barriersSection = moduleContent.sections.find(s => s.id === 'barriers');
    const baseUrl = process.env.PUBLIC_URL || '';
    const barrierCatalog = pickFlowModule(moduleId, SIX_BARRIERS, EDUCATOR_FIVE_BARRIERS, COMMUNITY_FIVE_BARRIERS);
    const carouselSlides = pickFlowModule(moduleId, BARRIER_CAROUSEL_SLIDES, EDUCATOR_BARRIER_CAROUSEL_SLIDES, COMMUNITY_BARRIER_CAROUSEL_SLIDES);
    const barriersIntroPreviewImage = pickFlowModule(
      moduleId,
      BARRIERS_INTRO_PREVIEW_IMAGE,
      EDUCATOR_BARRIERS_INTRO_PREVIEW_IMAGE,
      COMMUNITY_BARRIERS_INTRO_PREVIEW_IMAGE
    );
    const allBarrierHotspots = pickFlowModule(moduleId, ALL_BARRIER_HOTSPOTS, EDUCATOR_BARRIER_HOTSPOTS, COMMUNITY_BARRIER_HOTSPOTS);
    const researchInsight = pickFlowModule(moduleId, WORKPLACE_M4_RESEARCH_INSIGHT, EDUCATOR_M4_RESEARCH_INSIGHT, COMMUNITY_M4_RESEARCH_INSIGHT);
    const modalBarrier = barrierSlideOutIndex != null ? barrierCatalog[barrierSlideOutIndex] : null;
    const slide0Ids = carouselSlides[0].hotspots.map((h) => h.barrierIndex);
    const firstImageComplete = slide0Ids.every((id) => revealedBarriers.includes(id));
    const totalExplored = revealedBarriers.filter((id) => allBarrierHotspots.some((h) => h.barrierIndex === id)).length;
    const totalHotspots = allBarrierHotspots.length;
    const totalProgressPct = Math.min(100, Math.round((totalExplored / totalHotspots) * 100));
    const slideCount = carouselSlides.length;
    const trackPctPerSlide = 100 / slideCount;
    const currentSlideHotspots = carouselSlides[barriersCarouselSlide].hotspots;
    const exploredOnCurrentSlide = currentSlideHotspots.filter((h) => revealedBarriers.includes(h.barrierIndex)).length;
    const sceneUnlockHint =
      slideCount > 1
        ? `The next scene unlocks after you have opened all ${currentSlideHotspots.length} barriers on this one.`
        : 'Explore each numbered spot in the scene.';
    const educatorBarriersSlideCount = 2;
    const barrierPollOptions = pickFlowModule(moduleId, [], EDUCATOR_BARRIER_POLL_OPTIONS, COMMUNITY_BARRIER_POLL_OPTIONS);
    const barriersImagePlaceholderText = isCommunityModule ? COMMUNITY_BARRIERS_IMAGE_PLACEHOLDER_TEXT : '';
    const useBarriersImagePlaceholder = Boolean(String(barriersImagePlaceholderText || '').trim());
    const showBarriersIntroSlide = !isFiveBarrierModule || barriersLearningSlide === 0;
    const showBarriersPollSlide = isFiveBarrierModule && barriersLearningSlide === 1;
    const barriersNavSlideCount = isFiveBarrierModule ? educatorBarriersSlideCount : 1;
    const barriersNavIndex = isFiveBarrierModule ? barriersLearningSlide : 0;
    const isFirstBarriersNavSlide = barriersNavIndex === 0;
    const isLastBarriersNavSlide = barriersNavIndex >= barriersNavSlideCount - 1;

    const renderEducatorBarrierPoll = () => {
      if (!educatorBarrierPollSubmitted) {
        return (
          <div className="meta-reflexivity-prompt-form barriers-poll-form">
            <p className="meta-reflexivity-prompt-label">Select all that apply</p>
            <div
              className="meta-reflexivity-option-grid barriers-poll-option-grid"
              role="group"
              aria-label="Barrier recognition options"
            >
              {barrierPollOptions.map((opt) => {
                const checked = educatorBarrierPollSelection.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    className={`meta-reflexivity-option-btn barriers-poll-option-btn ${checked ? 'meta-reflexivity-option-btn--active' : ''}`}
                    onClick={() => toggleEducatorBarrierPollOption(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="meta-reflexivity-prompt-submit"
              disabled={!educatorBarrierPollSelection.length || educatorBarrierPollSaving}
              onClick={() => void handleEducatorBarrierPollSubmit()}
            >
              {educatorBarrierPollSaving ? 'Saving…' : 'Submit (optional)'}
            </button>
          </div>
        );
      }

      const pollTotal = Math.max(0, Number(educatorBarrierPollAgg?.total ?? 0));

      return (
        <div className="meta-reflexivity-poll" aria-live="polite">
          {educatorBarrierPollStatus === 'loading' && (
            <p className="meta-reflexivity-poll-status">Thank you. Loading anonymous results…</p>
          )}
          {educatorBarrierPollStatus === 'error' && !educatorBarrierPollAgg ? (
            <p className="meta-reflexivity-poll-status meta-reflexivity-poll-status--error">
              Thank you. Community totals are unavailable right now. Your response was still saved.
            </p>
          ) : null}
          {educatorBarrierPollAgg && educatorBarrierPollStatus === 'ready' ? (
            <>
              <p className="meta-reflexivity-poll-intro">
                Thank you. Here is how participants responded so far.
              </p>
              <ul className="meta-reflexivity-poll-list">
                {barrierPollOptions.map((opt) => {
                  const count = Number(educatorBarrierPollAgg.counts?.[opt.value] ?? 0);
                  const pct = pollTotal > 0 ? Math.round((count / pollTotal) * 100) : 0;
                  const isYou = educatorBarrierPollSelection.includes(opt.value);
                  return (
                    <li
                      key={opt.value}
                      className={`meta-reflexivity-poll-row${isYou ? ' meta-reflexivity-poll-row--you' : ''}`}
                    >
                      <div className="meta-reflexivity-poll-row-header">
                        <span className="meta-reflexivity-poll-label">
                          {opt.label}
                          {isYou ? <span className="meta-reflexivity-poll-you"> (your selection)</span> : null}
                        </span>
                        <span className="meta-reflexivity-poll-count">
                          {count}
                          <span className="meta-reflexivity-poll-pct"> ({pct}%)</span>
                        </span>
                      </div>
                      <div className="meta-reflexivity-poll-bar-track" aria-hidden="true">
                        <div className="meta-reflexivity-poll-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="meta-reflexivity-poll-total">
                {pollTotal} total response{pollTotal === 1 ? '' : 's'} (each submission may select multiple
                barriers).
              </p>
            </>
          ) : null}
        </div>
      );
    };

    return (
      <div className="section-content barriers-interactive barriers-image-layout">
        {showBarriersIntroSlide && (
          <div key="barriers-intro-slide" className="barriers-text-slide-card barriers-text-slide-card--intro content-enter">
            <h3 className="barriers-text-slide-title">
              {isFiveBarrierModule ? 'The Five Barriers' : 'Unpacking the Story'}
            </h3>
            <div className="barriers-single-body">
              <div className="barriers-single-content">
                <div dangerouslySetInnerHTML={{ __html: barriersSection?.content || '' }} />
                <div className="barriers-research-insight-block">
                  <h4 className="barriers-research-insight-title">{researchInsight.title}</h4>
                  {(Array.isArray(researchInsight.body) ? researchInsight.body : [researchInsight.body]).filter(Boolean).map((para) => (
                    <p key={para} className="barriers-research-insight-copy">{para}</p>
                  ))}
                  <h4 className="barriers-research-insight-title">{researchInsight.quoteTitle}</h4>
                  <blockquote className="barriers-research-insight-quote">
                    {isEducatorModule || isCommunityModule
                      ? ensureDoubleQuoted(researchInsight.quote)
                      : researchInsight.quote}
                  </blockquote>
                </div>
              </div>
              <button
                type="button"
                className="barriers-search-legend"
                onClick={() => setBarriersExploreModalOpen(true)}
                aria-label={
                  isFiveBarrierModule
                    ? 'Open barriers exploration: The Five Barriers at a glance. Click to open the scene and explore each barrier.'
                    : 'Open barriers exploration: The Six Barriers at a glance. Click to open the scene and explore each barrier.'
                }
                title="Open barriers exploration"
              >
                <span className="barriers-search-preview-wrap" aria-hidden="true">
                  {useBarriersImagePlaceholder ? (
                    <div className="barriers-search-preview-placeholder" role="note">
                      <p>{barriersImagePlaceholderText}</p>
                    </div>
                  ) : (
                  <img
                    src={`${baseUrl}/${barriersIntroPreviewImage}`}
                    alt=""
                    className="barriers-search-preview-img"
                    {...contentImageProps({
                      width: 800,
                      height: 700,
                      sizes: '(max-width: 900px) 42vw, 290px',
                      loading: 'lazy',
                      fetchPriority: 'low',
                    })}
                  />
                  )}
                  <span className="barriers-search-preview-overlay">
                    <span className="barriers-search-preview-cta">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M15 3h6v6M14 10l7-7M10 21H4v-6M10 14l-7 7" />
                      </svg>
                      Click to explore
                    </span>
                  </span>
                </span>
                <span className="barriers-search-note">
                  {isFiveBarrierModule ? 'Explore The Five Barriers: At-A-Glance' : 'Explore The Six Barriers: At-A-Glance'}
                </span>
                <span className="barriers-search-hint">
                  Click here to open the scene and explore each barrier.
                </span>
              </button>
            </div>
          </div>
        )}

        {showBarriersPollSlide && (
          <div
            key="barriers-poll-slide"
            className="barriers-text-slide-card barriers-text-slide-card--poll barriers-text-slide-card--poll-split content-enter"
          >
            <h3 className="meta-reflexivity-heading">Which barriers feel most familiar?</h3>
            {!educatorBarrierPollSubmitted && (
              <div className="meta-reflexivity-header-lines barriers-poll-header-lines">
                <div className="meta-reflexivity-line-stack">
                
                  <p className="meta-reflexivity-line">
                    Share your answer in the poll below to see the results.
                  </p>
                </div>
              </div>
            )}
            <div
              className={`meta-reflexivity-layout meta-reflexivity-layout--reverse barriers-poll-split-layout${
                educatorBarrierPollSubmitted ? ' meta-reflexivity-layout--submitted' : ''
              }`}
            >
              {!educatorBarrierPollSubmitted && (
                <div className="meta-reflexivity-option-wrap" aria-hidden="true">
                  <Lottie
                    animationData={checklistAnimationData}
                    loop
                    autoplay
                    className="meta-reflexivity-checklist-lottie"
                  />
                </div>
              )}
              <div className="meta-reflexivity-layout-content">
                <div className="meta-reflexivity-inline-form barriers-educator-poll">
                  {renderEducatorBarrierPoll()}
                </div>
              </div>
            </div>
          </div>
        )}

        {barriersExploreModalOpen && (
          <div className="barriers-explore-modal-layer" role="dialog" aria-modal="true" aria-label="Barriers exploration">
            <button
              type="button"
              className="barriers-explore-modal-backdrop"
              onClick={closeBarriersExploreModal}
              aria-label="Close barriers exploration"
            />
            <div className="barriers-explore-modal-card">
              <button
                type="button"
                className="barriers-explore-modal-close"
                onClick={closeBarriersExploreModal}
                aria-label="Close"
              >
                ×
              </button>
              <div className="barriers-explore-modal-body">
                <div className="barriers-explore-layout">
                  <div className="barriers-explore-main">
                    <p className="barriers-image-hint">
                      Scene {barriersCarouselSlide + 1} of {slideCount}: click the numbered spots. {sceneUnlockHint}
                    </p>
                    <p className="barriers-image-progress">
                      This scene: {exploredOnCurrentSlide}/{currentSlideHotspots.length} · Total explored: {totalExplored}/{totalHotspots}
                    </p>
                    <div className="barriers-progress-track" aria-label={`Exploration progress ${totalProgressPct}%`}>
                      <div className="barriers-progress-fill" style={{ width: `${totalProgressPct}%` }} />
                    </div>
                    <div className="barriers-carousel" aria-roledescription="carousel">
                      <div className="barriers-carousel-controls" role="tablist" aria-label="Barrier scenes">
                        {/* {BARRIER_CAROUSEL_SLIDES.map((slide, idx) => {
                          const disabled = idx > 0 && !firstImageComplete;
                          const isActive = barriersCarouselSlide === idx;
                          return (
                            <button
                              key={slide.imageSrc}
                              type="button"
                              role="tab"
                              aria-selected={isActive}
                              aria-disabled={disabled}
                              disabled={disabled}
                              className={`barriers-carousel-dot ${isActive ? 'barriers-carousel-dot--active' : ''}`}
                              onClick={() => !disabled && goToBarriersCarouselSlide(idx)}
                            >
                              Scene {idx + 1}
                            </button>
                          );
                        })} */}
                      </div>
                      <div className="barriers-carousel-viewport">
                        <div
                          className="barriers-carousel-track"
                          style={{
                            width: `${slideCount * 100}%`,
                            transform: `translateX(-${barriersCarouselSlide * trackPctPerSlide}%)`,
                          }}
                        >
                          {carouselSlides.map((slide, slideIndex) => {
                            const imageSrc = `${baseUrl}/${slide.imageSrc}`;
                            return (
                              <div
                                key={slide.imageSrc}
                                className="barriers-carousel-slide"
                                style={{ flex: `0 0 ${trackPctPerSlide}%` }}
                              >
                                <div className="barriers-image-wrap">
                                  {useBarriersImagePlaceholder ? (
                                    <div className="barriers-image-placeholder" role="note">
                                      <p>{barriersImagePlaceholderText}</p>
                                    </div>
                                  ) : (
                                  <img
                                    src={imageSrc}
                                    alt={slide.alt}
                                    className="barriers-image"
                                    {...sceneImageProps(slideIndex)}
                                  />
                                  )}
                                  {slide.hotspots.map(({ barrierIndex, left, top, label }) => {
                                    const barrier = barrierCatalog[barrierIndex];
                                    const revealed = revealedBarriers.includes(barrierIndex);
                                    return (
                                      <button
                                        key={barrierIndex}
                                        type="button"
                                        className={`barriers-hotspot ${revealed ? 'barriers-hotspot--revealed' : 'barriers-hotspot--pulse'}`}
                                        style={{ left: `${left}%`, top: `${top}%` }}
                                        onClick={() => openBarrierSlideOut(barrierIndex)}
                                        aria-label={`Barrier ${barrierIndex + 1}: ${barrier.name}. Click to read more.`}
                                      >
                                        <span className="barriers-hotspot-number">{label}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="barriers-carousel-arrows">
                        <button
                          type="button"
                          className="barriers-carousel-arrow"
                          disabled={barriersCarouselSlide === 0}
                          onClick={() => goToBarriersCarouselSlide(Math.max(0, barriersCarouselSlide - 1))}
                        >
                          Previous scene
                        </button>
                        <button
                          type="button"
                          className="barriers-carousel-arrow"
                          disabled={barriersCarouselSlide >= slideCount - 1 || !firstImageComplete}
                          onClick={() => goToBarriersCarouselSlide(Math.min(slideCount - 1, barriersCarouselSlide + 1))}
                        >
                          Next scene
                        </button>
                      </div>
                    </div>
                  </div>
                  <aside
                    className={`barriers-explore-detail ${modalBarrier ? 'barriers-explore-detail--active' : ''}`}
                    aria-live="polite"
                  >
                    {modalBarrier ? (
                      <div className="barriers-explore-detail-content">
                        <button
                          type="button"
                          className="barriers-explore-detail-close"
                          onClick={closeBarrierSlideOut}
                          aria-label="Close barrier details"
                        >
                          ×
                        </button>
                          <BarrierDetailBody
                            barrier={modalBarrier}
                            barrierIndex={barrierSlideOutIndex}
                            variant={isCommunityModule ? 'community' : isEducatorModule ? 'educator' : 'workplace'}
                          />
                      </div>
                    ) : (
                      <div className="barriers-explore-detail-empty">
                        <h4>Select a barrier</h4>
                        <p>Click a numbered hotspot in the scene to view barrier details here.</p>
                      </div>
                    )}
                  </aside>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="barriers-slide-nav">
          <button
            type="button"
            className="barriers-slide-nav-btn"
            disabled={isFirstBarriersNavSlide && !hasPrevSection('barriers')}
            onClick={() => {
              if (!isFirstBarriersNavSlide) {
                setBarriersLearningSlide((s) => Math.max(0, s - 1));
                return;
              }
              goToPrevSectionFrom('barriers');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {Array.from({ length: barriersNavSlideCount }, (_, idx) => (
              <span
                key={`barriers-nav-bubble-${idx}`}
                className={`module-slide-bubble ${barriersNavIndex === idx ? 'module-slide-bubble--active' : ''}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="barriers-slide-nav-btn barriers-slide-nav-btn--primary"
            onClick={() => {
              if (!isLastBarriersNavSlide) {
                setBarriersLearningSlide((s) => Math.min(barriersNavSlideCount - 1, s + 1));
                return;
              }
              goToNextSectionFrom('barriers');
            }}
          >
            {isLastBarriersNavSlide ? 'Next module' : 'Next'}
          </button>
        </div>
        {/* Animated modal (fallback when explore modal is closed) */}
        {!barriersExploreModalOpen && barrierSlideOutIndex != null && (
          <>
            <div className="barriers-modal-backdrop" onClick={closeBarrierSlideOut} aria-hidden="true" />
            <div
              className="barriers-modal barriers-modal--open"
              role="dialog"
              aria-labelledby="barriers-modal-title"
              aria-modal="true"
            >
              <div className="barriers-modal-content">
                <button
                  type="button"
                  className="barriers-modal-close"
                  onClick={closeBarrierSlideOut}
                  aria-label="Close"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
                {modalBarrier && (
                  <div className="barriers-modal-body">
                    <BarrierDetailBody
                      barrier={modalBarrier}
                      barrierIndex={barrierSlideOutIndex}
                      titleId="barriers-modal-title"
                      variant={isCommunityModule ? 'community' : isEducatorModule ? 'educator' : 'workplace'}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  /** Module 6 (Small Actions): uses latest table; modal shows How it looks, What triggers it, Leverage point */
  const renderActionsBlock = () => {
    const actionsImagePath = pickFlowModule(moduleId, ACTIONS_IMAGE_SRC, EDUCATOR_ACTIONS_IMAGE_SRC, COMMUNITY_ACTIONS_IMAGE_SRC);
    const imageSrc = `${process.env.PUBLIC_URL || ''}/${actionsImagePath}`;
    const actionsTable = pickFlowModule(moduleId, ACTIONS_TABLE_CONTENT, EDUCATOR_ACTIONS_TABLE_CONTENT, COMMUNITY_ACTIONS_TABLE_CONTENT);
    const actionsHotspots = pickFlowModule(moduleId, ACTIONS_HOTSPOTS, EDUCATOR_ACTIONS_HOTSPOTS, COMMUNITY_ACTIONS_HOTSPOTS);
    const m7slides = pickFlowModule(moduleId, WORKPLACE_M7_SLIDES, EDUCATOR_M7_SLIDES, COMMUNITY_M7_SLIDES);
    const modalRow = actionModalIndex != null ? actionsTable[actionModalIndex] : null;
    const slide = m7slides[workplaceM7Slide] || m7slides[0];
    const { hotspot: m7HotspotSlideIndex, shift: m7ShiftSlideIndex } = getM7SlideIndices(m7slides);
    const actionsImagePlaceholderText = isCommunityModule ? COMMUNITY_ACTIONS_IMAGE_PLACEHOLDER_TEXT : '';
    const useActionsImagePlaceholder = Boolean(String(actionsImagePlaceholderText || '').trim());
    const isM7VideoSlide = Boolean(slide.youtubeVideoId);
    const isFirst = workplaceM7Slide === 0;
    const isLast = workplaceM7Slide === m7slides.length - 1;
    const canGoNext = isM7VideoSlide
      ? true
      : slide.lines.length
        ? workplaceM7RevealCount >= slide.lines.length
        : true;
    const insightLines = slide.lines.slice(0, Math.max(1, workplaceM7RevealCount));
    const exploredCount = revealedActionSpots.filter((id) => actionsHotspots.some((h) => h.spotIndex === id)).length;
    const mappingRoleWorksheetPdf = isCommunityModule
      ? COMMUNITY_MAPPING_ROLE_PDF
      : isEducatorModule
        ? EDUCATOR_MAPPING_ROLE_PDF
        : SYSTEM_MAPPING_WORKSHEET_PDF;
    const mappingRoleWorksheetUrl = `${process.env.PUBLIC_URL || ''}/${mappingRoleWorksheetPdf}`;
    const glossaryPdf = isCommunityModule
      ? COMMUNITY_GLOSSARY_AND_RESOURCES_PDF
      : isEducatorModule
        ? EDUCATOR_GLOSSARY_AND_RESOURCES_PDF
        : GLOSSARY_AND_RESOURCES_PDF;
    const glossaryAndResourcesUrl = `${process.env.PUBLIC_URL || ''}/${glossaryPdf}`;
    const howLooksLabel = isCommunityModule ? 'How it looks in Aman' : isEducatorModule ? 'How it looks in Sara' : 'How it looks in Anika';
    const triggersLabel = isCommunityModule
      ? 'What this means in everyday terms'
      : isEducatorModule
        ? 'What academic or program condition triggers it'
        : 'What workplace condition triggers it';

    return (
      <div className="section-content actions-interactive actions-image-layout actions-guided-slides">
        <div
          key={`m7-slide-${workplaceM7Slide}`}
          className={`actions-guided-card content-enter${workplaceM7Slide === m7HotspotSlideIndex ? ' actions-guided-card--slide3' : ''}${isM7VideoSlide ? ' actions-guided-card--video actions-guided-card--m7-video-split' : ''}${workplaceM7Slide === m7ShiftSlideIndex ? ' actions-guided-card--shift' : ''}`}
        >
          {workplaceM7Slide === m7HotspotSlideIndex ? (
            <div className="actions-slide3-header-row">
              <h3 className="actions-intro-heading">{slide.title}</h3>
              <button
                type="button"
                className="actions-at-a-glance-link actions-at-a-glance-link--slide3-top"
                onClick={() => setActionsTableModalOpen(true)}
              >
                View full at-a-glance barrier table
              </button>
            </div>
          ) : !isM7VideoSlide ? (
            <h3 className="actions-intro-heading">{slide.title}</h3>
          ) : null}
          {isM7VideoSlide && (
            <>
              <h3 className="actions-intro-heading meta-reflexivity-heading">{slide.title}</h3>
              <div className="meta-reflexivity-layout meta-reflexivity-layout--with-video meta-reflexivity-layout--reverse actions-m7-slide2-layout">
                <div className="meta-reflexivity-checklist-wrap actions-m7-worksheet-column">
                  {pdfAnimationData && (
                    <Lottie
                      animationData={pdfAnimationData}
                      loop={false}
                      autoplay
                      className="meta-reflexivity-checklist-lottie"
                    />
                  )}
                  <div className="meta-reflexivity-download-wrap meta-reflexivity-download-wrap--under-animation">
                    <a
                      href={mappingRoleWorksheetUrl}
                      download={mappingRoleWorksheetPdf}
                      className="meta-reflexivity-download-btn"
                    >
                      <svg
                        className="meta-reflexivity-download-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Mapping Your Role in the System (Worksheet)
                    </a>
                  </div>
                </div>
                <div className="meta-reflexivity-layout-content">
                  <div className="meta-reflexivity-m5-video-shell actions-m7-video-shell">
                    <IntroYoutubePosterPlay
                      embedUrl={getIntroModuleYoutubeEmbedUrl(slide.youtubeVideoId)}
                      videoId={slide.youtubeVideoId}
                      posterImgProps={contentImageProps({
                        width: 1280,
                        height: 720,
                        loading: 'eager',
                        fetchPriority: 'high',
                      })}
                      isPlaying={workplaceM7Slide0VideoPlaying}
                      onPlay={() => setWorkplaceM7Slide0VideoPlaying(true)}
                      title={
                        isCommunityModule
                          ? 'Community module small actions overview video'
                          : isEducatorModule
                            ? 'Educator module small actions overview video'
                            : 'Building Workplace Bridges small actions overview video'
                      }
                      mode="modal"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          {workplaceM7Slide === m7HotspotSlideIndex && (
            <div className="actions-slide3-wrap">
              <div className="actions-slide3-body-inner">
                <div className="actions-slide3-split">
                  <div className="actions-slide3-split-main">
                    <div className="actions-image-wrap" aria-label="Small actions image - click hotspots to learn more">
                      {useActionsImagePlaceholder ? (
                        <div className="actions-image-placeholder" role="note">
                          <p>{actionsImagePlaceholderText}</p>
                        </div>
                      ) : (
                      <img
                        src={imageSrc}
                        alt="Explore small actions and big shifts"
                        className="actions-image"
                        {...contentImageProps({
                          width: 1600,
                          height: 900,
                          sizes: '(max-width: 900px) 100vw, min(92vw, 1100px)',
                          loading: 'lazy',
                          fetchPriority: 'low',
                        })}
                      />
                      )}
                      {actionsHotspots.map(({ spotIndex, left, top, label }) => {
                        const revealed = revealedActionSpots.includes(spotIndex);
                        return (
                          <button
                            key={spotIndex}
                            type="button"
                            className={`actions-hotspot ${revealed ? 'actions-hotspot--revealed' : ''}`}
                            style={{ left: `${left}%`, top: `${top}%` }}
                            onClick={() => openActionModal(spotIndex)}
                            aria-label={`Spot ${spotIndex + 1}: ${actionsTable[spotIndex]?.name || 'Learn more'}. Click to read.`}
                          >
                            <span className="actions-hotspot-number">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <aside className="actions-slide3-split-aside" aria-live="polite">
                    {modalRow ? (
                      <div className="barriers-explore-detail-content actions-slide3-detail-card">
                        <button
                          type="button"
                          className="barriers-explore-detail-close"
                          onClick={closeActionModal}
                          aria-label="Clear selection"
                        >
                          ×
                        </button>
                        <span className="actions-modal-number">Recommendation</span>
                        <h3 className="actions-modal-title">{modalRow.name}</h3>
                        <div className="actions-modal-section">
                          <h4 className="actions-modal-label">{howLooksLabel}</h4>
                          <p className="actions-modal-desc">{modalRow.howItLooksInAnika}</p>
                        </div>
                        <div className="actions-modal-section">
                          <h4 className="actions-modal-label">{triggersLabel}</h4>
                          <p className="actions-modal-desc">{modalRow.workplaceConditionTriggersIt}</p>
                        </div>
                        <div className="actions-modal-section">
                          <h4 className="actions-modal-label">Leverage point (What to examine or redesign)</h4>
                          <p className="actions-modal-desc">{modalRow.leveragePoint}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="actions-slide3-detail-empty">
                        <h4>Select a barrier</h4>
                        <p>Click a numbered hotspot on the image to view details here.</p>
                      </div>
                    )}
                  </aside>
                </div>
              </div>
            </div>
          )}

          {workplaceM7Slide === m7ShiftSlideIndex && (
            <div className="actions-shift-wrap">
              <div className="actions-shift-body-inner">
                <div className="actions-slide2-layout">
                  <div className="actions-slide2-content">
                    {insightLines[0] && <p className="actions-intentional-shift-note actions-guided-line">{insightLines[0]}</p>}
                    <div className="actions-intentional-shift actions-intentional-shift--guided">
                      <div className="actions-intentional-shift-inner">
                        <div className="actions-intentional-shift-content">
                          {intentionalShiftSubmitted ? (
                            <p className="actions-intentional-shift-success">Thank you for your response.</p>
                          ) : (
                            <>
                              {insightLines[1] && (
                                <label htmlFor="intentional-shift-input" className="actions-intentional-shift-label actions-guided-line">
                                  {insightLines[1]}
                                </label>
                              )}
                              {workplaceM7InputReady && (
                                <div className="actions-delayed-input content-enter">
                                  <textarea
                                    id="intentional-shift-input"
                                    className="actions-intentional-shift-input"
                                    value={intentionalShiftText}
                                    onChange={(e) => setIntentionalShiftText(e.target.value)}
                                    placeholder="Type your commitment statement..."
                                    rows={3}
                                    maxLength={400}
                                  />
                                  <div className="actions-intentional-shift-footer">
                                    <button
                                      type="button"
                                      className="actions-intentional-shift-submit"
                                      disabled={!String(intentionalShiftText || '').trim() || intentionalShiftSaving}
                                      onClick={() => void handleIntentionalShiftSubmit()}
                                    >
                                      {intentionalShiftSaving ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>
                                </div>
                              )}
                              {insightLines[2] && (
                                <p className="actions-intentional-shift-footnote actions-guided-line">{insightLines[2]}</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="actions-slide2-lottie-wrap" aria-hidden="true">
                    <IntentionalShiftQuestionLottie />
                  </div>
                </div>
                <div className="actions-slide3-bottom actions-slide3-bottom--shift">
                  <span className="actions-slide3-worksheet-intro">
                    Download the glossary and additional resources.
                  </span>
                  <a
                    href={glossaryAndResourcesUrl}
                    download={glossaryPdf}
                    className="actions-slide1-worksheet-link"
                  >
                    Glossary and Additional Resources (PDF)
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="actions-guided-nav">
          <button
            type="button"
            className="actions-guided-nav-btn"
            disabled={isFirst && !hasPrevSection('actions')}
            onClick={() => {
              if (!isFirst) {
                const prevPos = Math.max(0, workplaceM7Slide - 1);
                setWorkplaceM7Slide(prevPos);
                setWorkplaceM7RevealCount(1);
                return;
              }
              goToPrevSectionFrom('actions');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {m7slides.map((_, idx) => (
              <span key={`m7-bubble-${idx}`} className={`module-slide-bubble ${workplaceM7Slide === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="actions-guided-nav-btn actions-guided-nav-btn--primary"
            disabled={isLast ? false : !canGoNext}
            onClick={() => {
              if (isLast) {
                goToNextSectionFrom('actions');
                return;
              }
              const nextPos = Math.min(m7slides.length - 1, workplaceM7Slide + 1);
              setWorkplaceM7Slide(nextPos);
              setWorkplaceM7RevealCount(1);
            }}
          >
            {isLast ? 'Next module' : 'Next'}
          </button>
        </div>

        {actionsTableModalOpen && (
          <>
            <div className="actions-table-modal-backdrop" onClick={() => setActionsTableModalOpen(false)} aria-hidden="true" />
            <div
              className="actions-table-modal actions-table-modal--open"
              role="dialog"
              aria-labelledby="actions-table-modal-title"
              aria-modal="true"
            >
              <div className="actions-table-modal-content">
                <button
                  type="button"
                  className="actions-table-modal-close"
                  onClick={() => setActionsTableModalOpen(false)}
                  aria-label="Close"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
                <h3 id="actions-table-modal-title" className="actions-table-modal-title">At-a-glance: Barriers, Triggers, and Leverage Points</h3>
                <div className="actions-table-wrap">
                  <table className="actions-table">
                    <thead>
                      <tr>
                        <th>Barrier</th>
                        <th>{howLooksLabel}</th>
                        <th>{triggersLabel}</th>
                        <th>Leverage point (What to examine or redesign)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actionsTable.map((row, index) => (
                        <tr key={row.name}>
                          <td>{index + 1}. {row.name}</td>
                          <td>{row.howItLooksInAnika}</td>
                          <td>{row.workplaceConditionTriggersIt}</td>
                          <td>{row.leveragePoint}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const submitParentPause = async (storyKey) => {
    const text = String(parentPauseAnswers[storyKey] || '').trim();
    if (!text) return;
    try {
      const stored = JSON.parse(localStorage.getItem(parentPauseStorageKey(storyKey)) || '[]');
      stored.push({ text, at: new Date().toISOString() });
      localStorage.setItem(parentPauseStorageKey(storyKey), JSON.stringify(stored));
    } catch (_) {}
    setParentPauseSubmitted((prev) => ({ ...prev, [storyKey]: true }));
    const saved = await writeParentModuleStoryResponse({ storyKey, responseType: 'pause', text, moduleId });
    if (saved) {
      const result = await fetchParentPauseWordCloudAggregates(storyKey, moduleId);
      setParentWordCloudCounts(result?.counts ?? null);
    } else {
      window.alert(
        'Your Pause response was saved on this device, but could not be saved to your account. Please sign in so it is stored with your progress and included in the shared word cloud.'
      );
      const ownWord = normalizeReflectionCloudWord(text);
      if (ownWord) {
        setParentWordCloudCounts((prev) => ({
          ...(prev || {}),
          [ownWord]: ((prev && prev[ownWord]) || 0) + 1,
        }));
      }
    }
  };

  const submitParentAct = async (storyKey) => {
    const text = String(parentActAnswers[storyKey] || '').trim();
    if (!text || parentActSaving) return;
    setParentActSaving(true);
    try {
      try {
        const stored = JSON.parse(localStorage.getItem(parentActStorageKey(storyKey)) || '[]');
        stored.push({ text, at: new Date().toISOString() });
        localStorage.setItem(parentActStorageKey(storyKey), JSON.stringify(stored));
      } catch (_) {}
      const saved = await writeParentModuleStoryResponse({ storyKey, responseType: 'act', text, moduleId });
      setParentActSubmitted((prev) => ({ ...prev, [storyKey]: true }));
      if (!saved) {
        window.alert(
          'Your Act response was saved on this device, but could not be saved to your account. Please sign in so it is stored with your progress.'
        );
      }
    } finally {
      setParentActSaving(false);
    }
  };

  const renderParentStoryBlock = (sectionId) => {
    const config = PARENT_STORY_SECTIONS[sectionId];
    if (!config) return null;
    const { storyKey, character, introSlide } = config;
    const slideIndex = parentStorySlide;
    const introLines = introSlide.lines || [];
    const introRevealTotal = introLines.length + (introSlide.closingLine ? 1 : 0);
    const visibleIntroLines = introLines.slice(0, Math.min(parentStoryRevealCount, introLines.length));
    const showIntroClosing = introSlide.closingLine && parentStoryRevealCount >= introLines.length;
    const youtubeId = config.youtubeVideoId ? String(config.youtubeVideoId).trim() : '';
    const youtubeEmbedUrl = youtubeId ? getIntroModuleYoutubeEmbedUrl(youtubeId) : '';
    const useYoutube = Boolean(youtubeEmbedUrl);
    const caseVideo = config.caseVideo || null;
    const base = process.env.PUBLIC_URL || '';
    const caseVideoSources = caseVideo
      ? [
          resolvePublicOrAbsoluteUrl(base, caseVideo.localSrc),
          resolvePublicOrAbsoluteUrl(base, caseVideo.remoteSrc),
        ].filter((src, idx, arr) => Boolean(src) && arr.indexOf(src) === idx)
      : [];
    const casePosterUrl = caseVideo ? resolvePublicOrAbsoluteUrl(base, caseVideo.poster) : '';
    const hasCaseVideo = !useYoutube && caseVideoSources.length > 0;
    const showVideoPlaceholder = Boolean(config.videoPlaceholderText) && !useYoutube && !hasCaseVideo;
    const thumbPosterUrl = useYoutube
      ? getYoutubePosterUrl(youtubeId)
      : (casePosterUrl || `${process.env.PUBLIC_URL || ''}/images/thumb1.jpg`);
    const closeParentStoryVideoModal = () => {
      setParentStoryVideoModalOpen(false);
      setParentStoryYoutubePlayingModal(false);
    };
    const pauseSubmitted = parentPauseSubmitted[storyKey];
    const actSubmitted = parentActSubmitted[storyKey];
    const isFirst = slideIndex === 0;
    const isLast = slideIndex === 2;
    const sectionSlides = 3;

    return (
      <div className="section-content workplace-m2-interactive parent-story-interactive">
        {slideIndex === 0 && (
          <div key={`parent-${sectionId}-intro`} className="workplace-m2-slide-card workplace-m2-slide-card--anim">
            <h3 className="workplace-m2-title">{introSlide.title}</h3>
            <div className="workplace-m2-layout">
              <div className="workplace-m2-content">
                <div className="workplace-m2-line-stack">
                  {visibleIntroLines.map((line) => (
                    <p key={line} className="workplace-m2-line">{line}</p>
                  ))}
                  {showIntroClosing && introSlide.closingLine && (
                    <p className="workplace-m2-line workplace-m2-closing-line">{introSlide.closingLine}</p>
                  )}
                </div>
              </div>
              {showVideoPlaceholder ? (
                <div className="workplace-m2-video-text-placeholder" role="note">
                  <p>{config.videoPlaceholderText}</p>
                </div>
              ) : (
                <button
                  type="button"
                  className="workplace-m2-lottie-trigger workplace-m2-video-thumb-trigger"
                  onClick={() => {
                    setParentStoryYoutubePlayingModal(false);
                    setParentStoryVideoModalOpen(true);
                  }}
                  aria-label={`Open ${character}'s story video`}
                  title={`Open video: ${config.videoTitle}`}
                >
                  <img
                    src={thumbPosterUrl}
                    alt=""
                    className="workplace-m2-video-thumb"
                    draggable={false}
                    {...(useYoutube ? youtubePosterImgHandlers : {})}
                    {...videoThumbProps}
                  />
                  <div className="workplace-m2-video-overlay" aria-hidden="true">
                    <span className="workplace-m2-video-play">
                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        {slideIndex === 1 && (
          <div key={`parent-${sectionId}-pause`} className="reflection-slide-card reflection-slide-card--focus reflection-slide-card--pause reflection-slide-card--parent-pause-split content-enter">
            <h3 className="reflection-slide-title">Pause</h3>
            <div className="reflection-pause-wrap">
              <div className="reflection-pause-body-inner">
                <div className={`parent-pause-split${config.researchInsight ? ' parent-pause-split--with-insight' : ''}`}>
                  <div className="parent-pause-split-left">
                    {config.pauseLines?.length > 0 && (
                      <div className="reflection-slide-lines">
                        {config.pauseLines.map((line) => (
                          <p key={line} className="reflection-slide-line reflection-slide-line--pause">{line}</p>
                        ))}
                      </div>
                    )}
                    <p className="reflection-slide-line parent-story-prompt parent-story-prompt--question">
                      {config.pausePrompt}
                    </p>
                    {pauseSubmitted ? (
                      <div className="reflection-after-submit parent-pause-after-submit">
                        <p className="reflection-user-word-chip" aria-live="polite">
                          Your word:{' '}
                          <strong>
                            {normalizeReflectionCloudWord(parentPauseAnswers[storyKey])
                              || String(parentPauseAnswers[storyKey] || '').trim()}
                          </strong>
                        </p>
                        <div className="reflection-word-cloud-wrap parent-pause-word-cloud" role="region" aria-label="Word cloud of reactions">
                          <h4 className="reflection-word-cloud-title">What others shared (one word)</h4>
                          <div
                            ref={parentWordCloudContainerRef}
                            className="reflection-word-cloud-react"
                          >
                            <ReactWordcloud
                              words={parentWordCloudWords}
                              options={reflectionWordCloudOptions}
                              size={parentWordCloudSize}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="reflection-footer-input reflection-footer-input--inline">
                        <textarea
                          id={`parent-pause-${storyKey}`}
                          className="reflection-box reflection-textarea parent-story-textarea"
                          rows={2}
                          maxLength={80}
                          value={parentPauseAnswers[storyKey] || ''}
                          onChange={(e) => setParentPauseAnswers((prev) => ({ ...prev, [storyKey]: e.target.value }))}
                          placeholder="Share your reaction in one word…"
                          aria-label={config.pausePrompt}
                        />
                        <button
                          type="button"
                          className="reflection-submit-btn"
                          disabled={!String(parentPauseAnswers[storyKey] || '').trim()}
                          onClick={() => void submitParentPause(storyKey)}
                        >
                          Save reflection
                        </button>
                      </div>
                    )}
                  </div>
                  {config.researchInsight && (config.researchInsight.showAfterForm || pauseSubmitted) && (
                    <div className="parent-pause-split-right parent-research-insight content-enter">
                      {config.researchInsight.title && (
                        <h4 className="reflection-slide-subtitle">{config.researchInsight.title}</h4>
                      )}
                      {(config.researchInsight.bodyLines || (config.researchInsight.body ? [config.researchInsight.body] : [])).map((line) =>
                        renderReflectionResearchLine(line, {
                          onOpenMoreChart: (chart) => setParentResearchMoreChart(chart),
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {slideIndex === 2 && (
          <div key={`parent-${sectionId}-act`} className="reflection-slide-card reflection-slide-card--focus reflection-slide-card--parent-act content-enter">
            <div className="reflection-pause-wrap">
              <div className="reflection-pause-body-inner">
                <h3 className="reflection-slide-title">{config.actSlideTitle ?? config.reflectTitle}</h3>
                <div className="reflection-slide-content parent-story-act-content">
                  {config.reflectQuestions?.length > 0 && (
                    <div className="parent-reflect-block parent-reflect-block--lead content-enter">
                      {config.reflectIntro && (
                        <p className="reflection-slide-line">{config.reflectIntro}</p>
                      )}
                      <ul className="parent-reflect-question-list">
                        {config.reflectQuestions.map((q) => (
                          <li key={q} className="reflection-slide-line">{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <h4 className="reflection-slide-subtitle">{config.actTitle}</h4>
                  {config.actIntro && (
                    <p className="reflection-slide-line">{config.actIntro}</p>
                  )}
                  <p className="reflection-slide-line parent-story-prompt">{config.actPrompt}</p>
                  {config.actExamples?.length > 0 && (
                    <div className="parent-act-examples">
                      {!config.actHideExamplesHeading && (
                        <h4 className="reflection-slide-subtitle">Example questions from the research</h4>
                      )}
                      {/* <ul className="parent-reflect-question-list">
                        {config.actExamples.map((item) => (
                          <li key={item} className="reflection-slide-line">{item}</li>
                        ))}
                      </ul> */}
                    </div>
                  )}
                  {!config.actHideInput && (
                    actSubmitted ? (
                      <div className="reflection-box">
                        <p className="reflection-label">Thank you for sharing.</p>
                        <p className="reflection-user-word-chip">{parentActAnswers[storyKey]}</p>
                      </div>
                    ) : (
                      <div className="reflection-footer-input">
                        <textarea
                          id={`parent-act-${storyKey}`}
                          className="reflection-box reflection-textarea parent-story-textarea"
                          rows={4}
                          maxLength={600}
                          value={parentActAnswers[storyKey] || ''}
                          onChange={(e) => setParentActAnswers((prev) => ({ ...prev, [storyKey]: e.target.value }))}
                          placeholder={config.actPlaceholder}
                        />
                        <button
                          type="button"
                          className="reflection-submit-btn"
                          disabled={!String(parentActAnswers[storyKey] || '').trim() || parentActSaving}
                          onClick={() => void submitParentAct(storyKey)}
                        >
                          {parentActSaving ? 'Saving…' : 'Save response'}
                        </button>
                      </div>
                    )
                  )}
                  {config.transitionLine && (config.actHideInput || actSubmitted) && (
                    <p className="reflection-slide-line parent-story-transition">{config.transitionLine}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {parentStoryVideoModalOpen && (useYoutube || hasCaseVideo) && (
          <div className="anika-video-modal-layer" role="dialog" aria-modal="true" aria-label={`${character} case study video`}>
            <button
              type="button"
              className="anika-video-modal-backdrop"
              onClick={closeParentStoryVideoModal}
              aria-label="Close video modal"
            />
            <div className="anika-video-modal-card">
              <button
                type="button"
                className="anika-video-modal-close"
                onClick={closeParentStoryVideoModal}
                aria-label="Close video"
              >
                ×
              </button>
              <h3 className="animation-visual-title">{config.videoTitle}</h3>
              <div className={`animation-visual-frame animation-visual-frame--video${useYoutube ? ' intro-video-modal-placeholder' : ''}`}>
                {useYoutube ? (
                  <IntroYoutubePosterPlay
                    embedUrl={youtubeEmbedUrl}
                    videoId={youtubeId}
                    posterImgProps={videoThumbProps}
                    isPlaying={parentStoryYoutubePlayingModal}
                    onPlay={() => setParentStoryYoutubePlayingModal(true)}
                    title={`${character} case study video`}
                    mode="modal"
                  />
                ) : (
                  <video
                    className="animation-visual-video"
                    preload="metadata"
                    poster={casePosterUrl}
                    aria-label={`${character} case study video`}
                    {...LOCAL_VIDEO_PLAYER_PROPS}
                  >
                    {caseVideoSources.map((src) => (
                      <source key={src} src={src} type="video/mp4" />
                    ))}
                    Your browser does not support embedded video playback.
                  </video>
                )}
              </div>
            </div>
          </div>
        )}

        {parentResearchMoreChart
          && createPortal(
            <div
              className="anika-video-modal-layer parent-research-chart-modal-layer"
              role="dialog"
              aria-modal="true"
              aria-label={parentResearchMoreChart.title || parentResearchMoreChart.alt || 'Additional research chart'}
            >
              <button
                type="button"
                className="anika-video-modal-backdrop"
                onClick={() => setParentResearchMoreChart(null)}
                aria-label="Close chart modal"
              />
              <div className="anika-video-modal-card parent-research-chart-modal-card">
                <button
                  type="button"
                  className="anika-video-modal-close"
                  onClick={() => setParentResearchMoreChart(null)}
                  aria-label="Close chart"
                >
                  ×
                </button>
                <div className="parent-research-chart-modal-body">
                  {parentResearchMoreChart.src ? (
                    <figure className="parent-research-stat-image-wrap">
                      <img
                        src={parentResearchMoreChart.src}
                        alt={parentResearchMoreChart.alt || ''}
                        className="parent-research-stat-image"
                      />
                      {parentResearchMoreChart.caption ? (
                        <figcaption className="parent-research-bar-chart-caption">
                          {parentResearchMoreChart.caption}
                        </figcaption>
                      ) : null}
                      {parentResearchMoreChart.footnote ? (
                        <p className="parent-research-bar-chart-footnote">
                          {parentResearchMoreChart.footnote}
                        </p>
                      ) : null}
                    </figure>
                  ) : (
                    renderResearchGroupedBarChart(parentResearchMoreChart, 'parent-research-more-chart')
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

        <div className="workplace-m2-nav">
          <button
            type="button"
            className="workplace-m2-nav-btn"
            disabled={isFirst && !hasPrevSection(sectionId)}
            onClick={() => {
              if (!isFirst) {
                setParentStorySlide((s) => Math.max(0, s - 1));
                return;
              }
              goToPrevSectionFrom(sectionId);
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {Array.from({ length: sectionSlides }).map((_, idx) => (
              <span key={`parent-bubble-${sectionId}-${idx}`} className={`module-slide-bubble ${slideIndex === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="workplace-m2-nav-btn workplace-m2-nav-btn--primary"
            disabled={
              (slideIndex === 0 && parentStoryRevealCount < introRevealTotal)
              || (slideIndex === 1 && !pauseSubmitted)
              || (slideIndex === 2 && !config.actHideInput && !actSubmitted)
            }
            onClick={() => {
              if (isLast) {
                goToNextSectionFrom(sectionId);
                return;
              }
              if (slideIndex === 1 && !pauseSubmitted && String(parentPauseAnswers[storyKey] || '').trim()) {
                void submitParentPause(storyKey);
              }
              setParentStorySlide((s) => Math.min(sectionSlides - 1, s + 1));
              if (slideIndex === 0) setParentStoryRevealCount(1);
            }}
          >
            {isLast ? 'Next section' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  const renderParentMetaReflexivityBlock = () => {
    const slides = PARENT_M5_SLIDES;
    const slide = slides[workplaceM5Slide] || slides[0];
    const isFirst = workplaceM5Slide === 0;
    const isLast = workplaceM5Slide === slides.length - 1;
    const baseUrl = process.env.PUBLIC_URL || '';
    const youtubeId = slide.youtubeVideoId ? String(slide.youtubeVideoId).trim() : '';
    const youtubeEmbedUrl = youtubeId ? getIntroModuleYoutubeEmbedUrl(youtubeId) : '';
    const isVideoSlide = Boolean(youtubeEmbedUrl);
    const slideLines = slide.lines || [];
    const visibleLines = slideLines.slice(0, Math.min(workplaceM5RevealCount, slideLines.length));
    const canGoNext = isVideoSlide || workplaceM5RevealCount >= slideLines.length;

    return (
      <div className="section-content workplace-m2-interactive reflection-interactive reflection-slides parent-meta-interactive">
        <div
          key={`parent-m5-slide-${workplaceM5Slide}`}
          className={`reflection-slide-card reflection-slide-card--focus content-enter${
            isVideoSlide
              ? ' reflection-slide-card--parent-meta-video'
              : ' reflection-slide-card--parent-meta-text'
          }`}
        >
          <h3 className="reflection-slide-title">{slide.title}</h3>
          {isVideoSlide ? (
            <div className="meta-reflexivity-m5-video-shell parent-meta-video-shell">
              <IntroYoutubePosterPlay
                embedUrl={youtubeEmbedUrl}
                videoId={youtubeId}
                posterImgProps={contentImageProps({
                  width: 1280,
                  height: 720,
                  loading: 'eager',
                  fetchPriority: 'high',
                })}
                isPlaying={workplaceM5Slide0VideoPlaying}
                onPlay={() => setWorkplaceM5Slide0VideoPlaying(true)}
                title={slide.title || 'Parent module meta-reflexivity video'}
                mode="modal"
              />
            </div>
          ) : (
            <div className="reflection-pause-wrap parent-meta-text-wrap">
              <div className="reflection-pause-body-inner parent-meta-text-inner">
                <div className="reflection-slide-content parent-meta-text-content">
                  <div className="reflection-slide-lines parent-meta-practice-copy">
                    {visibleLines.map((line) => (
                      <p key={line} className="reflection-slide-line">
                        {line}
                      </p>
                    ))}
                  </div>
                  {slide.crossModuleLinks?.length > 0 && (
                    <div className="parent-cross-module-links">
                      {slide.crossModuleLinks.map((link) => (
                        <Link key={link.moduleId} to={`/study/${link.moduleId}`} className="parent-cross-module-card">
                          <strong>{link.title}</strong>
                          <span>{link.description}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                  {slide.resources?.length > 0 && isLast && (
                    <div className="parent-meta-resources">
                      {slide.resourceIntro && (
                        <p className="reflection-slide-line">{slide.resourceIntro}</p>
                      )}
                      {slide.resources.map((resource) =>
                        resource.type === 'pdf' ? (
                          <a
                            key={resource.filename}
                            href={`${baseUrl}/${resource.filename}`}
                            download={resource.filename}
                            className="actions-slide1-worksheet-link"
                          >
                            {resource.label}
                          </a>
                        ) : (
                          <a
                            key={resource.href}
                            href={resource.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="actions-slide1-worksheet-link"
                          >
                            {resource.label}
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="workplace-m2-nav">
          <button
            type="button"
            className="workplace-m2-nav-btn"
            disabled={isFirst && !hasPrevSection('meta-reflexivity')}
            onClick={() => {
              if (!isFirst) {
                setWorkplaceM5Slide((s) => s - 1);
                setWorkplaceM5RevealCount(1);
                setWorkplaceM5Slide0VideoPlaying(false);
                return;
              }
              goToPrevSectionFrom('meta-reflexivity');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {slides.map((_, idx) => (
              <span key={`parent-m5-bubble-${idx}`} className={`module-slide-bubble ${workplaceM5Slide === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="workplace-m2-nav-btn workplace-m2-nav-btn--primary"
            disabled={!canGoNext}
            onClick={() => {
              if (isLast) {
                goToNextSectionFrom('meta-reflexivity');
                return;
              }
              setWorkplaceM5Slide((s) => s + 1);
              setWorkplaceM5RevealCount(1);
              setWorkplaceM5Slide0VideoPlaying(false);
            }}
          >
            {isLast ? 'Next module section' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  /** Anika's Story (slide 0) + former Module 3 Pause & Reflect (slides 1-3); one syllabus step. */
  const renderAnikaBlock = () => {
    const base = process.env.PUBLIC_URL || '';
    const caseVideo = pickFlowModule(moduleId, ANIKA_CASE_STUDY_VIDEO, SARA_CASE_STUDY_VIDEO, AMAN_CASE_STUDY_VIDEO);
    const caseVideoSources = [
      resolvePublicOrAbsoluteUrl(base, caseVideo.localSrc),
      resolvePublicOrAbsoluteUrl(base, caseVideo.remoteSrc),
    ].filter((src, idx, arr) => Boolean(src) && arr.indexOf(src) === idx);
    const posterUrl = resolvePublicOrAbsoluteUrl(base, caseVideo.poster);
    const caseM2Slides = pickFlowModule(moduleId, WORKPLACE_M2_SLIDES, EDUCATOR_M2_SLIDES, COMMUNITY_M2_SLIDES);
    const slide = caseM2Slides[workplaceM2Slide] || caseM2Slides[0];
    const m2YoutubeId = slide.youtubeVideoId ? String(slide.youtubeVideoId).trim() : '';
    const m2YoutubeEmbedUrl = m2YoutubeId ? getIntroModuleYoutubeEmbedUrl(m2YoutubeId) : '';
    const useM2Youtube = Boolean(m2YoutubeEmbedUrl);
    const showM2VideoPlaceholder = Boolean(slide.videoPlaceholderText) && !useM2Youtube;
    /** Workplace keeps the previous case-study artwork (`images/thumb1.jpg`); other modules use YouTube poster when embedding. */
    const useCustomM2Poster = moduleId === 'workplace-module' && Boolean(posterUrl);
    const thumbPosterUrl = useCustomM2Poster
      ? posterUrl
      : useM2Youtube
        ? getYoutubePosterUrl(m2YoutubeId)
        : posterUrl;
    const closeM2VideoModal = () => {
      setWorkplaceM2VideoModalOpen(false);
      setWorkplaceM2YoutubePlayingModal(false);
    };
    const isFirst = workplaceM2Slide === 0;
    const isLast = workplaceM2Slide === caseM2Slides.length - 1;
    const m3ReflectionSlides = pickFlowModule(moduleId, WORKPLACE_M3_REFLECTION_SLIDES, EDUCATOR_M3_REFLECTION_SLIDES, COMMUNITY_M3_REFLECTION_SLIDES);
    const latestStoredWordNav = getLatestStoredReflectionWord();
    const hasExistingReflectionNav = reflectionSubmitted || Boolean(latestStoredWordNav);
    const reflectionIntroCanProceed =
      workplaceM3RevealCount >= m3ReflectionSlides.length
      && (hasExistingReflectionNav || Boolean((reflectionAnswers.reflection1 || '').trim()));
    const primaryNextDisabled = workplaceM2Slide === 1 && !reflectionIntroCanProceed;
    const isResearchReflectionSlide = workplaceM2Slide === caseM2Slides.length - 1;
    const outerClass =
      workplaceM2Slide >= 1
        ? `section-content workplace-m2-interactive reflection-interactive reflection-slides${isResearchReflectionSlide ? ' reflection-slides--research' : ''}`
        : 'section-content workplace-m2-interactive';
    return (
      <div className={outerClass}>
        {workplaceM2Slide === 0 && (() => {
          const { visibleLines, visibleBullets, showClosing, closingLine } = getM2SlideRevealParts(
            slide,
            Math.max(1, workplaceM2RevealCount)
          );
          return (
          <div key={`m2-slide-${workplaceM2Slide}`} className="workplace-m2-slide-card workplace-m2-slide-card--anim">
            <h3 className="workplace-m2-title">{slide.title}</h3>
            <div className="workplace-m2-layout">
              <div className="workplace-m2-content">
                <div className="workplace-m2-line-stack">
                  {visibleLines.map((line) => (
                    <p key={line} className="workplace-m2-line">{line}</p>
                  ))}
                  {visibleBullets.length > 0 && (
                    <ul className="workplace-m2-bullet-list">
                      {visibleBullets.map((item) => (
                        <li key={item} className="workplace-m2-line workplace-m2-bullet-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {showClosing && closingLine && (
                    <p key={closingLine} className="workplace-m2-line workplace-m2-closing-line">
                      {closingLine}
                    </p>
                  )}
                </div>
              </div>
              {showM2VideoPlaceholder ? (
                <div className="workplace-m2-video-text-placeholder" role="note">
                  <p>{slide.videoPlaceholderText}</p>
                </div>
              ) : (
              <button
                type="button"
                className="workplace-m2-lottie-trigger workplace-m2-video-thumb-trigger"
                onClick={() => {
                  setWorkplaceM2YoutubePlayingModal(false);
                  setWorkplaceM2VideoModalOpen(true);
                }}
                aria-label={isCommunityModule ? 'Open Aman case study video' : isEducatorModule ? 'Open Sara case study video' : 'Open Anika case study video'}
                title="Open video"
              >
                <img
                  src={thumbPosterUrl}
                  alt=""
                  className="workplace-m2-video-thumb"
                  draggable={false}
                  {...(useM2Youtube && !useCustomM2Poster ? youtubePosterImgHandlers : {})}
                  {...videoThumbProps}
                />
                <div className="workplace-m2-video-overlay" aria-hidden="true">
                  <span className="workplace-m2-video-play">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </div>
              </button>
              )}
            </div>
          </div>
          );
        })()}

        {workplaceM2Slide >= 1 && renderReflectionInner()}

        {workplaceM2VideoModalOpen && (
          <div className="anika-video-modal-layer" role="dialog" aria-modal="true" aria-label={isCommunityModule ? 'Aman case study video' : isEducatorModule ? 'Sara case study video' : 'Anika case study video'}>
            <button type="button" className="anika-video-modal-backdrop" onClick={closeM2VideoModal} aria-label="Close video modal" />
            <div className="anika-video-modal-card">
              <button
                type="button"
                className="anika-video-modal-close"
                onClick={closeM2VideoModal}
                aria-label="Close video"
              >
                ×
              </button>
              <h3 className="animation-visual-title">{caseVideo.title}</h3>
              <div className={`animation-visual-frame animation-visual-frame--video${useM2Youtube ? ' intro-video-modal-placeholder' : ''}`}>
                {useM2Youtube ? (
                  <IntroYoutubePosterPlay
                    embedUrl={m2YoutubeEmbedUrl}
                    videoId={m2YoutubeId}
                    posterSrc={useCustomM2Poster ? posterUrl : undefined}
                    posterImgProps={videoThumbProps}
                    isPlaying={workplaceM2YoutubePlayingModal}
                    onPlay={() => setWorkplaceM2YoutubePlayingModal(true)}
                    title={caseVideo.title}
                    mode="modal"
                  />
                ) : (
                  <video
                    className="animation-visual-video"
                    preload="metadata"
                    poster={posterUrl}
                    aria-label={isCommunityModule ? 'Aman case study video' : isEducatorModule ? 'Sara case study video' : 'Anika case study video'}
                    {...LOCAL_VIDEO_PLAYER_PROPS}
                  >
                    {caseVideoSources.map((src) => (
                      <source key={src} src={src} type="video/mp4" />
                    ))}
                    Your browser does not support embedded video playback.
                  </video>
                )}
              </div>
              {moduleId === 'workplace-module' && (
                <p className="module-video-footnote">{VIDEO_FOOTNOTE_TEXT}</p>
              )}
            </div>
          </div>
        )}

        <div className="workplace-m2-nav">
          <button
            type="button"
            className="workplace-m2-nav-btn"
            disabled={isFirst && !hasPrevSection('anika-story')}
            onClick={() => {
              if (!isFirst) {
                const next = workplaceM2Slide - 1;
                setWorkplaceM2Slide(next);
                if (next === 0) setWorkplaceM2RevealCount(1);
                return;
              }
              goToPrevSectionFrom('anika-story');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {caseM2Slides.map((_, idx) => (
              <span key={`m2-bubble-${idx}`} className={`module-slide-bubble ${workplaceM2Slide === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="workplace-m2-nav-btn workplace-m2-nav-btn--primary"
            disabled={primaryNextDisabled}
            onClick={() => {
              if (isLast) {
                goToNextSectionFrom('anika-story');
                return;
              }
              if (workplaceM2Slide === 1) {
                submitReflectionAndAdvance();
                return;
              }
              setWorkplaceM2Slide((s) => Math.min(caseM2Slides.length - 1, s + 1));
              if (workplaceM2Slide === 0) setWorkplaceM2RevealCount(1);
            }}
          >
            {isLast ? 'Next module' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  /** Module 5 (Meta-Reflexivity): framework + workplace poll or educator private note. */
  const renderMetaReflexivityBlock = () => {
    const metaReflexivityPdfFilename = isCommunityModule
      ? COMMUNITY_META_REFLEXIVITY_PDF_FILENAME
      : isEducatorModule
        ? EDUCATOR_META_REFLEXIVITY_PDF_FILENAME
        : META_REFLEXIVITY_PDF_FILENAME;
    const pdfUrl = `${process.env.PUBLIC_URL || ''}/${metaReflexivityPdfFilename}`;
    const m5slides = pickFlowModule(moduleId, WORKPLACE_M5_SLIDES, EDUCATOR_M5_SLIDES, COMMUNITY_M5_SLIDES);
    const m5Header = isCommunityModule ? COMMUNITY_M5_HEADER : isEducatorModule ? EDUCATOR_M5_HEADER : WORKPLACE_M5_HEADER;
    const slide = m5slides[workplaceM5Slide] || m5slides[0];
    const isFirst = workplaceM5Slide === 0;
    const isLast = workplaceM5Slide === m5slides.length - 1;
    const m5RevealTarget =
      !isFiveBarrierModule && workplaceM5Slide === 0
        ? Math.min(2, slide.lines.length)
        : slide.lines.length;
    const canGoNext = slide.lines.length ? workplaceM5RevealCount >= m5RevealTarget : true;
    const metaReflexivityPollTotal =
      metaReflexivityPoll && metaReflexivityPollStatus === 'ready'
        ? Math.max(0, Number(metaReflexivityPoll.total ?? 0))
        : 0;
    const m5PollResultsLayout = !isFiveBarrierModule && workplaceM5Slide === 1 && workplaceM5InputStep === 2;

    return (
      <div
        className={`section-content meta-reflexivity-block meta-reflexivity-slides${m5PollResultsLayout ? ' meta-reflexivity-slides--m5-poll-results' : ''}`}
      >
        {metaReflexivityCelebrate && (
          <div className="meta-reflexivity-lottie-layer" aria-hidden="true">
            {badgeAnimationData && (
              <div className="meta-reflexivity-lottie-card">
                <Lottie
                  animationData={badgeAnimationData}
                  loop={false}
                  autoplay
                  className="meta-reflexivity-lottie-player"
                />
                <p className="meta-reflexivity-lottie-text">Response submitted successfully</p>
              </div>
            )}
          </div>
        )}
        <div
          key={`m5-slide-${workplaceM5Slide}`}
          className={`meta-reflexivity-slide-card content-enter${
            workplaceM5Slide === 0 && !isFiveBarrierModule ? ' meta-reflexivity-slide-card--m5-video-split' : ''
          }${workplaceM5Slide === 1 ? ' meta-reflexivity-slide-card--m5-slide-2' : ''}${
            isFiveBarrierModule && workplaceM5Slide === 1 ? ' meta-reflexivity-slide-card--m5-educator-note' : ''
          }`}
        >
          <h3 className="meta-reflexivity-heading">{m5Header || slide.title}</h3>

          {workplaceM5Slide === 0 && isFiveBarrierModule && (
            <>
              {slide.lines?.length > 0 && (
                <div className="meta-reflexivity-header-lines">
                  {slide.lines
                    .slice(0, Math.max(1, workplaceM5RevealCount))
                    .map((line) => renderMetaReflexivityTextLine(line))}
                </div>
              )}
              <div className="meta-reflexivity-layout meta-reflexivity-layout--with-video">
                <div className="meta-reflexivity-layout-content">
                  <div className="meta-reflexivity-m5-video-shell">
                    <IntroYoutubePosterPlay
                      embedUrl={getIntroModuleYoutubeEmbedUrl(WORKPLACE_M5_SLIDE0_YOUTUBE_VIDEO_ID, { captions: false })}
                      videoId={WORKPLACE_M5_SLIDE0_YOUTUBE_VIDEO_ID}
                      posterImgProps={contentImageProps({
                        width: 1280,
                        height: 720,
                        loading: 'eager',
                        fetchPriority: 'high',
                      })}
                      isPlaying={workplaceM5Slide0VideoPlaying}
                      onPlay={() => setWorkplaceM5Slide0VideoPlaying(true)}
                      title={
                        isCommunityModule
                          ? 'Community module meta-reflexivity overview video'
                          : 'Educator module meta-reflexivity overview video'
                      }
                      mode="modal"
                    />
                  </div>
                </div>
                <div className="meta-reflexivity-checklist-wrap">
                  <Lottie
                    animationData={pdfAnimationData}
                    loop={false}
                    autoplay
                    className="meta-reflexivity-checklist-lottie"
                  />
                  <div className="meta-reflexivity-download-wrap meta-reflexivity-download-wrap--under-animation">
                    <a
                      href={pdfUrl}
                      download={metaReflexivityPdfFilename}
                      className="meta-reflexivity-download-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg className="meta-reflexivity-download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download PDF
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}

          {workplaceM5Slide === 0 && !isFiveBarrierModule && (
            <div className="meta-reflexivity-layout meta-reflexivity-layout--with-video meta-reflexivity-layout--reverse meta-reflexivity-m5-slide1-layout">
              <div className="meta-reflexivity-checklist-wrap meta-reflexivity-m5-download-column">
                {pdfAnimationData && (
                  <Lottie
                    animationData={pdfAnimationData}
                    loop={false}
                    autoplay
                    className="meta-reflexivity-checklist-lottie"
                  />
                )}
                <div className="meta-reflexivity-download-wrap meta-reflexivity-download-wrap--under-animation">
                  <a
                    href={pdfUrl}
                    download={metaReflexivityPdfFilename}
                    className="meta-reflexivity-download-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="meta-reflexivity-download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download PDF
                  </a>
                </div>
              </div>
              <div className="meta-reflexivity-layout-content">
                <div className="meta-reflexivity-m5-video-shell">
                  <IntroYoutubePosterPlay
                    embedUrl={getIntroModuleYoutubeEmbedUrl(WORKPLACE_M5_SLIDE0_YOUTUBE_VIDEO_ID, { captions: false })}
                    videoId={WORKPLACE_M5_SLIDE0_YOUTUBE_VIDEO_ID}
                    posterImgProps={contentImageProps({
                      width: 1280,
                      height: 720,
                      loading: 'eager',
                      fetchPriority: 'high',
                    })}
                    isPlaying={workplaceM5Slide0VideoPlaying}
                    onPlay={() => setWorkplaceM5Slide0VideoPlaying(true)}
                    title="Building Workplace Bridges meta-reflexivity overview video"
                    mode="modal"
                  />
                </div>
              </div>
            </div>
          )}

          {workplaceM5Slide === 1 && isFiveBarrierModule && (
            <div className="meta-reflexivity-m5-educator-note-body">
              <div className="meta-reflexivity-m5-educator-note-body-inner">
                <div
                  className={
                    isEducatorModule
                      ? 'meta-reflexivity-layout meta-reflexivity-m5-educator-slide2-layout meta-reflexivity-m5-educator-slide2-layout--text-only'
                      : 'meta-reflexivity-layout meta-reflexivity-layout--with-video meta-reflexivity-m5-educator-slide2-layout'
                  }
                >
                  <div className={`meta-reflexivity-layout-content${isEducatorModule ? ' meta-reflexivity-m5-slide2-reading' : ''}`}>
                    <div className="meta-reflexivity-header-lines">
                      <div className="meta-reflexivity-line-stack">
                        {slide.introLine ? (
                          <p className="meta-reflexivity-line meta-reflexivity-m5-slide2-intro">{slide.introLine}</p>
                        ) : null}
                        {slide.title && slide.lines?.[0] !== slide.title ? (
                          <h4 className="meta-reflexivity-slide-subtitle">{slide.title}</h4>
                        ) : null}
                        {(slide.lines || [])
                          .filter((line) => !(typeof line === 'string' && slide.title && line.trim() === slide.title.trim()))
                          .map((line) => renderMetaReflexivityTextLine(line))}
                        {slide.bullets?.length > 0 && (
                          <ul className="meta-reflexivity-bullet-list">
                            {slide.bullets.map((item) => (
                              <li key={item} className="meta-reflexivity-line meta-reflexivity-bullet-item">
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                        {slide.closingLine ? (
                          <p className="meta-reflexivity-line meta-reflexivity-m5-slide2-closing">{slide.closingLine}</p>
                        ) : null}
                        {slide.closingBullets?.length > 0 && (
                          <ul className="meta-reflexivity-bullet-list meta-reflexivity-m5-slide2-closing-bullets">
                            {slide.closingBullets.map((item) => (
                              <li key={item} className="meta-reflexivity-line meta-reflexivity-bullet-item">
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                  {!isEducatorModule && (
                    <div className="meta-reflexivity-checklist-wrap meta-reflexivity-checklist-wrap--download-only">
                      <div className="meta-reflexivity-download-wrap">
                        <a
                          href={pdfUrl}
                          download={metaReflexivityPdfFilename}
                          className="meta-reflexivity-download-btn"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="meta-reflexivity-download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download PDF
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {workplaceM5Slide === 1 && !isFiveBarrierModule && (
            <>
              {workplaceM5InputStep !== 2 && (
                <div className="meta-reflexivity-header-lines">
                  <div className="meta-reflexivity-line-stack">
                    {slide.lines.slice(0, Math.max(1, workplaceM5RevealCount)).map((line) => (
                      <p key={line} className="meta-reflexivity-line">{line}</p>
                    ))}
                  </div>
                </div>
              )}
              <div className={`meta-reflexivity-layout meta-reflexivity-layout--reverse ${workplaceM5InputStep === 2 ? 'meta-reflexivity-layout--submitted' : ''}`}>
                {workplaceM5InputStep !== 2 && (
                  <div className="meta-reflexivity-option-wrap" aria-hidden="true">
                    <Lottie
                      animationData={checklistAnimationData}
                      loop
                      autoplay
                      className="meta-reflexivity-checklist-lottie"
                    />
                  </div>
                )}
                <div className="meta-reflexivity-layout-content">
                  <div className="meta-reflexivity-inline-form">
                    {workplaceM5InputStep !== 2 && (
                      <div className="meta-reflexivity-prompt-form">
                        <p className="meta-reflexivity-prompt-label">Select all that apply</p>
                        <div
                          className="meta-reflexivity-option-grid"
                          role="group"
                          aria-label="Meta reflexivity moment selection (multi-select)"
                        >
                          {META_REFLEXIVITY_MOMENT_OPTIONS.map(({ value, label }) => {
                            const checked = metaReflexivityMomentTypes.includes(value);
                            return (
                              <button
                                key={value}
                                type="button"
                                role="checkbox"
                                aria-checked={checked}
                                className={`meta-reflexivity-option-btn ${checked ? 'meta-reflexivity-option-btn--active' : ''}`}
                                onClick={() => toggleMetaReflexivityMomentType(value)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        {metaReflexivityMomentTypes.includes('other') && (
                          <div className="meta-reflexivity-option-other-wrap">
                            <label htmlFor="meta-reflexivity-moment-other" className="meta-reflexivity-prompt-label">Please specify (optional)</label>
                            <input
                              type="text"
                              id="meta-reflexivity-moment-other"
                              className="meta-reflexivity-prompt-input"
                              placeholder="Describe your example or moment"
                              value={metaReflexivityMomentOther}
                              onChange={(e) => setMetaReflexivityMomentOther(e.target.value)}
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          className="meta-reflexivity-prompt-submit"
                          onClick={handleMetaReflexivitySurveySubmit}
                          disabled={metaReflexivityMomentTypes.length === 0}
                        >
                          Submit
                        </button>
                      </div>
                    )}

                    {workplaceM5InputStep === 2 && (
                      <div className="meta-reflexivity-poll" aria-live="polite">
                        {metaReflexivityPollStatus === 'loading' && (
                          <p className="meta-reflexivity-poll-status">Thank you. Loading anonymous results…</p>
                        )}
                        {metaReflexivityPollStatus === 'error' && (
                          <p className="meta-reflexivity-poll-status meta-reflexivity-poll-status--error">
                            Results could not be loaded. Your response was still saved.
                          </p>
                        )}
                        {metaReflexivityPollStatus === 'ready' && metaReflexivityPoll && (
                          <>
                            <p className="meta-reflexivity-poll-intro">
                              {metaReflexivityPoll.source === 'local-only'
                                ? 'Thank you. Full community totals are unavailable . Your response is shown below.'
                                : 'Thank you. Here is how participants responded  .'}
                            </p>
                            <ul className="meta-reflexivity-poll-list">
                              {META_REFLEXIVITY_MOMENT_OPTIONS.map(({ value, label }) => {
                                const count = Number(metaReflexivityPoll.counts?.[value] ?? 0);
                                const pct = metaReflexivityPollTotal > 0
                                  ? Math.round((count / metaReflexivityPollTotal) * 100)
                                  : 0;
                                const isYou = metaReflexivityUserChoice?.momentTypes?.includes(value);
                                return (
                                  <li key={value} className={`meta-reflexivity-poll-row ${isYou ? 'meta-reflexivity-poll-row--you' : ''}`}>
                                    <div className="meta-reflexivity-poll-row-header">
                                      <span className="meta-reflexivity-poll-label">
                                        {label}
                                        {isYou ? <span className="meta-reflexivity-poll-you"> (your selection)</span> : null}
                                      </span>
                                      <span className="meta-reflexivity-poll-count">
                                        {count}
                                        <span className="meta-reflexivity-poll-pct"> ({pct}%)</span>
                                      </span>
                                    </div>
                                    <div className="meta-reflexivity-poll-bar-track" aria-hidden="true">
                                      <div className="meta-reflexivity-poll-bar-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                            {metaReflexivityUserChoice?.momentTypes?.includes('other') && metaReflexivityUserChoice?.otherText ? (
                              <p className="meta-reflexivity-poll-your-other">
                                <span className="meta-reflexivity-poll-your-other-label">You added:</span>
                                {' '}
                                {metaReflexivityUserChoice.otherText}
                              </p>
                            ) : null}
                            <p className="meta-reflexivity-poll-total">
                              {metaReflexivityPollTotal} total response{metaReflexivityPollTotal === 1 ? '' : 's'}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="meta-reflexivity-slide-nav">
          <button
            type="button"
            className="meta-reflexivity-slide-nav-btn"
            disabled={isFirst && !hasPrevSection('meta-reflexivity')}
            onClick={() => {
              if (!isFirst) {
                setWorkplaceM5Slide((s) => Math.max(0, s - 1));
                setWorkplaceM5RevealCount(1);
                return;
              }
              goToPrevSectionFrom('meta-reflexivity');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {m5slides.map((_, idx) => (
              <span key={`m5-bubble-${idx}`} className={`module-slide-bubble ${workplaceM5Slide === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="meta-reflexivity-slide-nav-btn meta-reflexivity-slide-nav-btn--primary"
            disabled={isLast ? false : !canGoNext}
            onClick={() => {
              if (isLast) {
                goToNextSectionFrom('meta-reflexivity');
                return;
              }
              setWorkplaceM5Slide((s) => Math.min(m5slides.length - 1, s + 1));
              setWorkplaceM5RevealCount(1);
            }}
          >
            {isLast ? 'Next module' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  /** Module 6: Revisiting story - workplace (Anika) or educator (Sara). */
  const renderMetaReflexiveManagerBlock = () => {
    const base = process.env.PUBLIC_URL || '';
    const revVideo = pickFlowModule(moduleId, REVISITING_ANIKA_STORY_VIDEO, REVISITING_SARA_STORY_VIDEO, REVISITING_AMAN_STORY_VIDEO);
    const revisitingVideoSources = [
      resolvePublicOrAbsoluteUrl(base, revVideo.localSrc),
      resolvePublicOrAbsoluteUrl(base, revVideo.remoteSrc),
    ].filter((src, idx, arr) => Boolean(src) && arr.indexOf(src) === idx);
    const revisitingPosterUrl = resolvePublicOrAbsoluteUrl(base, revVideo.poster);
    const m6slides = pickFlowModule(moduleId, WORKPLACE_M6_SLIDES, EDUCATOR_M6_SLIDES, COMMUNITY_M6_SLIDES);
    const slide = m6slides[workplaceM6Slide] || m6slides[0];
    const m6YoutubeId = slide.youtubeVideoId ? String(slide.youtubeVideoId).trim() : '';
    const m6YoutubeEmbedUrl = m6YoutubeId ? getIntroModuleYoutubeEmbedUrl(m6YoutubeId) : '';
    const useM6Youtube = Boolean(m6YoutubeEmbedUrl);
    const showM6VideoPlaceholder = Boolean(slide.videoPlaceholderText) && !useM6Youtube;
    /** Workplace keeps previous Revisiting Anika artwork (`images/thumb2.jpg`). */
    const useCustomM6Poster = moduleId === 'workplace-module' && Boolean(revisitingPosterUrl);
    const m6ThumbPosterUrl = useCustomM6Poster
      ? revisitingPosterUrl
      : useM6Youtube
        ? getYoutubePosterUrl(m6YoutubeId)
        : revisitingPosterUrl;
    const closeM6VideoModal = () => {
      setWorkplaceM6VideoModalOpen(false);
      setWorkplaceM6YoutubePlayingModal(false);
    };
    const m6VideoAriaLabel = isCommunityModule
      ? 'Revisiting Aman story video'
      : isEducatorModule
        ? 'Revisiting Sara story video'
        : 'Revisiting Anika story video';
    const isFirst = workplaceM6Slide === 0;
    const isLast = workplaceM6Slide === m6slides.length - 1;
    const linesBeforeTitle = slide.linesBeforeTitle || [];
    const slideMainLines = slide.lines || [];
    const slideStages = slide.stages || [];
    const postStagesLines = slide.postStagesLines || [];
    const closingLines = slide.closingLines || [];
    const revealTarget =
      linesBeforeTitle.length
      + slideMainLines.length
      + slideStages.length
      + postStagesLines.length
      + closingLines.length;
    const canGoNext = revealTarget ? workplaceM6RevealCount >= revealTarget : true;
    const revealedBeforeTitle = linesBeforeTitle.slice(
      0,
      Math.min(linesBeforeTitle.length, workplaceM6RevealCount)
    );
    const afterBeforeCount = Math.max(0, workplaceM6RevealCount - linesBeforeTitle.length);
    const revealedMainLines = slideMainLines.slice(
      0,
      Math.min(slideMainLines.length, afterBeforeCount)
    );
    const stageRevealCount = Math.max(0, afterBeforeCount - slideMainLines.length);
    const revealedStages = slideStages.slice(0, Math.min(slideStages.length, stageRevealCount));
    const afterStagesCount = Math.max(
      0,
      workplaceM6RevealCount - linesBeforeTitle.length - slideMainLines.length - slideStages.length
    );
    const revealedPostStagesLines = postStagesLines.slice(
      0,
      Math.min(postStagesLines.length, afterStagesCount)
    );
    const afterPostStagesCount = Math.max(0, afterStagesCount - postStagesLines.length);
    const revealedClosingLines = closingLines.slice(0, afterPostStagesCount);
    const showManagerShiftTitle =
      Boolean(slide.title) && (linesBeforeTitle.length === 0 || workplaceM6RevealCount > linesBeforeTitle.length);
    const isManagerShiftSlide = Boolean(slide.videoLayout);
    return (
      <div className="section-content meta-reflexivity-block meta-reflexivity-manager-slides">
        <div
          key={`m6-slide-${workplaceM6Slide}`}
          className={`meta-reflexivity-manager-card content-enter${isManagerShiftSlide ? '' : ' meta-reflexivity-manager-card--m6-text'}`}
        >
          {isManagerShiftSlide ? (
            <>
              <h3 className="workplace-m2-title">{slide.title}</h3>
              <div className="workplace-m2-layout">
                <div className="workplace-m2-content">
                  <div className="workplace-m2-line-stack">
                    {revealedMainLines.map((line, i) =>
                      line && typeof line === 'object' && line.html ? (
                        <p
                          key={`m6-vid-${i}-${metaReflexivityLineKey(line)}`}
                          className="workplace-m2-line"
                          dangerouslySetInnerHTML={{ __html: line.html }}
                        />
                      ) : (
                        <p key={metaReflexivityLineKey(line)} className="workplace-m2-line">
                          {line}
                        </p>
                      )
                    )}
                  </div>
                </div>
                {showM6VideoPlaceholder ? (
                  <div className="workplace-m2-video-text-placeholder" role="note">
                    <p>{slide.videoPlaceholderText}</p>
                  </div>
                ) : (
                <button
                  type="button"
                  className="workplace-m2-lottie-trigger workplace-m2-video-thumb-trigger"
                  onClick={() => {
                    setWorkplaceM6YoutubePlayingModal(false);
                    setWorkplaceM6VideoModalOpen(true);
                  }}
                  aria-label={isCommunityModule ? 'Open revisiting Aman story video' : isEducatorModule ? 'Open revisiting Sara story video' : 'Open revisiting Anika story video'}
                  title="Open video"
                >
                  <img
                    src={m6ThumbPosterUrl}
                    alt=""
                    className="workplace-m2-video-thumb"
                    draggable={false}
                    {...(useM6Youtube && !useCustomM6Poster ? youtubePosterImgHandlers : {})}
                    {...videoThumbProps}
                  />
                  <div className="workplace-m2-video-overlay" aria-hidden="true">
                    <span className="workplace-m2-video-play">
                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </div>
                </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="meta-reflexivity-manager-header">
                {revealedBeforeTitle.length > 0 && (
                  slide.beforeTitleLayout === 'what-you-saw' ? (
                    <div className="m6-what-you-saw-tight">
                      {revealedBeforeTitle.length >= 1 ? (
                        <h3 className="meta-reflexivity-story-heading meta-reflexivity-story-heading--wys">
                          {revealedBeforeTitle[0]}
                        </h3>
                      ) : null}
                      {revealedBeforeTitle.length >= 2 ? (
                        <p className="m6-wys-lead">
                          {(() => {
                            const lead = revealedBeforeTitle[1];
                            const parts = lead.split('intentional');
                            if (parts.length === 2) {
                              return (
                                <>
                                  {parts[0]}
                                  <strong>intentional</strong>
                                  {parts[1]}
                                </>
                              );
                            }
                            return lead;
                          })()}
                        </p>
                      ) : null}
                      {revealedBeforeTitle.length >= 3 ? <p className="m6-wys-list-intro">{revealedBeforeTitle[2]}</p> : null}
                      {revealedBeforeTitle.length >= 4 ? (
                        <ul className="m6-wys-list">
                          {revealedBeforeTitle.slice(3).map((item, j) => (
                            <li key={`m6-wys-li-${j}`} className="m6-wys-list-item">
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : (
                    <div className="meta-reflexivity-manager-lines meta-reflexivity-manager-lines--before-title">
                      {revealedBeforeTitle.map((line, i) => (
                        <p key={`m6-before-${workplaceM6Slide}-${i}`} className="meta-reflexivity-body meta-reflexivity-manager-line">
                          {line}
                        </p>
                      ))}
                    </div>
                  )
                )}
                {showManagerShiftTitle ? (
                  <h3 className="meta-reflexivity-story-heading">{slide.title}</h3>
                ) : null}
              </div>
              <div className="meta-reflexivity-manager-body">
                <div className="meta-reflexivity-manager-body-inner">
                  {revealedMainLines.length > 0 && (
                    <div className="meta-reflexivity-manager-lines">
                      {revealedMainLines.map((line, i) => (
                        <p key={`m6-main-${workplaceM6Slide}-${i}`} className="meta-reflexivity-body meta-reflexivity-manager-line">
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                  {revealedStages.length > 0 && (
                    <div className="meta-reflexivity-manager-lines meta-reflexivity-manager-lines--stages">
                      {slide.stagesSectionTitle ? (
                        <h4 className="meta-reflexivity-manager-section-heading">{slide.stagesSectionTitle}</h4>
                      ) : null}
                      {revealedStages.map((stage) => renderManagerStageBlock(stage))}
                    </div>
                  )}
                  {revealedPostStagesLines.length > 0 && (
                    <div className="meta-reflexivity-manager-lines meta-reflexivity-manager-lines--post-stages">
                      {revealedPostStagesLines.map((line, i) => (
                        <p key={`m6-post-${workplaceM6Slide}-${i}`} className="meta-reflexivity-body meta-reflexivity-manager-line">
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                  {revealedClosingLines.length > 0 && (
                    <div className="meta-reflexivity-manager-outcome">
                      {slide.resultSectionTitle ? (
                        <h4 className="meta-reflexivity-manager-section-heading meta-reflexivity-manager-section-heading--result">
                          {slide.resultSectionTitle}
                        </h4>
                      ) : null}
                      {revealedClosingLines.map((line, i) => (
                        <p key={`m6-closing-${workplaceM6Slide}-${i}`} className="meta-reflexivity-manager-outcome-line">
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {workplaceM6VideoModalOpen && (
          <div className="anika-video-modal-layer" role="dialog" aria-modal="true" aria-label={m6VideoAriaLabel}>
            <button type="button" className="anika-video-modal-backdrop" onClick={closeM6VideoModal} aria-label="Close video modal" />
            <div className="anika-video-modal-card">
              <button
                type="button"
                className="anika-video-modal-close"
                onClick={closeM6VideoModal}
                aria-label="Close video"
              >
                ×
              </button>
              <h3 className="animation-visual-title">{revVideo.title}</h3>
              <div className={`animation-visual-frame animation-visual-frame--video${useM6Youtube ? ' intro-video-modal-placeholder' : ''}`}>
                {useM6Youtube ? (
                  <IntroYoutubePosterPlay
                    embedUrl={m6YoutubeEmbedUrl}
                    videoId={m6YoutubeId}
                    posterSrc={useCustomM6Poster ? revisitingPosterUrl : undefined}
                    posterImgProps={videoThumbProps}
                    isPlaying={workplaceM6YoutubePlayingModal}
                    onPlay={() => setWorkplaceM6YoutubePlayingModal(true)}
                    title={revVideo.title}
                    mode="modal"
                  />
                ) : (
                  <video
                    className="animation-visual-video"
                    preload="metadata"
                    poster={revisitingPosterUrl}
                    aria-label={m6VideoAriaLabel}
                    {...LOCAL_VIDEO_PLAYER_PROPS}
                  >
                    {revisitingVideoSources.map((src) => (
                      <source key={src} src={src} type="video/mp4" />
                    ))}
                    Your browser does not support embedded video playback.
                  </video>
                )}
              </div>
              {moduleId === 'workplace-module' && (
                <p className="module-video-footnote">{VIDEO_FOOTNOTE_TEXT}</p>
              )}
            </div>
          </div>
        )}

        <div className="meta-reflexivity-manager-nav">
          <button
            type="button"
            className="meta-reflexivity-manager-nav-btn"
            disabled={isFirst && !hasPrevSection('meta-reflexive-manager')}
            onClick={() => {
              if (!isFirst) {
                setWorkplaceM6Slide((s) => Math.max(0, s - 1));
                setWorkplaceM6RevealCount(1);
                return;
              }
              goToPrevSectionFrom('meta-reflexive-manager');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {m6slides.map((_, idx) => (
              <span key={`m6-bubble-${idx}`} className={`module-slide-bubble ${workplaceM6Slide === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="meta-reflexivity-manager-nav-btn meta-reflexivity-manager-nav-btn--primary"
            disabled={isLast ? false : !canGoNext}
            onClick={() => {
              if (isLast) {
                goToNextSectionFrom('meta-reflexive-manager');
                return;
              }
              setWorkplaceM6Slide((s) => Math.min(m6slides.length - 1, s + 1));
              setWorkplaceM6RevealCount(1);
            }}
          >
            {isLast ? 'Next module' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  const renderWorkplaceSurveyBlock = () => {
    const optionalRoleOptions = [
      { value: 'teamLead', label: 'Team lead / project lead' },
      { value: 'managerSupervisor', label: 'Manager / supervisor' },
      { value: 'seniorLeaderExecutive', label: 'Senior leader / executive' },
      { value: 'informalLeaderMentorChangeAgent', label: 'Informal leader / mentor / change agent' },
      { value: 'other', label: 'Other' },
      { value: 'preferNotToSay', label: 'Prefer not to say' },
    ];
    const optionalYearsExperienceOptions = [
      { value: 'under5', label: 'Under 5' },
      { value: '6to10', label: '6-10' },
      { value: '11to15', label: '11-15' },
      { value: '16plus', label: '16+' },
      { value: 'preferNotToSay', label: 'Prefer not to say' },
    ];
    const optionalWorkContextOptions = [
      { value: 'scienceResearch', label: 'Science / research' },
      { value: 'technologyDigital', label: 'Technology / digital' },
      { value: 'engineering', label: 'Engineering' },
      { value: 'mathAccountingFinanceAnalytics', label: 'Math / accounting and finance / analytics' },
      { value: 'medicineHealth', label: 'Medicine / health' },
      { value: 'educationAcademia', label: 'Education / academia' },
      { value: 'nonProfitCommunity', label: 'Non-profit / community sector' },
      { value: 'publicSectorGovernment', label: 'Public sector / government' },
      { value: 'privateSectorCorporate', label: 'Private sector / corporate' },
      { value: 'other', label: 'Other' },
      { value: 'preferNotToSay', label: 'Prefer not to say' },
    ];
    const surveySteps = [
      {
        type: 'intro',
        title: isParentModule ? PARENT_SURVEY_INTRO.title : 'Building Workplace Bridges Learning Module - Survey',
        body: isParentModule ? PARENT_SURVEY_INTRO.body : 'As a participant in the Building Workplace Bridges Learning module, we are interested in understanding what you gained from participating and your overall experience.',
        pausePrompt: isParentModule
          ? PARENT_SURVEY_INTRO.pausePrompt
          : 'Please pause before you continue. Take a moment to think about your participation before you answer the questions-and how you may reflect on it after you finish. Several items ask for your views both before and after the module.',
      },
      ...SURVEY_AWARENESS_STATEMENTS.map((item) => ({
        type: 'awareness-question',
        statement: item,
        sectionTitle: 'Section 1: Understanding What You Gained',
      })),
      ...getSurveyExperienceStatementsForModule(moduleId).map((item) => ({
        type: 'experience-question',
        statement: item,
        sectionTitle: 'Section 2: Experience in the Session',
      })),
      {
        type: 'feedback',
        title: 'Additional Feedback',
      },
      {
        type: 'optional-followup',
        title: 'Would you like to take part in a 1-1 follow-up interview to share your experiences in more detail?',
        body:
          'If you select Yes, our team may contact you in approximately three months to invite you to an optional one-on-one interview. Selecting Yes does not mean you will definitely be contacted; we may reach out to a subset of participants depending on study needs and capacity.',
      },
      {
        type: 'optional-identity',
        title: WORKPLACE_EDI_IDENTITY_TITLE,
        body: WORKPLACE_EDI_IDENTITY_INTRO_BODY,
      },
      {
        type: 'optional-role',
        title: 'Which best describes your current role?',
      },
      {
        type: 'optional-years-experience',
        title: 'Approximately how many years of professional experience do you have?',
      },
      {
        type: 'optional-work-context',
        title: 'Which best describes your primary work context?',
      },
      {
        type: 'submitted',
        title: isParentModule
          ? PARENT_SURVEY_SUBMITTED_TITLE
          : 'Thank you for participating in the Building Workplace Bridges Module!',
      },
    ];

    const totalSteps = surveySteps.length;
    const surveyReviewLocked = isModuleCompleted || surveySubmitted;
    const lastSurveyStepIndex = totalSteps - 1;
    const clampedStep = surveyReviewLocked
      ? lastSurveyStepIndex
      : Math.max(0, Math.min(workplaceSurveyStep, lastSurveyStepIndex));
    const step = surveySteps[clampedStep];
    const isFirst = clampedStep === 0;
    const isLast = clampedStep === totalSteps - 1;
    const isOptionalContextStep = typeof step?.type === 'string' && step.type.startsWith('optional-');
    const surveyOptional = surveyResponses.optional || {};
    const optionalDemographicsRequired = isOptionalDemographicsRequired(surveyOptional);
    const isMandatoryDemographicsStep =
      optionalDemographicsRequired && OPTIONAL_DEMOGRAPHIC_STEP_TYPES.has(step?.type);
    const identitySelections = Array.isArray(surveyResponses.optional?.identitySelections)
      ? surveyResponses.optional.identitySelections
      : [];
    const progressPct = Math.round(((clampedStep + 1) / totalSteps) * 100);

    const currentStepComplete = (() => {
      if (!step) return false;
      if (step.type === 'awareness-question') {
        const id = step.statement.id;
        return Number(surveyResponses.awarenessBefore?.[id]) >= 1 && Number(surveyResponses.awarenessAfter?.[id]) >= 1;
      }
      if (step.type === 'experience-question') {
        const selected = Number(surveyResponses.experience?.[step.statement.id]);
        if (SURVEY_EXPERIENCE_NA_IDS.has(step.statement.id) && selected === 0) return true;
        return selected >= 1;
      }
      if (typeof step.type === 'string' && step.type.startsWith('optional-')) {
        return isOptionalSurveyStepComplete(step, surveyOptional);
      }
      return true;
    })();
    const isSubmittedStep = step?.type === 'submitted';

    const renderSurveyStepRequiredHint = () => {
      if (isSubmittedStep || isLast || currentStepComplete) return null;
      if (step.type === 'feedback') {
        if (isSurveyComplete) return null;
        return (
          <p className="workplace-survey-required" role="status">
            Please complete all required rating questions before submitting.
          </p>
        );
      }
      if (step.type === 'intro' || step.type === 'optional-identity' || step.type === 'submitted') return null;
      let message;
      if (step.type === 'optional-followup') {
        message = 'Please select Yes or No to continue.';
      } else if (isMandatoryDemographicsStep) {
        message = 'Please answer this question to continue.';
      } else if (step.type === 'awareness-question' || step.type === 'experience-question') {
        message = 'Please select a rating to continue.';
      } else {
        return null;
      }
      return (
        <p className="workplace-survey-required" role="status">
          {message}
        </p>
      );
    };

    const renderSurveyRequiredSlot = () => (
      <div className="workplace-survey-required-slot" aria-live="polite">
        {renderSurveyStepRequiredHint()}
      </div>
    );

    const renderScaleOptions = (selectedValue, onSelect, labelPrefix, includeNa = false) => (
      <div className="workplace-survey-scale-row" role="radiogroup" aria-label={labelPrefix}>
        {[...(includeNa ? [{ value: 0, label: 'N/A', fullLabel: 'Not applicable' }] : []), ...SURVEY_SCALE_HEADERS].map((scale) => (
          <button
            key={`${labelPrefix}-${scale.value}`}
            type="button"
            role="radio"
            aria-checked={Number(selectedValue) === scale.value}
            className={`workplace-survey-scale-pill ${Number(selectedValue) === scale.value ? 'workplace-survey-scale-pill--active' : ''}`}
            onClick={onSelect(scale.value)}
          >
            <span className="workplace-survey-scale-pill-value">{scale.value === 0 ? '' : scale.value}</span>
            <span className="workplace-survey-scale-pill-label">{scale.fullLabel}</span>
          </button>
        ))}
      </div>
    );
    const renderOptionalChoiceOptions = (options, selectedValue, onSelect, labelPrefix, layoutClass = '') => (
      <div
        className={`workplace-survey-scale-row workplace-survey-scale-row--optional${layoutClass ? ` ${layoutClass}` : ''}`}
        role="radiogroup"
        aria-label={labelPrefix}
      >
        {options.map((option) => (
          <button
            key={`${labelPrefix}-${option.value}`}
            type="button"
            role="radio"
            aria-checked={selectedValue === option.value}
            className={`workplace-survey-scale-pill workplace-survey-scale-pill--optional ${selectedValue === option.value ? 'workplace-survey-scale-pill--active' : ''}`}
            onClick={() => onSelect(option.value)}
          >
            <span className="workplace-survey-scale-pill-label">{option.label}</span>
          </button>
        ))}
      </div>
    );
    const setSurveyOptional = (key, value) => {
      setSurveyResponses((prev) => ({
        ...prev,
        optional: {
          ...(prev.optional || {}),
          [key]: value,
        },
      }));
    };
    const toggleIdentitySelection = (value) => {
      setSurveyResponses((prev) => {
        const current = Array.isArray(prev.optional?.identitySelections) ? prev.optional.identitySelections : [];
        if (value === 'preferNotToAnswer') {
          const next = current.includes(value) ? [] : ['preferNotToAnswer'];
          return {
            ...prev,
            optional: {
              ...(prev.optional || {}),
              identitySelections: next,
              identityOther: next.includes('preferNotToAnswer') ? '' : prev.optional?.identityOther || '',
            },
          };
        }
        const withoutPrefer = current.filter((v) => v !== 'preferNotToAnswer');
        const next = withoutPrefer.includes(value)
          ? withoutPrefer.filter((v) => v !== value)
          : [...withoutPrefer, value];
        return {
          ...prev,
          optional: {
            ...(prev.optional || {}),
            identitySelections: next,
            identityOther: value === 'another' && !next.includes('another') ? '' : prev.optional?.identityOther || '',
          },
        };
      });
    };

    return (
      <div
        className={`section-content workplace-survey-slides${surveyReviewLocked ? ' workplace-survey-slides--review-locked' : ''}`}
      >
        <div className="workplace-survey-progress" aria-label={`Survey progress ${progressPct}%`}>
          <div className="workplace-survey-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="workplace-survey-step-count">
          {getSurveyStepCounterLabel(step, clampedStep, surveySteps, surveyOptional.followupInterview === 'yes')}
        </p>

        <div
          key={`survey-step-${clampedStep}`}
          className={`workplace-survey-slide-card content-enter${
            step?.type === 'intro'
              ? ' workplace-survey-slide-card--intro'
              : step?.type === 'optional-identity'
              ? ' workplace-survey-slide-card--optional workplace-survey-slide-card--identity'
              : isOptionalContextStep
                ? ' workplace-survey-slide-card--optional'
                : ''
          }`}
        >
          <>
            {step.type === 'intro' && (
              <div className="workplace-survey-intro-screen">
                <p className="workplace-survey-pause-callout" role="note">
                  {step.pausePrompt}
                </p>
                <div className="workplace-survey-intro-body">
                  <div className="workplace-survey-intro-body-inner">
                    <div className="workplace-survey-intro-layout">
                      <div className="workplace-survey-intro-main">
                        <h3 className="workplace-survey-heading">{step.title}</h3>
                        <p className="workplace-survey-intro">{step.body}</p>
                        <div className="workplace-survey-legend">
                          <p className="workplace-survey-legend-title">Rating scale legend</p>
                          <ul>
                            <li>1. Strongly disagree = SD</li>
                            <li>2. Disagree = D</li>
                            <li>3. Neither agree nor disagree = NAD</li>
                            <li>4. Agree = A</li>
                            <li>5. Strongly agree = SA</li>
                          </ul>
                        </div>
                      </div>
                      <div className="workplace-survey-intro-lottie-wrap" aria-hidden="true">
                        <Lottie
                          animationData={thinkAnimationData}
                          loop
                          autoplay
                          className="workplace-survey-intro-lottie"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step.type === 'awareness-question' && (
              <div className="workplace-survey-question">
                <p className="workplace-survey-section-chip">{step.sectionTitle}</p>
                <p className="workplace-survey-question-label">{step.statement.label}</p>
                <div className="workplace-survey-before-after-grid">
                  <div className="workplace-survey-rating-block">
                    <p className="workplace-survey-rating-title">Before participating</p>
                    {renderScaleOptions(
                      surveyResponses.awarenessBefore?.[step.statement.id],
                      (value) => () => setSurveyAwareness('awarenessBefore', step.statement.id, value),
                      `${step.statement.label} before`
                    )}
                  </div>
                  <div className="workplace-survey-rating-block">
                    <p className="workplace-survey-rating-title">After participating</p>
                    {renderScaleOptions(
                      surveyResponses.awarenessAfter?.[step.statement.id],
                      (value) => () => setSurveyAwareness('awarenessAfter', step.statement.id, value),
                      `${step.statement.label} after`
                    )}
                  </div>
                </div>
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'experience-question' && (
              <div className="workplace-survey-question">
                <p className="workplace-survey-section-chip">{step.sectionTitle}</p>

                <p className="workplace-survey-question-label">{step.statement.label}</p>
                <div className="workplace-survey-rating-block workplace-survey-rating-block--single">
                  {renderScaleOptions(
                    surveyResponses.experience?.[step.statement.id],
                    (value) => () => setSurveyExperience(step.statement.id, value),
                    `${step.statement.label} rating`,
                    SURVEY_EXPERIENCE_NA_IDS.has(step.statement.id)
                  )}
                </div>
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'feedback' && (
              <div className="workplace-survey-question">
                <h3 className="workplace-survey-heading">{step.title}</h3>
                <label htmlFor="workplace-survey-feedback" className="workplace-survey-label">
                  Is there anything else you would like to share with us regarding your experiences with the session on Building Workplace Opportunities module?
                </label>
                <textarea
                  id="workplace-survey-feedback"
                  className="workplace-survey-textarea"
                  value={surveyResponses.additionalFeedback}
                  onChange={(e) => setSurveyResponses((prev) => ({ ...prev, additionalFeedback: e.target.value }))}
                  rows={5}
                  maxLength={1200}
                  placeholder="Optional feedback..."
                />
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'optional-identity' && (
              <div className="workplace-survey-question workplace-survey-question--optional workplace-survey-question--identity">
                <div className="workplace-survey-identity-header">
                  <h3 className="workplace-survey-heading">{step.title}</h3>
                  {step.body ? (
                    <p className="workplace-survey-intro workplace-survey-intro--identity">{step.body}</p>
                  ) : null}
                </div>
                <div className="workplace-survey-identity-body">
                  <div className="workplace-survey-identity-body-inner">
                    <fieldset className="workplace-survey-identity-fieldset">
                      <legend className="workplace-survey-identity-legend">Select all that apply (optional)</legend>
                      <ul className="workplace-survey-identity-list">
                        {WORKPLACE_EDI_IDENTITY_OPTIONS.map((option) => {
                          const inputId = `workplace-survey-identity-${option.value}`;
                          const checked = identitySelections.includes(option.value);
                          const isFullWidth =
                            option.value === 'preferNotToAnswer' || option.value === 'another';
                          return (
                            <li
                              key={option.value}
                              className={isFullWidth ? 'workplace-survey-identity-item--full' : undefined}
                            >
                              <label htmlFor={inputId} className="workplace-survey-identity-option">
                                <input
                                  id={inputId}
                                  type="checkbox"
                                  className="workplace-survey-identity-checkbox"
                                  checked={checked}
                                  onChange={() => toggleIdentitySelection(option.value)}
                                />
                                <span className="workplace-survey-identity-label">{option.label}</span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </fieldset>
                    {identitySelections.includes('another') && (
                      <textarea
                        className="workplace-survey-textarea workplace-survey-textarea--identity-other"
                        rows={2}
                        maxLength={300}
                        placeholder="Please specify"
                        value={surveyResponses.optional?.identityOther || ''}
                        onChange={(e) => setSurveyOptional('identityOther', e.target.value)}
                        aria-label="Another identity (please specify)"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {step.type === 'optional-followup' && (
              <div className="workplace-survey-question workplace-survey-question--optional">
                <h3 className="workplace-survey-heading">{step.title}</h3>
                {step.body ? <p className="workplace-survey-intro">{step.body}</p> : null}
                {renderOptionalChoiceOptions(
                  [
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ],
                  surveyResponses.optional?.followupInterview || '',
                  (value) => setSurveyOptional('followupInterview', value),
                  'Optional follow-up interview',
                  'workplace-survey-scale-row--optional-2col'
                )}
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'optional-role' && (
              <div
                className={`workplace-survey-question${isMandatoryDemographicsStep ? ' workplace-survey-question--required' : ' workplace-survey-question--optional'}`}
              >
                <h3 className="workplace-survey-heading">{step.title}</h3>
                {renderOptionalChoiceOptions(
                  optionalRoleOptions,
                  surveyResponses.optional?.currentRole || '',
                  (value) => setSurveyOptional('currentRole', value),
                  'Optional current role',
                  'workplace-survey-scale-row--optional-2col'
                )}
                {surveyResponses.optional?.currentRole === 'other' && (
                  <textarea
                    className="workplace-survey-textarea"
                    rows={2}
                    maxLength={180}
                    placeholder="Other role (optional)"
                    value={surveyResponses.optional?.currentRoleOther || ''}
                    onChange={(e) => setSurveyOptional('currentRoleOther', e.target.value)}
                  />
                )}
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'optional-years-experience' && (
              <div
                className={`workplace-survey-question${isMandatoryDemographicsStep ? ' workplace-survey-question--required' : ' workplace-survey-question--optional'}`}
              >
                <h3 className="workplace-survey-heading">{step.title}</h3>
                {renderOptionalChoiceOptions(
                  optionalYearsExperienceOptions,
                  surveyResponses.optional?.yearsExperience || '',
                  (value) => setSurveyOptional('yearsExperience', value),
                  'Optional years of experience',
                  'workplace-survey-scale-row--optional-2col'
                )}
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'optional-work-context' && (
              <div
                className={`workplace-survey-question${isMandatoryDemographicsStep ? ' workplace-survey-question--required' : ' workplace-survey-question--optional'}`}
              >
                <h3 className="workplace-survey-heading">{step.title}</h3>
                {renderOptionalChoiceOptions(
                  optionalWorkContextOptions,
                  surveyResponses.optional?.workContext || '',
                  (value) => setSurveyOptional('workContext', value),
                  'Optional work context',
                  'workplace-survey-scale-row--optional-2col'
                )}
                {surveyResponses.optional?.workContext === 'other' && (
                  <textarea
                    className="workplace-survey-textarea"
                    rows={2}
                    maxLength={180}
                    placeholder="Other work context (optional)"
                    value={surveyResponses.optional?.workContextOther || ''}
                    onChange={(e) => setSurveyOptional('workContextOther', e.target.value)}
                  />
                )}
                {renderSurveyRequiredSlot()}
              </div>
            )}

            {step.type === 'submitted' && (
              <div className="workplace-survey-question workplace-survey-question--submitted">
                <h3 className="workplace-survey-heading">{step.title}</h3>
                {/* <p className="workplace-survey-thanks">Thank you for completing the survey. Your response has been recorded.</p> */}
                {participantReviewEnabled && participantReviewSubmitted ? (
                  <p className="workplace-survey-thanks workplace-survey-thanks--review">
                    {isParentModule
                      ? 'Thank you for completing the Parent Learning Module participant review.'
                      : isCommunityModule
                        ? 'Thank you for completing the Community Learning Module participant review.'
                        : 'Thank you for completing the Workplace Learning Module participant review.'}
                  </p>
                ) : participantReviewEnabled ? (
                  <p className="workplace-survey-label">
                    When you are ready, you can complete the{' '}
                    <strong>
                      {isParentModule
                        ? 'Parent Learning Module - Participant Review'
                        : isCommunityModule
                          ? 'Community Learning Module - Participant Review'
                          : 'Workplace Learning Module - Participant Review'}
                    </strong>{' '}
                    (short form; all fields optional). Open it from the button below. Your certificate is available to download anytime.
                  </p>
                ) : (
                  <p className="workplace-survey-label">
                    Your certificate is available to download anytime.
                  </p>
                )}
                <div className="workplace-survey-complete-actions workplace-survey-complete-actions--with-review">
                  {participantReviewEnabled && !participantReviewSubmitted ? (
                    <button
                      type="button"
                      className="workplace-survey-nav-btn workplace-survey-nav-btn--primary workplace-survey-complete-btn workplace-survey-complete-btn--review"
                      onClick={() => {
                        setParticipantReviewErrors({});
                        setParticipantReviewModalOpen(true);
                      }}
                    >
                      Open participant review
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="workplace-survey-nav-btn workplace-survey-nav-btn--primary workplace-survey-complete-btn"
                    onClick={() => void handleCertificateDownload()}
                    disabled={certificateDownloading}
                  >
                    {certificateDownloading ? 'Preparing certificate...' : 'Download certificate'}
                  </button>
                  <button
                    type="button"
                    className="workplace-survey-nav-btn workplace-survey-complete-btn"
                    onClick={() => navigate('/')}
                  >
                    Return to home
                  </button>
                </div>
              </div>
            )}
          </>
        </div>

        {!isSubmittedStep && !surveyReviewLocked && (
          <>
            <div className="workplace-survey-nav">
              <button
                type="button"
                className="workplace-survey-nav-btn"
                disabled={(isFirst && !hasPrevSection(surveyStageId)) || surveySaving}
                onClick={() => {
                  if (!isFirst) {
                    setWorkplaceSurveyStep((s) => Math.max(0, s - 1));
                    return;
                  }
                  goToPrevSectionFrom(surveyStageId);
                }}
              >
                Previous
              </button>
              <div className="workplace-survey-nav-progress-text" aria-hidden="true">
                {progressPct}%
              </div>
              <button
                type="button"
                className="workplace-survey-nav-btn workplace-survey-nav-btn--primary"
                disabled={
                  surveySaving
                  || (!isLast && !currentStepComplete)
                  || (step.type === 'feedback' && !isSurveyComplete)
                }
                onClick={() => {
                  if (step.type === SURVEY_SUBMIT_STEP_TYPE) {
                    void (async () => {
                      if (!surveySubmitted) await handleSurveySubmit();
                      setWorkplaceSurveyStep((s) => Math.min(totalSteps - 1, s + 1));
                    })();
                    return;
                  }
                  setWorkplaceSurveyStep((s) => Math.min(totalSteps - 1, s + 1));
                }}
              >
                {step.type === SURVEY_SUBMIT_STEP_TYPE
                  ? surveySaving
                    ? 'Submitting...'
                    : 'Submit and finish'
                  : 'Next'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderIntroModuleSlides = (sectionId) => {
    const slides = INTRO_SECTION_SLIDES[sectionId];
    if (!slides?.length) return null;
    const currentIdx = Math.min(introSlideIndex, slides.length - 1);
    const currentSlide = slides[currentIdx] || slides[0];
    const lines = currentSlide.lines || [];
    const revealTarget = lines.length;
    const canGoNext = revealTarget === 0 || introRevealCount >= revealTarget;
    const isLastSlideInDeck = currentIdx === slides.length - 1;
    const sectionIndex = sectionsWithSteps.findIndex((s) => s.id === sectionId);
    const hasNextTopic = sectionIndex >= 0 && sectionIndex < sectionsWithSteps.length - 1;

    const handlePrimary = () => {
      if (!canGoNext) return;
      if (!isLastSlideInDeck) {
        setIntroSlideIndex((s) => s + 1);
        setIntroRevealCount(1);
        return;
      }
      if (hasNextTopic) {
        goToNextSectionFrom(sectionId);
        return;
      }
      if (moduleId === 'intro-module' && sectionId === INTRO_MODULE_FINAL_SECTION_ID) {
        return;
      }
      if (viewMode === 'overview') {
        setSyllabusModalSectionId(null);
      } else {
        setViewMode('overview');
      }
    };

    const showIntroCompletionChoices =
      moduleId === 'intro-module'
      && sectionId === INTRO_MODULE_FINAL_SECTION_ID
      && isLastSlideInDeck
      && canGoNext;

    const handleIntroMarkCompleteAndRefresh = async () => {
      if (!firebaseUser?.uid) return;
      await markModuleProgressCompleted(firebaseUser.uid, 'intro-module', {
        finalStageId: INTRO_MODULE_FINAL_SECTION_ID,
        totalStages: sectionsWithSteps.length,
        allStageIds: sectionsWithSteps.map((s) => s.id),
      });
      requestProgressRefresh();
    };

    const handleIntroContinueToWorkplace = () => {
      void (async () => {
        await handleIntroMarkCompleteAndRefresh();
        const wp = moduleProgress?.find((p) => p.moduleId === 'workplace-module');
        const workplaceNotStarted = !wp?.status || wp.status === 'not-started';
        if (workplaceNotStarted) {
          navigate('/', { state: { openWorkplaceConsent: true } });
        } else {
          navigate('/study-module/workplace-module');
        }
      })();
    };

    const handleIntroReturnHome = () => {
      void (async () => {
        await handleIntroMarkCompleteAndRefresh();
        navigate('/');
      })();
    };

    const handleIntroStartOverIntro = () => {
      const firstId = INTRO_MODULE_CONTENT.sections[0]?.id;
      if (!firstId) return;
      setCompletedSyllabusSteps([]);
      if (firebaseUser?.uid) {
        void resetModuleProgressToBeginning(
          firebaseUser.uid,
          'intro-module',
          firstId,
          sectionsWithSteps.length
        ).finally(requestProgressRefresh);
      }
      if (viewMode === 'overview') {
        resetSectionInteractiveState(firstId);
        setIntroSlideIndex(0);
        setIntroRevealCount(1);
        setIntroVideoModalOpen(false);
        setSyllabusModalSectionId(firstId);
        setContentKey((k) => k + 1);
        setTimeout(() => scrollOverviewStepSurfacesToTop(), 0);
      } else {
        handleStepSelect(firstId);
      }
    };
    const primaryLabel = !isLastSlideInDeck ? 'Next' : hasNextTopic ? 'Next topic' : 'Course overview';
    const isVideo = currentSlide.variant === 'video';

    return (
      <div
        className={`section-content workplace-m1-interactive intro-module-slides${
          showIntroCompletionChoices ? ' intro-module-slides--with-completion' : ''
        }`}
      >
        <div
          key={`intro-${sectionId}-${currentIdx}`}
          className={`workplace-m1-slide-card workplace-m1-slide-card--anim intro-module-slide-card${
            isVideo ? ' intro-module-slide-card--video' : ' intro-module-slide-card--text'
          }`}
        >
          <h3 className="workplace-m1-title">{currentSlide.title}</h3>
          <div className="intro-module-body">
            <div className="intro-module-body-inner">
              {isVideo ? (
                <div className="workplace-m2-layout intro-start-video-layout">
                  <div className="workplace-m2-content">
                    <div className="workplace-m2-line-stack">
                      {lines.slice(0, Math.min(introRevealCount, lines.length)).map((line, i) => (
                        <p key={`intro-vline-${i}`} className="workplace-m2-line">{line}</p>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="workplace-m2-lottie-trigger workplace-m2-video-thumb-trigger"
                    onClick={() => setIntroVideoModalOpen(true)}
                    aria-label="Open introduction module video"
                    title="Open video"
                  >
                    <img
                      src={introYoutubeEmbedUrl ? getYoutubePosterUrl(INTRO_MODULE_YOUTUBE_VIDEO_ID) : introVideoPosterSrc}
                      alt=""
                      className="workplace-m2-video-thumb"
                      draggable={false}
                      {...youtubePosterImgHandlers}
                      {...videoThumbProps}
                    />
                    <div className="workplace-m2-video-overlay" aria-hidden="true">
                      <span className="workplace-m2-video-play">
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="workplace-m1-layout intro-module-text-layout">
                  <div className="workplace-m1-content">
                    <ul className="workplace-m1-reveal-list">
                      {lines.slice(0, Math.min(introRevealCount, lines.length)).map((line, i) => (
                        <li
                          key={`intro-line-${i}`}
                          className="workplace-m1-reveal-item"
                          style={{ '--m1-item-delay': `${i * 260}ms` }}
                        >
                          {line}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="workplace-m1-lottie-wrap" aria-hidden="true">
                    <Lottie
                      animationData={moduleOneAnimationData}
                      loop={false}
                      autoplay
                      className="workplace-m1-lottie"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {showIntroCompletionChoices ? (
            <div className="intro-module-completion-panel">
              <p className="intro-module-completion-lead">
                You&apos;ve finished the Introduction Module. What would you like to do next?
              </p>
              <div className="intro-module-completion-actions">
                <button
                  type="button"
                  className="intro-module-completion-btn intro-module-completion-btn--primary"
                  onClick={handleIntroContinueToWorkplace}
                >
                  Continue to Building Workplace Bridges
                </button>
                <button
                  type="button"
                  className="intro-module-completion-btn"
                  onClick={() => {
                    void (async () => {
                      await handleIntroMarkCompleteAndRefresh();
                      await handleCertificateDownload();
                    })();
                  }}
                  disabled={certificateDownloading}
                >
                  {certificateDownloading ? 'Preparing certificate...' : 'Download certificate'}
                </button>
                <button
                  type="button"
                  className="intro-module-completion-btn"
                  onClick={handleIntroStartOverIntro}
                >
                  Start Introduction over
                </button>
                <button
                  type="button"
                  className="intro-module-completion-btn intro-module-completion-btn--ghost"
                  onClick={handleIntroReturnHome}
                >
                  Return to Learning Hub
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="workplace-m1-nav">
          <button
            type="button"
            className="workplace-m1-nav-btn"
            disabled={currentIdx === 0 && !hasPrevSection(sectionId)}
            onClick={() => {
              if (currentIdx > 0) {
                setIntroSlideIndex((s) => Math.max(0, s - 1));
                setIntroRevealCount(1);
                return;
              }
              goToPrevSectionFrom(sectionId);
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {slides.map((_, idx) => (
              <span
                key={`intro-bubble-${sectionId}-${idx}`}
                className={`module-slide-bubble ${currentIdx === idx ? 'module-slide-bubble--active' : ''}`}
              />
            ))}
          </div>
          {showIntroCompletionChoices ? (
            <span className="workplace-m1-nav-end-spacer" aria-hidden="true" />
          ) : (
            <button
              type="button"
              className="workplace-m1-nav-btn workplace-m1-nav-btn--primary"
              disabled={!canGoNext}
              onClick={handlePrimary}
            >
              {primaryLabel}
            </button>
          )}
        </div>
        {introVideoModalOpen && (
          <div className="anika-video-modal-layer" role="dialog" aria-modal="true" aria-label="Introduction module video">
            <button
              type="button"
              className="anika-video-modal-backdrop"
              onClick={() => setIntroVideoModalOpen(false)}
              aria-label="Close video modal"
            />
            <div className="anika-video-modal-card">
              <button
                type="button"
                className="anika-video-modal-close"
                onClick={() => setIntroVideoModalOpen(false)}
                aria-label="Close video"
              >
                ×
              </button>
              <div className="animation-visual-frame intro-video-modal-placeholder">
                {introYoutubeEmbedUrl ? (
                  <IntroYoutubePosterPlay
                    embedUrl={introYoutubeEmbedUrl}
                    videoId={INTRO_MODULE_YOUTUBE_VIDEO_ID}
                    posterImgProps={contentImageProps({
                      width: 1280,
                      height: 720,
                      loading: 'eager',
                      fetchPriority: 'high',
                    })}
                    isPlaying={introYoutubePlayingModal}
                    onPlay={() => setIntroYoutubePlayingModal(true)}
                    title="Introduction module video"
                    mode="modal"
                  />
                ) : (
                  <video
                    ref={introModalVideoRef}
                    className="intro-video-modal-player"
                    preload="metadata"
                    poster={introVideoPosterSrc}
                    src={introVideoSrc}
                    {...LOCAL_VIDEO_PLAYER_PROPS}
                  >
                    Your browser does not support embedded video playback.
                  </video>
                )}
              </div>
              {/* <p className="module-video-footnote">{VIDEO_FOOTNOTE_TEXT}</p> */}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWorkplaceModuleOneInteractive = () => {
    const m1Slides = isParentModule
      ? PARENT_M1_SLIDES
      : pickFlowModule(moduleId, WORKPLACE_M1_SLIDES, EDUCATOR_M1_SLIDES, COMMUNITY_M1_SLIDES);
    const currentSlide = m1Slides[workplaceM1Slide] || m1Slides[0];
    const m1YoutubeId = currentSlide.youtubeVideoId || '';
    const m1YoutubeEmbedUrl = m1YoutubeId ? getIntroModuleYoutubeEmbedUrl(m1YoutubeId) : '';
    const showM1YoutubeVideo = Boolean(m1YoutubeEmbedUrl);
    const revealLines = currentSlide.lines || [];
    const staticTopLine = currentSlide.introLine || revealLines[0] || '';
    const listLines =
      currentSlide.goalLines ||
      (revealLines.length > 1 ? revealLines.slice(1, Math.max(1, revealLines.length - 1)) : revealLines);
    const parentBodyLines = isParentModule ? (currentSlide.bodyLines || []) : [];
    const parentWhyHeading = isParentModule ? (currentSlide.whyParentsHeading || '') : '';
    const parentClosingLine = isParentModule ? (currentSlide.closingLine || '') : '';
    const parentGoalCount = isParentModule && currentSlide.goalLines ? currentSlide.goalLines.length : 0;
    const parentTextRevealTotal =
      parentGoalCount + parentBodyLines.length + (parentClosingLine ? 1 : 0);
    const footerLine =
      currentSlide.outroLine || (revealLines.length > 2 ? revealLines[revealLines.length - 1] : '');
    const showFooterLine = Boolean(footerLine) && !isParentModule;
    const isSingleSlideDeck = m1Slides.length === 1;
    const visibleListLines = isParentModule && !showM1YoutubeVideo
      ? listLines.slice(0, Math.min(workplaceM1RevealCount, listLines.length))
      : isSingleSlideDeck
        ? listLines
        : listLines.slice(0, Math.min(workplaceM1RevealCount, listLines.length));
    const visibleParentBodyLines = isParentModule
      ? parentBodyLines.slice(
          0,
          Math.max(0, Math.min(workplaceM1RevealCount - parentGoalCount, parentBodyLines.length))
        )
      : [];
    const showParentWhyHeading =
      isParentModule && parentWhyHeading && workplaceM1RevealCount > parentGoalCount;
    const showParentClosing =
      isParentModule && parentClosingLine && workplaceM1RevealCount >= parentTextRevealTotal;
    const revealTarget = showM1YoutubeVideo
      ? 0
      : isParentModule && currentSlide.goalLines
        ? parentTextRevealTotal
        : isSingleSlideDeck
          ? 0
          : listLines.length;
    const isFinalModuleOneSlide = workplaceM1Slide === m1Slides.length - 1;
    const canGoNext = revealTarget ? workplaceM1RevealCount >= revealTarget : true;

    return (
      <div className="section-content workplace-m1-interactive">
        <div key={`m1-slide-${workplaceM1Slide}`} className="workplace-m1-slide-card workplace-m1-slide-card--anim">
          <h3 className="workplace-m1-title">{currentSlide.title}</h3>
          {showM1YoutubeVideo ? (
            <div className="workplace-m1-video-shell">
              <IntroYoutubePosterPlay
                embedUrl={m1YoutubeEmbedUrl}
                videoId={m1YoutubeId}
                posterImgProps={contentImageProps({
                  width: 1280,
                  height: 720,
                  loading: 'eager',
                  fetchPriority: 'high',
                })}
                isPlaying={workplaceM1VideoPlaying}
                onPlay={() => setWorkplaceM1VideoPlaying(true)}
                title={
                  isParentModule
                    ? 'Parent module overview video'
                    : isCommunityModule
                    ? 'Community module overview video'
                    : isEducatorModule
                      ? 'Educator module overview video'
                      : 'Building Workplace Bridges module overview video'
                }
                mode="modal"
              />
            </div>
          ) : (
            <>
              {staticTopLine && !isParentModule && (
                <p className="workplace-m1-static-line">{staticTopLine}</p>
              )}
              <div className="workplace-m1-layout">
                <div className="workplace-m1-content">
                  {isParentModule && parentGoalCount > 0 && (
                    <h4 className="workplace-m1-section-heading">Learning Goals</h4>
                  )}
                  <ul className="workplace-m1-reveal-list">
                    {visibleListLines.map((line, idx) => {
                      const goalKey =
                        typeof line === 'object' && line?.emphasis
                          ? line.emphasis
                          : String(line);
                      return (
                        <li
                          key={goalKey}
                          className="workplace-m1-reveal-item"
                          style={{ '--m1-item-delay': `${idx * 260}ms` }}
                        >
                          {typeof line === 'object' && line?.emphasis ? (
                            <>
                              <strong>{line.emphasis}</strong> {line.tail}
                            </>
                          ) : (
                            <>
                              <strong>{String(line).split(' ')[0]}</strong>{' '}
                              {String(line).split(' ').slice(1).join(' ')}
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="workplace-m1-lottie-wrap" aria-hidden="true">
                  <Lottie
                    animationData={moduleOneAnimationData}
                    loop={false}
                    autoplay
                    className="workplace-m1-lottie"
                  />
                </div>
              </div>
              {showFooterLine && (
                <p className="workplace-m1-footer-line">{footerLine}</p>
              )}
              {visibleParentBodyLines.length > 0 && (
                <div className="workplace-m1-parent-body">
                  {showParentWhyHeading && (
                    <h4 className="workplace-m1-section-heading">{parentWhyHeading}</h4>
                  )}
                  {visibleParentBodyLines.map((line) => (
                    <p key={line} className="workplace-m1-line">{line}</p>
                  ))}
                </div>
              )}
              {showParentClosing && (
                <p className="workplace-m1-footer-line workplace-m1-parent-closing">{parentClosingLine}</p>
              )}
            </>
          )}

        </div>

        <div className="workplace-m1-nav">
          <button
            type="button"
            className="workplace-m1-nav-btn"
            disabled={workplaceM1Slide === 0 && !hasPrevSection('overview')}
            onClick={() => {
              if (workplaceM1Slide > 0) {
                setWorkplaceM1Slide((s) => Math.max(0, s - 1));
                setWorkplaceM1RevealCount(1);
                return;
              }
              goToPrevSectionFrom('overview');
            }}
          >
            Previous
          </button>
          <div className="module-slide-bubbles" aria-hidden="true">
            {m1Slides.map((_, idx) => (
              <span key={`m1-bubble-${idx}`} className={`module-slide-bubble ${workplaceM1Slide === idx ? 'module-slide-bubble--active' : ''}`} />
            ))}
          </div>
          <button
            type="button"
            className="workplace-m1-nav-btn workplace-m1-nav-btn--primary"
            disabled={!canGoNext}
            onClick={() => {
              if (isFinalModuleOneSlide) {
                goToNextSectionFrom('overview');
                return;
              }
              setWorkplaceM1Slide((s) => Math.min(m1Slides.length - 1, s + 1));
              setWorkplaceM1RevealCount(1);
            }}
          >
            {isFinalModuleOneSlide ? 'Next module' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  const renderSectionContent = () => {
    if (isParentModule && PARENT_STORY_SECTION_IDS.includes(activeSection)) {
      return renderParentStoryBlock(activeSection);
    }
    if (activeSection === 'barriers' && isStudyFlowModule && !isParentModule) {
      return renderBarriersBlock();
    }
    if (activeSection === 'anika-story' && isStudyFlowModule && !isParentModule) {
      return renderAnikaBlock();
    }
    if (activeSection === 'meta-reflexivity' && isParentModule) {
      return renderParentMetaReflexivityBlock();
    }
    if (activeSection === 'meta-reflexivity' && isStudyFlowModule && !isParentModule) {
      return renderMetaReflexivityBlock();
    }
    if (activeSection === 'meta-reflexive-manager' && isStudyFlowModule && !isParentModule) {
      return renderMetaReflexiveManagerBlock();
    }
    if (activeSection === 'actions' && isStudyFlowModule && !isParentModule) {
      return renderActionsBlock();
    }
    if (activeSection === surveyStageId && isStudyFlowModule) {
      return renderWorkplaceSurveyBlock();
    }
    if (activeSection === 'overview') {
      if (isStudyFlowModule) {
        return renderWorkplaceModuleOneInteractive();
      }
      return (
        <div className="section-content">
          <div dangerouslySetInnerHTML={{ __html: moduleContent.sections.find(s => s.id === 'overview').content }} />
          <div className="learning-goals">
            <h3>Learning Goals</h3>
            <ul>
              {moduleContent.learningGoals.map((goal, index) => (
                <li key={index}>{goal}</li>
              ))}
            </ul>
          </div>
          {isStudyFlowModule && (
            <p className="module-overview-after-goals">
              {moduleId === 'community-module' ? (
                <>
                  You will use Aman&apos;s story and research framing to notice how community norms and care shape legitimacy and mobility-without reducing
                  those patterns to individual choices alone.
                </>
              ) : moduleId === 'parent-module' ? (
                <>
                  You will follow Sara, Anika, and Aman across education, employment, and community settings-practicing Pause, Reflect, and Act as a parent
                  or guardian supporting STEMM pathways at home.
                </>
              ) : moduleId === 'educator-module' ? (
                <>
                  You will use Sara&apos;s story and research framing to notice how academic structures and informal endorsement shape who advances-without
                  reducing those patterns to individual student deficits.
                </>
              ) : (
                <>
                  In this module, you&apos;ll be introduced to real stories - based on lived experiences from our research - which are designed to invite
                  meta-reflexivity: deeper understanding of why certain barriers go unseen, how those barriers are shaped by systems, what role you might play
                  in shifting them.
                </>
              )}
            </p>
          )}
        </div>
      );
    }
    if (moduleId === 'intro-module' && INTRO_SECTION_SLIDES[activeSection]) {
      return renderIntroModuleSlides(activeSection);
    }
    const section = moduleContent.sections.find(s => s.id === activeSection);
    if (section) {
      return (
        <div className="section-content">
          <div dangerouslySetInnerHTML={{ __html: section.content }} />
        </div>
      );
    }
    return null;
  };

  /** Renders section content by id (for use in modal). */
  const renderSectionContentForId = (sectionId) => {
    if (isParentModule && PARENT_STORY_SECTION_IDS.includes(sectionId)) {
      return renderParentStoryBlock(sectionId);
    }
    if (sectionId === 'overview') {
      if (isStudyFlowModule) {
        return renderWorkplaceModuleOneInteractive();
      }
      return (
        <div className="section-content">
          <div dangerouslySetInnerHTML={{ __html: moduleContent.sections.find(s => s.id === 'overview').content }} />
          <div className="learning-goals">
            <h3>Learning Goals</h3>
            <ul>
              {moduleContent.learningGoals.map((goal, index) => (
                <li key={index}>{goal}</li>
              ))}
            </ul>
          </div>
          {(moduleId === 'workplace-module' || moduleId === 'educator-module') && (
            <p className="module-overview-after-goals">
              {moduleId === 'educator-module' ? (
                <>
                  You will use Sara&apos;s story and research framing to notice how academic structures and informal endorsement shape who advances-without
                  reducing those patterns to individual student deficits.
                </>
              ) : (
                <>
                  In this module, you&apos;ll be introduced to real stories - based on lived experiences from our research - which are designed to invite
                  meta-reflexivity: deeper understanding of why certain barriers go unseen, how those barriers are shaped by systems, what role you might play
                  in shifting them.
                </>
              )}
            </p>
          )}
        </div>
      );
    }
    if (sectionId === 'barriers' && isStudyFlowModule && !isParentModule) {
      return renderBarriersBlock();
    }
    if (sectionId === 'anika-story' && isStudyFlowModule && !isParentModule) {
      return renderAnikaBlock();
    }
    if (sectionId === 'meta-reflexivity' && isParentModule) {
      return renderParentMetaReflexivityBlock();
    }
    if (sectionId === 'meta-reflexivity' && isStudyFlowModule && !isParentModule) {
      return renderMetaReflexivityBlock();
    }
    if (sectionId === 'meta-reflexive-manager' && isStudyFlowModule && !isParentModule) {
      return renderMetaReflexiveManagerBlock();
    }
    if (sectionId === 'actions' && isStudyFlowModule && !isParentModule) {
      return renderActionsBlock();
    }
    if (sectionId === surveyStageId && isStudyFlowModule) {
      return renderWorkplaceSurveyBlock();
    }
    if (moduleId === 'intro-module' && INTRO_SECTION_SLIDES[sectionId]) {
      return renderIntroModuleSlides(sectionId);
    }
    const section = moduleContent.sections.find(s => s.id === sectionId);
    if (section) {
      return (
        <div className="section-content">
          <div dangerouslySetInnerHTML={{ __html: section.content }} />
        </div>
      );
    }
    return null;
  };

  const participantReviewPortal =
    participantReviewEnabled && participantReviewModalOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="elearning-enrollment-overlay"
            role="presentation"
            onClick={() => {
              if (participantReviewSubmitting) return;
              setParticipantReviewModalOpen(false);
            }}
          >
            <div
              className="elearning-enrollment-modal elearning-enrollment-modal--research workplace-participant-review-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="workplace-participant-review-title"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && !participantReviewSubmitting) {
                  setParticipantReviewModalOpen(false);
                }
              }}
            >
              <div className="elearning-enrollment-research-hero">
                <div className="elearning-enrollment-research-hero__row">
                  <span className="elearning-enrollment-research-chip">
                    {isParentModule
                      ? 'Parent module'
                      : isCommunityModule
                        ? 'Community module'
                        : isEducatorModule
                          ? 'Educator module'
                          : 'Workplace module'}
                  </span>
                </div>
                <h3 id="workplace-participant-review-title" className="elearning-enrollment-title elearning-enrollment-title--research">
                  {isParentModule
                    ? 'Parent Learning Module - Participant Review'
                    : isCommunityModule
                      ? 'Community Learning Module - Participant Review'
                      : isEducatorModule
                        ? 'Educator Learning Module - Participant Review'
                        : 'Workplace Learning Module - Participant Review'}
                </h3>
              </div>
              <div className="elearning-enrollment-form-scroll">
                <div className="elearning-enrollment-consent-copy workplace-participant-review-intro">
                  <p>Thank you for reviewing this early version of the learning module developed from the research you contributed to.</p>
                  {isCommunityModule ? (
                    <>
                      <p>
                        This module translates our key research findings into a format designed for families, faith leaders, cultural organizers, alumni
                        networks, and informal community leaders. It focuses on how community norms and care shape legitimacy, endorsement, and mobility in
                        STEMM pathways.
                      </p>
                      <p>
                        We are not asking you to take the full training right now unless you wish to. We are asking you to provide your honest feedback -
                        both as a research participant and as someone who shapes community norms and opportunity - whether the material is clear,
                        representative, and actionable for its intended audience (from your point of view only). To support with sufficient context, we ask
                        that you review the Intro module as well.
                      </p>
                    </>
                  ) : isEducatorModule ? (
                    <>
                      <p>
                        This module translates our key research findings into a format designed for faculty, academic administrators, and STEMM program
                        leads. It focuses on how systemic patterns shape qualification, endorsement, and progression in post-secondary STEMM pathways.
                      </p>
                      <p>
                        We are not asking you to take the full training right now unless you wish to. We are asking you to provide your honest feedback -
                        both as a research participant and as someone who shapes learning and opportunity - whether the material is clear, representative, and
                        actionable for its intended audience (from your point of view only). To support with sufficient context, we ask that you review the
                        Intro module as well.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        This module translates our key research findings into a format designed for workplace leaders. It focuses on how systemic patterns
                        shape progression and leadership outcomes.
                      </p>
                      <p>
                        We are not asking you to take the training itself (although you are more than welcome to do so after our official launch!). We are
                        asking you to provide your honest feedback - both as a research participant and as someone with workplace influence - whether the
                        learning is clear, representative, and actionable for its intended audience (from your point of view only - we understand that no
                        learning content can in-depth cover every possible perspective). To support with sufficient context, we ask that you review the
                        Intro module as well.
                      </p>
                    </>
                  )}
                  <p>
                    All questions in this form are optional. Use the 5-point scale below (where indicated) and add a brief written explanation after any
                    rating if you wish.
                  </p>
                </div>
                <p className="workplace-participant-review-scale-title">Scale (questions 2-8, optional)</p>
                <ul className="workplace-participant-review-scale-list">
                  <li>1 - Strongly disagree</li>
                  <li>2 - Somewhat disagree</li>
                  <li>3 - Neither agree nor disagree</li>
                  <li>4 - Somewhat agree</li>
                  <li>5 - Strongly agree</li>
                </ul>

                <h4 className="workplace-participant-review-feedback-heading">Feedback questions (all optional)</h4>

                <div className="participant-review-two-col-fields participant-review-two-col-fields--scale">
                <div className="participant-review-field">
                  <p className="workplace-survey-question-label">
                    1. I found the introduction clear in explaining what this learning is (and is not) trying to do. (optional)
                  </p>
                  <p className="participant-review-q-hint">Response (optional):</p>
                  <div className="workplace-survey-scale-row workplace-survey-scale-row--optional" role="radiogroup" aria-label="Question 1 response">
                    {PARTICIPANT_REVIEW_Q1_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={participantReviewForm.q1IntroClarity === opt.value}
                        className={`workplace-survey-scale-pill workplace-survey-scale-pill--optional ${
                          participantReviewForm.q1IntroClarity === opt.value ? 'workplace-survey-scale-pill--active' : ''
                        }`}
                        onClick={() => {
                          setParticipantReviewForm((p) => ({ ...p, q1IntroClarity: opt.value }));
                          setParticipantReviewErrors((er) => ({ ...er, q1IntroClarity: undefined }));
                        }}
                      >
                        <span className="workplace-survey-scale-pill-label">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                  {participantReviewErrors.q1IntroClarity ? (
                    <p className="elearning-enrollment-field-error" role="alert">
                      {participantReviewErrors.q1IntroClarity}
                    </p>
                  ) : null}
                  <label className="workplace-participant-review-label" htmlFor="participant-review-q1-explain">
                    Please explain your rating (optional):
                  </label>
                  <textarea
                    id="participant-review-q1-explain"
                    className="elearning-enrollment-textarea elearning-enrollment-textarea--compact"
                    rows={3}
                    maxLength={1200}
                    value={participantReviewForm.q1Explain}
                    onChange={(e) => {
                      setParticipantReviewForm((p) => ({ ...p, q1Explain: e.target.value }));
                      setParticipantReviewErrors((er) => ({ ...er, q1Explain: undefined }));
                    }}
                  />
                  {participantReviewErrors.q1Explain ? (
                    <p className="elearning-enrollment-field-error" role="alert">
                      {participantReviewErrors.q1Explain}
                    </p>
                  ) : null}
                </div>
                {PARTICIPANT_REVIEW_SCALE_QUESTIONS.map((sq, idx) => {
                  const qNum = idx + 2;
                  const sel = participantReviewForm.ratings[sq.id];
                  return (
                    <div key={sq.id} className="participant-review-field">
                      <p className="workplace-survey-question-label">
                        {qNum}. {sq.label} (optional)
                      </p>
                      <p className="participant-review-q-hint">Response (1-5, optional):</p>
                      <div className="workplace-survey-scale-row workplace-survey-scale-row--optional" role="radiogroup" aria-label={`Question ${qNum} scale`}>
                        {PARTICIPANT_REVIEW_LIKERT_HEADERS.map((sc) => (
                          <button
                            key={`${sq.id}-${sc.value}`}
                            type="button"
                            role="radio"
                            aria-checked={Number(sel) === sc.value}
                            className={`workplace-survey-scale-pill workplace-survey-scale-pill--optional ${
                              Number(sel) === sc.value ? 'workplace-survey-scale-pill--active' : ''
                            }`}
                            onClick={() => {
                              setParticipantReviewForm((p) => ({
                                ...p,
                                ratings: { ...p.ratings, [sq.id]: sc.value },
                              }));
                              setParticipantReviewErrors((er) => ({ ...er, [`rating_${sq.id}`]: undefined }));
                            }}
                          >
                            <span className="workplace-survey-scale-pill-value">{sc.value}</span>
                            <span className="workplace-survey-scale-pill-label">{sc.fullLabel}</span>
                          </button>
                        ))}
                      </div>
                      {participantReviewErrors[`rating_${sq.id}`] ? (
                        <p className="elearning-enrollment-field-error" role="alert">
                          {participantReviewErrors[`rating_${sq.id}`]}
                        </p>
                      ) : null}
                      <label className="workplace-participant-review-label" htmlFor={`participant-review-explain-${sq.id}`}>
                        Please explain your rating (optional):
                      </label>
                      <textarea
                        id={`participant-review-explain-${sq.id}`}
                        className="elearning-enrollment-textarea elearning-enrollment-textarea--compact"
                        rows={3}
                        maxLength={1200}
                        value={participantReviewForm.ratingExplains[sq.id]}
                        onChange={(e) => {
                          setParticipantReviewForm((p) => ({
                            ...p,
                            ratingExplains: { ...p.ratingExplains, [sq.id]: e.target.value },
                          }));
                          setParticipantReviewErrors((er) => ({ ...er, [`explain_${sq.id}`]: undefined }));
                        }}
                      />
                      {participantReviewErrors[`explain_${sq.id}`] ? (
                        <p className="elearning-enrollment-field-error" role="alert">
                          {participantReviewErrors[`explain_${sq.id}`]}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
                </div>

                <div className="participant-review-two-col-fields participant-review-two-col-fields--demographics">
                <div className="participant-review-field">
                  <p className="workplace-survey-question-label">
                    9. Is there anything you would want us to be especially mindful of as this module is shared with employers? (optional)
                  </p>
                  <textarea
                    id="participant-review-q9"
                    className="elearning-enrollment-textarea elearning-enrollment-textarea--compact"
                    rows={4}
                    maxLength={2000}
                    placeholder="Your response…"
                    value={participantReviewForm.q9Mindful}
                    onChange={(e) => {
                      setParticipantReviewForm((p) => ({ ...p, q9Mindful: e.target.value }));
                      setParticipantReviewErrors((er) => ({ ...er, q9Mindful: undefined }));
                    }}
                  />
                  {participantReviewErrors.q9Mindful ? (
                    <p className="elearning-enrollment-field-error" role="alert">
                      {participantReviewErrors.q9Mindful}
                    </p>
                  ) : null}
                </div>

                <div className="participant-review-field">
                  <p className="workplace-survey-question-label">
                    10. Approximately how many years of experience do you have in a STEMM profession? (optional)
                  </p>
                  <div className="workplace-survey-scale-row workplace-survey-scale-row--optional" role="radiogroup" aria-label="Years in STEMM">
                    {PARTICIPANT_REVIEW_YEARS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={participantReviewForm.q10YearsStemm === opt.value}
                        className={`workplace-survey-scale-pill workplace-survey-scale-pill--optional ${
                          participantReviewForm.q10YearsStemm === opt.value ? 'workplace-survey-scale-pill--active' : ''
                        }`}
                        onClick={() => {
                          setParticipantReviewForm((p) => ({ ...p, q10YearsStemm: opt.value }));
                          setParticipantReviewErrors((er) => ({ ...er, q10YearsStemm: undefined }));
                        }}
                      >
                        <span className="workplace-survey-scale-pill-label">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                  {participantReviewErrors.q10YearsStemm ? (
                    <p className="elearning-enrollment-field-error" role="alert">
                      {participantReviewErrors.q10YearsStemm}
                    </p>
                  ) : null}
                </div>
                </div>

                <div className="participant-review-field">
                  <p className="workplace-survey-question-label">11. Which STEMM profession do you belong to? (optional)</p>
                  <p className="participant-review-q-hint">Select all that apply (optional):</p>
                  <div className="participant-review-stemm-grid">
                    {PARTICIPANT_REVIEW_STEMM_PROFESSIONS.map((prof) => (
                      <label key={prof.id} className="participant-review-stemm-option">
                        <input
                          type="checkbox"
                          checked={participantReviewForm.stemmProfessions.includes(prof.id)}
                          onChange={() => {
                            toggleParticipantStemmProfession(prof.id);
                            setParticipantReviewErrors((er) => ({ ...er, stemmProfessions: undefined }));
                          }}
                        />
                        <span>{prof.label}</span>
                      </label>
                    ))}
                  </div>
                  {participantReviewErrors.stemmProfessions ? (
                    <p className="elearning-enrollment-field-error" role="alert">
                      {participantReviewErrors.stemmProfessions}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="elearning-enrollment-footer">
                <div className="elearning-enrollment-actions elearning-enrollment-actions--split">
                  <button
                    type="button"
                    className="elearning-enrollment-btn elearning-enrollment-btn--ghost"
                    disabled={participantReviewSubmitting}
                    onClick={() => setParticipantReviewModalOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="elearning-enrollment-btn elearning-enrollment-btn--primary"
                    disabled={participantReviewSubmitting}
                    onClick={() => void handleParticipantReviewSubmit()}
                  >
                    {participantReviewSubmitting ? 'Submitting…' : 'Submit review'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  if (isComingSoonModule) {
    return (
      <div className="study-module-page study-module-coming-soon-gate">
        <div className="module-breadcrumb-bar">
          <div className="module-breadcrumb-inner">
            <Link to="/" className="module-breadcrumb-back" aria-label="Go to home">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10.5L12 3l9 7.5" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 9.5V20h14V9.5" />
              </svg>
            </Link>
            <span className="module-breadcrumb-current">{catalogModule?.title || 'Module'}</span>
          </div>
        </div>
        <div className="study-module-coming-soon-inner">
          <p className="study-module-coming-soon-badge">Coming soon</p>
          <h1 className="study-module-coming-soon-title">{catalogModule?.title}</h1>
          <p className="study-module-coming-soon-release">{catalogModule?.releaseLabel || 'Releasing Q2 2026'}</p>
          {catalogModule?.description ? (
            <p className="study-module-coming-soon-desc">{catalogModule.description}</p>
          ) : null}
          <Link to="/" className="study-module-coming-soon-link">
            Return to Learning Hub
          </Link>
        </div>
      </div>
    );
  }

  // -- Reference layout: overview view --
  if (viewMode === 'overview') {
    return (
      <div className={`study-module-page study-module-overview${useOverviewStepperLayout ? ' study-module-overview--stepper-experiment' : ''}`}>
        {/* Breadcrumb bar (full width) */}
        <div className="module-breadcrumb-bar">
          <div className="module-breadcrumb-inner">
            <Link to="/" className="module-breadcrumb-back" aria-label="Go to home">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10.5L12 3l9 7.5" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 9.5V20h14V9.5" />
              </svg>
            </Link>
            <span className="module-breadcrumb-current">{moduleContent.title}</span>
          </div>
        </div>

        {/* Full-width gradient banner (temporarily hidden per layout update request) */}
        {/* <div className="module-hero-banner">
          <div className="module-hero-inner">
            <h1 className="module-hero-title">{moduleContent.title}</h1>
            <p className="module-hero-meta">E-learning</p>
            <p className="module-hero-meta">Default language: English</p>
          </div>
        </div> */}

        {/* Main content: tabular layout - left = description + syllabus list, right = step content */}
        <div className="module-overview-main">
          {useOverviewStepperLayout && (
            <ModuleOverviewPartsStepper
              sections={sectionsWithSteps}
              activeSectionId={syllabusModalSectionId}
              isStepUnlocked={isSyllabusStepUnlocked}
              onStepSelect={handleSyllabusStepClick}
            />
          )}
          <div className="module-overview-content module-overview-tabular">
            {/* Left: Course description + Syllabus list (collapsible on mobile only) */}
            <div className="module-overview-left">
              <details
                className="module-overview-mobile-info"
                open={overviewDetailsOpen || undefined}
                onToggle={(event) => {
                  if (isDesktopOverviewLayout) {
                    event.preventDefault();
                    return;
                  }
                  setMobileOverviewInfoOpen(event.currentTarget.open);
                }}
              >
                <summary className="module-overview-mobile-info__summary">
                  Course info &amp; parts
                </summary>
                <div className="module-overview-mobile-info__body">
              <section className="module-overview-section">
                <h2 className="module-overview-heading">Course description</h2>
                <p className="module-overview-description">
                  {moduleContent.courseDescriptionLong}
                </p>
              </section>
              <section className="module-overview-section">
                <h2 className="module-overview-heading">Course syllabus</h2>
                {/* <p className="module-syllabus-summary">{moduleContent.sections.length} Step{moduleContent.sections.length !== 1 ? 's' : ''} · ~40 min</p> */}
                <ul className="module-syllabus-list" aria-label="Course steps">
                  {sectionsWithSteps.map((section, index) => {
                    const unlocked = isSyllabusStepUnlocked(section, index);
                    return (
                      <li key={section.id}>
                        {unlocked ? (
                          <button
                            type="button"
                            className={`module-syllabus-item ${syllabusModalSectionId === section.id ? 'module-syllabus-item--selected' : ''}`}
                            onClick={() => handleSyllabusStepClick(section, index)}
                          >
                            <span className="module-syllabus-icon" aria-hidden="true">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                              </svg>
                            </span>
                            <span>Part {section.stepNumber} {section.shortLabel}</span>
                          </button>
                        ) : (
                          <div
                            className="module-syllabus-item module-syllabus-item--locked"
                            title={`Complete Part ${index} first`}
                            role="presentation"
                          >
                            <span className="module-syllabus-icon module-syllabus-icon--locked" aria-hidden="true">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            </span>
                            <span>Part {section.stepNumber} {section.shortLabel}</span>
                            {/* <span className="module-syllabus-locked-label">Complete previous step</span> */}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
                </div>
              </details>
            </div>

            {/* Right: Intro video (default) or selected step content (tabular) */}
            <div className="module-overview-right-panel">
              {syllabusModalSectionId ? (
                <>
                  <h2 className="module-overview-right-panel-title">
                    {(() => {
                      const step = sectionsWithSteps.find((s) => s.id === syllabusModalSectionId);
                      return step?.shortLabel || step?.title || '';
                    })()}
                  </h2>
                  <div ref={overviewStepContentRef} className="module-overview-right-panel-body">
                    {renderSectionContentForId(syllabusModalSectionId)}
                  </div>
                </>
              ) : moduleId === 'intro-module' ? (
                <div className="module-intro-video-wrap">
                  <h2 className="module-overview-right-panel-title sr-only">Module intro</h2>
                  <p className="module-intro-video-desc">
                    Watch the intro video for the Introduction Module - why this work began, who&apos;s behind it, and what&apos;s next.
                  </p>
                  <div className="module-intro-video-player-shell">
                    {introYoutubeEmbedUrl ? (
                      <IntroYoutubePosterPlay
                        embedUrl={introYoutubeEmbedUrl}
                        videoId={INTRO_MODULE_YOUTUBE_VIDEO_ID}
                        posterImgProps={contentImageProps({
                          width: 960,
                          height: 540,
                          loading: 'eager',
                          fetchPriority: 'high',
                        })}
                        isPlaying={introYoutubePlayingOverview}
                        onPlay={() => setIntroYoutubePlayingOverview(true)}
                        title="Module intro video"
                        mode="overview"
                      />
                    ) : (
                      <video
                        className="module-intro-video-player"
                        preload="metadata"
                        poster={introVideoPosterSrc}
                        src={introVideoSrc}
                        {...LOCAL_VIDEO_PLAYER_PROPS}
                      >
                        Your browser does not support embedded video playback.
                      </video>
                    )}
                  </div>
                  <button
                    type="button"
                    className="module-intro-continue-part1"
                    onClick={handleIntroVideoPosterClick}
                  >
                    Open Part 1 - Start Here
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Suggested for you */}
          {/* <section className="module-suggested">
            <div className="module-suggested-header">
              <h2 className="module-suggested-title">Suggested for you</h2>
              {suggestedModules.length > 3 && (
                <div className="module-suggested-pagination">
                  <span className="module-suggested-page-num">{suggestedPage}/{Math.ceil(suggestedModules.length / 3)}</span>
                  <button type="button" className="module-suggested-nav" aria-label="Previous" onClick={() => setSuggestedPage(p => Math.max(1, p - 1))}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button type="button" className="module-suggested-nav" aria-label="Next" onClick={() => setSuggestedPage(p => Math.min(Math.ceil(suggestedModules.length / 3), p + 1))}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}
            </div>
            <div className="module-suggested-grid">
              {suggestedModules.map((item) => (
                <Link to={`/study-module/${item.id}`} key={item.id} className={`module-suggested-card module-suggested-card--${item.color}`}>
                  <div className="module-suggested-card-top">
                    <span className="module-suggested-card-icon" aria-hidden="true">{item.icon}</span>
                  </div>
                  <div className="module-suggested-card-bottom">
                    <h4 className="module-suggested-card-title">{item.title}</h4>
                    <p className="module-suggested-card-meta">EN · E-learning</p>
                  </div>
                </Link>
              ))}
            </div>
          </section> */}
        </div>
        {participantReviewPortal}
      </div>
    );
  }

  // -- Learning view: existing topic sidebar + content --
  return (
    <div className="study-module-page study-module-interactive">
      <div className="study-module-container">
        <div className="study-module-header">
          <button onClick={() => setViewMode('overview')} className="back-link" type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Back to overview">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <div className="module-header-top">
            <div>
              <h1 className="module-title">{moduleContent.title}</h1>
              <p className="module-subtitle">{moduleContent.subtitle}</p>
            </div>
            <div className="module-meta-badge">
              <span className="module-meta-label">Estimated length:</span>
              <span className="module-meta-value">~40 minutes</span>
            </div>
          </div>
          <p className="module-description">{moduleContent.description}</p>
        </div>

        <div className="study-module-content">
          <nav className="step-menu-wrap" aria-label="Part steps">
            <p className="step-menu-hint">Select a topic to continue.</p>
            <div className="step-menu-arc">
              {sectionsWithSteps.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className={`step-btn ${activeSection === section.id ? 'step-btn-active' : ''}`}
                  onClick={() => handleStepSelect(section.id)}
                  aria-current={activeSection === section.id ? 'step' : undefined}
                  style={{ '--step-bg': section.color.bg, '--step-light': section.color.light }}
                >
                  <span className="step-btn-number">{section.stepNumber}</span>
                  <span className="step-btn-label">{section.shortLabel}</span>
                </button>
              ))}
            </div>
          </nav>

          <main ref={moduleMainScrollRef} className="module-main">
            <div className="module-content-panel">
              {currentSection ? (
                <div key={contentKey} className="module-content-inner content-enter">
                  <h2 className="content-panel-title">{currentSection.title}</h2>
                  {renderSectionContent()}
                </div>
              ) : (
                <div className="module-content-placeholder">
                  <p>Select a topic from the steps above to view content.</p>
                </div>
              )}
            </div>
          </main>
        </div>
        {participantReviewPortal}
      </div>
    </div>
  );
};

export default StudyModule;
